// FILE: src/Components/Copilot/CopilotMessageBubble.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment-timezone";
import {
  useSelector,
} from "react-redux";
import {
fetchAppTransaction,
} from "../../services/apps";
import {
getOnboardingForGroup,
updateOnboardingData,
} from "../../services/group";
import CopilotGoalCard from "./CopilotGoalCard";

const getBubbleStyles = (role) => {
  if (role === "user") {
    return {
      wrapper: "justify-end",
      bubble: "bg-white text-black rounded-2xl rounded-br-md",
      avatar: "bg-white text-black",
      icon: "fa-solid fa-user",
      attachment: "bg-black/10 text-black/70",
      meta: "bg-black/10 text-black/55",
      source: "bg-black/10 text-black/65 border-black/10",
    };
  }

  if (role === "tool") {
    return {
      wrapper: "justify-start",
      bubble: "bg-blue-500/[0.07] text-blue-50 rounded-xl",
      avatar: "bg-blue-500/[0.12] text-blue-50",
      icon: "fa-solid fa-screwdriver-wrench",
      attachment: "bg-white/[0.06] text-white/65",
      meta: "bg-white/[0.05] text-white/40",
      source: "bg-white/[0.05] text-white/60 border-white/[0.06]",
    };
  }

  return {
    wrapper: "justify-start",
    bubble: "bg-white/[0.035] text-white rounded-xl",
    avatar: "bg-white/[0.06] text-white",
    icon: "fa-solid fa-wand-magic-sparkles",
    attachment: "bg-white/[0.06] text-white/65",
    meta: "bg-white/[0.05] text-white/40",
    source: "bg-white/[0.05] text-white/60 border-white/[0.06]",
  };
};

const getToolActivityLabel = (message = {}) => {
  const toolNames = [
    message.toolName,
    message.metadata?.toolName,
    ...(Array.isArray(message.metadata?.tools?.toolNames)
      ? message.metadata.tools.toolNames
      : []),
  ]
    .filter(Boolean)
    .map((name) => String(name).toLowerCase());

  const combinedNames = toolNames.join(" ");

  if (
    combinedNames.includes("assets") ||
    combinedNames.includes("posts.search") ||
    combinedNames.includes("schedule.search")
  ) {
    return "Exploring workspace";
  }

  if (
    combinedNames.includes("report") ||
    combinedNames.includes("accounts") ||
    combinedNames.includes("brandkit") ||
    combinedNames.includes("credits") ||
    combinedNames.includes("apps.history") ||
    combinedNames.includes("backgroundjobs")
  ) {
    return "Reviewing workspace";
  }

  const intent = String(message.metadata?.intent || "").toLowerCase();

  if (
    intent.includes("asset") ||
    intent.includes("post") ||
    intent.includes("schedule")
  ) {
    return "Exploring workspace";
  }

  return "Reviewing workspace";
};

const getMessageId = (message, fallback) => {
  return message?._id || message?.id || fallback;
};

const getAttachmentId = (attachment, fallback) => {
  return attachment?._id || attachment?.id || attachment?.url || attachment?.name || fallback;
};

const formatTimestamp = (value) => {
  if (!value) return null;

  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return null;
  }
};

const normalizeReferenceKey = (reference = {}, fallback = "") => {
  return [
    reference.ref,
    reference.sourceType,
    reference.sourceId,
    reference.toolName,
    reference.invocationId,
    fallback,
  ]
    .filter(Boolean)
    .join(":");
};

const mergeReferenceLists = (...lists) => {
  const merged = [];
  const seen = new Set();

  lists.forEach((list) => {
    if (!Array.isArray(list)) return;

    list.forEach((reference, index) => {
      if (!reference || typeof reference !== "object") return;

      const key = normalizeReferenceKey(reference, index);

      if (seen.has(key)) return;

      seen.add(key);
      merged.push(reference);
    });
  });

  return merged;
};

const getSourceReferences = (message = {}) => {
return mergeReferenceLists(
message.metadata?.attachments?.sourceReferences,
message.metadata?.retrievedKnowledge?.sourceReferences,
message.metadata?.tools?.sourceReferences,
message.metadata?.attachmentReferences,
message.metadata?.knowledgeReferences,
message.metadata?.toolReferences,
message.metadata?.sourceReferences
);
};

const getUsageLabel = (usage = null) => {
  if (!usage) return null;

  const inputTokens = Number(
    usage.input_tokens ??
    usage.inputTokens ??
    usage.prompt_tokens ??
    usage.promptTokens ??
    0
  );

  const outputTokens = Number(
    usage.output_tokens ??
    usage.outputTokens ??
    usage.completion_tokens ??
    usage.completionTokens ??
    0
  );

  const credits = Number(
    usage.credits ??
    usage.creditsCharged ??
    0
  );

  const totalTokens = Number(
    usage.total_tokens ??
    usage.totalTokens ??
    inputTokens + outputTokens
  );

  if (
    inputTokens <= 0 &&
    outputTokens <= 0 &&
    totalTokens <= 0
  ) {
    return null;
  }

  return `I:${inputTokens.toLocaleString()} O:${outputTokens.toLocaleString()} C:${credits.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }
  )}`;
};

const getReferenceLabel = (source = {}) => {
if (source.ref) return `[${source.ref}]`;
if (source.url && !source.sourceType && !source.toolName) return "[A]";
if (source.toolName || source.displayName) return "[T]";
if (source.sourceType || source.sourceId) return "[S]";
return "[Ref]";
};

const getReferenceTitle = (source = {}) => {
  return (
    source.title ||
    source.displayName ||
    source.toolName ||
    source.sourceIdentity ||
    source.sourceId ||
    source.sourceType ||
    "Source"
  );
};

const getReferenceSubtitle = (source = {}) => {
if (source.toolName) {
return source.success === false ? "Tool result failed" : "Tool result";
}
if (source.url && !source.sourceType) {
return source.uploadedToLibrary ? "Uploaded attachment" : "Attachment";
}
if (source.sourceType) {
return source.sourceType;
}
return "Reference";
};

const getReferenceDescription = (source = {}) => {
  if (source.outputSummary) return source.outputSummary;
  if (source.error) return source.error;

  if (source.score !== undefined) {
    return `Relevance: ${Number(source.score || 0).toFixed(3)}`;
  }

  return null;
};

const formatMetaLabel = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!normalizedValue) return "";

  if (normalizedValue.toLowerCase() === "workspace multi") {
    return "Workspace review";
  }

  if (normalizedValue.toLowerCase() === "success") {
    return "Completed";
  }

  return normalizedValue.replace(/\b\w/g, (character) =>
    character.toUpperCase()
  );
};

const getPendingActionId = (
  pendingAction = {}
) => {
  return (
    pendingAction._id ||
    pendingAction.id ||
    null
  );
};

const formatActionDetailValue = (
  value
) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "Not provided";
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(formatActionDetailValue)
      .join(", ");
  }

  try {
    return JSON.stringify(value);
  } catch (_) {
    return String(value);
  }
};

const getActionDetailLabel = (
  detail = {},
  index = 0
) => {
  return (
    detail.label ||
    detail.title ||
    detail.status ||
    `Item ${index + 1}`
  );
};

const getActionDetailValue = (
detail = {}
) => {
if (
Object.prototype.hasOwnProperty.call(
detail,
"before"
) ||
Object.prototype.hasOwnProperty.call(
detail,
"after"
)
) {
return `${formatActionDetailValue(
detail.before
)} → ${formatActionDetailValue(
detail.after
)}`;
}

if (
Object.prototype.hasOwnProperty.call(
detail,
"value"
)
) {
return formatActionDetailValue(
detail.value
);
}

const values = [
detail.caption,
detail.message,
detail.postId,
detail.scheduledTime,
].filter(Boolean);

return values.length > 0
? values.join(" · ")
: formatActionDetailValue(detail);
};

const getActionStatusStyles = (
  status = ""
) => {
  const normalizedStatus = String(
    status || ""
  ).toLowerCase();

  if (
    normalizedStatus === "completed"
  ) {
    return {
      wrapper:
        "border-emerald-400/20 bg-emerald-400/[0.07]",
      badge:
        "bg-emerald-400/15 text-emerald-100",
      icon:
        "fa-solid fa-circle-check",
      label: "Completed",
    };
  }

  if (
    normalizedStatus === "cancelled"
  ) {
    return {
      wrapper:
        "border-white/[0.08] bg-white/[0.025]",
      badge:
        "bg-white/[0.06] text-white/45",
      icon:
        "fa-solid fa-ban",
      label: "Cancelled",
    };
  }

  if (
    normalizedStatus === "failed"
  ) {
    return {
      wrapper:
        "border-red-400/20 bg-red-400/[0.07]",
      badge:
        "bg-red-400/15 text-red-100",
      icon:
        "fa-solid fa-circle-exclamation",
      label: "Failed",
    };
  }

  if (
    normalizedStatus === "expired"
  ) {
    return {
      wrapper:
        "border-amber-400/20 bg-amber-400/[0.06]",
      badge:
        "bg-amber-400/15 text-amber-100",
      icon:
        "fa-solid fa-clock",
      label: "Expired",
    };
  }

  if (
    normalizedStatus === "executing" ||
    normalizedStatus === "confirmed"
  ) {
    return {
      wrapper:
        "border-blue-400/20 bg-blue-400/[0.07]",
      badge:
        "bg-blue-400/15 text-blue-100",
      icon:
        "fa-solid fa-spinner fa-spin",
      label: "Executing",
    };
  }

  return {
    wrapper:
      "border-violet-400/20 bg-violet-400/[0.07]",
    badge:
      "bg-violet-400/15 text-violet-100",
    icon:
      "fa-solid fa-shield-check",
    label: "Confirmation required",
  };
};

const getConfirmationPlatformDisplay = (
  platform = ""
) => {
  const normalized =
    String(platform || "")
      .trim()
      .toUpperCase();

  const display = {
    FACEBOOK: {
      label: "Facebook",
      icon:
        "fa-brands fa-facebook",
      color:
        "text-blue-400",
    },
    INSTAGRAM: {
      label: "Instagram",
      icon:
        "fa-brands fa-instagram",
      color:
        "text-pink-400",
    },
    PINTEREST: {
      label: "Pinterest",
      icon:
        "fa-brands fa-pinterest",
      color:
        "text-red-400",
    },
    LINKEDIN: {
      label: "LinkedIn",
      icon:
        "fa-brands fa-linkedin",
      color:
        "text-blue-400",
    },
    LINKEDIN_COMPANY: {
      label:
        "LinkedIn Company",
      icon:
        "fa-brands fa-linkedin",
      color:
        "text-blue-400",
    },
    YOUTUBE: {
      label: "YouTube",
      icon:
        "fa-brands fa-youtube",
      color:
        "text-red-400",
    },
    TIKTOK: {
      label: "TikTok",
      icon:
        "fa-brands fa-tiktok",
      color:
        "text-white/80",
    },
    GMB: {
      label:
        "Google Business",
      icon:
        "fa-brands fa-google",
      color:
        "text-blue-300",
    },
    TWITTER: {
      label: "X",
      icon:
        "fa-brands fa-x-twitter",
      color:
        "text-white/80",
    },
  };

  return (
    display[normalized] || {
      label:
        normalized.replace(
          /_/g,
          " "
        ) || "Destination",
      icon:
        "fa-solid fa-share-nodes",
      color:
        "text-violet-200",
    }
  );
};

const ConfirmationMediaPreview = ({
  media = [],
}) => {
  if (
    !Array.isArray(media) ||
    media.length === 0
  ) {
    return (
      <p className="mb-0 text-[10px] font-medium text-white/30">
        No media
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {media.map((item, index) => {
        const itemKey =
          item.assetId ||
          item.url ||
          `${item.name}-${index}`;

        const previewUrl =
          getActionMediaPreviewUrl(
            item
          );

        const mediaType =
          getActionMediaType(
            item
          );

        const mediaUrl =
          getActionMediaUrl(
            item
          );

        return (
          <div
            key={itemKey}
            className="relative h-24 w-28 overflow-hidden rounded-lg border border-white/[0.08] bg-black/30"
          >
            {mediaType ===
              "image" &&
            previewUrl ? (
              <img
                src={previewUrl}
                alt={
                  item.name ||
                  `Media ${index + 1}`
                }
                loading={
                  index < 3
                    ? "eager"
                    : "lazy"
                }
                className="h-full w-full bg-black object-contain"
                onError={(event) => {
                  if (
                    mediaUrl &&
                    event.currentTarget
                      .src !==
                      mediaUrl
                  ) {
                    event.currentTarget.src =
                      mediaUrl;
                  }
                }}
              />
            ) : mediaType ===
                "video" &&
              mediaUrl ? (
              <video
                src={mediaUrl}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full bg-black object-contain"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
                <i className="fa-solid fa-file text-lg text-white/35"></i>
                <span className="line-clamp-2 text-[8px] font-semibold text-white/40">
                  {item.name ||
                    "Media"}
                </span>
              </div>
            )}

            <span className="absolute left-1.5 top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-black/75 px-1 text-[8px] font-black text-white">
              {item.position ||
                index + 1}
            </span>

            {mediaType ===
              "video" && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/65 text-white">
                  <i className="fa-solid fa-play text-[8px]"></i>
                </span>
              </span>
            )}

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent px-1.5 pb-1.5 pt-5">
              <span className="block truncate text-[8px] font-semibold text-white/80">
                {item.name ||
                  item.type ||
                  "Media"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const getAssetPreviewUrl = (
  asset = {}
) => {
  return (
    asset.thumbnailUrl ||
    asset.metadata
      ?.thumbnail?.url ||
    asset.url ||
    null
  );
};

const getAssetTypeIcon = (
  type = ""
) => {
  const normalizedType =
    String(type || "")
      .trim()
      .toLowerCase();

  const icons = {
    image:
      "fa-solid fa-image",
    video:
      "fa-solid fa-video",
    audio:
      "fa-solid fa-volume-high",
    document:
      "fa-solid fa-file-lines",
    text:
      "fa-solid fa-file-lines",
    other:
      "fa-solid fa-file",
  };

  return (
    icons[normalizedType] ||
    icons.other
  );
};

const formatAssetSize = (
  value
) => {
  const bytes =
    Number(value || 0);

  if (
    !Number.isFinite(bytes) ||
    bytes <= 0
  ) {
    return null;
  }

  const units = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
  ];

  const unitIndex =
    Math.min(
      Math.floor(
        Math.log(bytes) /
          Math.log(1024)
      ),
      units.length - 1
    );

  const amount =
    bytes /
    Math.pow(
      1024,
      unitIndex
    );

  return `${amount.toFixed(
    amount >= 10 ||
    unitIndex === 0
      ? 0
      : 1
  )} ${units[unitIndex]}`;
};

const AssetPreviewTile = ({
  asset = {},
  selected = false,
  disabled = false,
  selectable = false,
  destructive = false,
  onClick = null,
}) => {
  const previewUrl =
    getAssetPreviewUrl(asset);

  const assetType =
    String(
      asset.type || "other"
    )
      .trim()
      .toLowerCase();

  const formattedSize =
    formatAssetSize(
      asset.size
    );

  const Wrapper =
    selectable
      ? "button"
      : "div";

  return (
    <Wrapper
      type={
        selectable
          ? "button"
          : undefined
      }
      disabled={
        selectable
          ? disabled
          : undefined
      }
      onClick={
        selectable
          ? onClick
          : undefined
      }
      className={`group block w-full min-w-0 max-w-full overflow-hidden rounded-xl border text-left align-top transition ${
selected
? destructive
? "border-red-300/45 bg-red-400/[0.1]"
: "border-violet-300/45 bg-violet-400/[0.1]"
: "border-white/[0.08] bg-black/[0.14]"
} ${
selectable
? "cursor-pointer hover:border-white/[0.18] disabled:cursor-not-allowed disabled:opacity-45"
: ""
}`}
    >
      <div className="relative aspect-[16/10] w-full min-w-0 overflow-hidden bg-black/30">
        {assetType ===
          "image" &&
        previewUrl ? (
          <img
            src={previewUrl}
            alt={
              asset.name ||
              "Asset preview"
            }
            className="h-full w-full object-cover"
          />
        ) : assetType ===
            "video" &&
          previewUrl ? (
          <video
            src={previewUrl}
            muted
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
            <i
              className={`${getAssetTypeIcon(
                assetType
              )} text-2xl text-white/35`}
            ></i>

            <span className="line-clamp-2 text-[10px] font-semibold text-white/45">
              {asset.name ||
                "Media Library asset"}
            </span>
          </div>
        )}

        {assetType ===
          "video" && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white">
              <i className="fa-solid fa-play text-[9px]"></i>
            </span>
          </span>
        )}

        {selectable && (
          <span
            className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border ${
              selected
                ? destructive
                  ? "border-red-200/70 bg-red-500 text-white"
                  : "border-violet-200/70 bg-violet-500 text-white"
                : "border-white/20 bg-black/65 text-transparent"
            }`}
          >
            <i className="fa-solid fa-check text-[9px]"></i>
          </span>
        )}
      </div>

      <div className="min-w-0 overflow-hidden p-2.5">
<p
className="mb-1 block w-full min-w-0 truncate text-xs font-bold text-white/80"
title={
asset.name ||
"Asset"
}
>
{asset.name ||
"Asset"}
</p>  
<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden text-[9px] font-semibold uppercase text-white/35">
          <span>
            {assetType}
          </span>

          {asset.status && (
            <span>
              {asset.status}
            </span>
          )}

          {formattedSize && (
            <span>
              {formattedSize}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

const ConfirmationAssetDetails = ({
  details = [],
}) => {
  const assetDetails =
    details.filter(
      (detail) =>
        detail?.asset &&
        typeof detail.asset ===
          "object"
    );

  const regularDetails =
    details.filter(
      (detail) =>
        !detail?.asset ||
        typeof detail.asset !==
          "object"
    );

  if (
    assetDetails.length === 0
  ) {
    return null;
  }

  return (
    <div className="border-t border-white/[0.06] px-3.5 py-3">
      <div className="grid min-w-0 grid-cols-1 items-start gap-3 sm:grid-cols-2">
        {assetDetails.map(
          (detail, index) => (
            <div
            key={
            detail.asset
            ?.assetId ||
            detail.asset?.id ||
            detail.asset?.url ||
            index
            }
            className="min-w-0 max-w-full overflow-hidden"
            >
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-white/35">
                {getActionDetailLabel(
                  detail,
                  index
                )}
              </p>

              <AssetPreviewTile
                asset={
                  detail.asset
                }
              />
            </div>
          )
        )}
      </div>

      {regularDetails.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-white/[0.05] pt-3">
          {regularDetails.map(
            (detail, index) => (
              <div
                key={`${getActionDetailLabel(
                  detail,
                  index
                )}-${index}`}
                className="grid grid-cols-[92px_minmax(0,1fr)] gap-2 text-xs"
              >
                <span className="font-semibold text-white/35">
                  {getActionDetailLabel(
                    detail,
                    index
                  )}
                </span>

                <span className="break-words text-white/65">
                  {getActionDetailValue(
                    detail
                  )}
                </span>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const ConfirmationDestinationSection = ({
  section,
}) => {
  const platformDisplay =
    getConfirmationPlatformDisplay(
      section.platform
    );

  const fields =
    Array.isArray(section.fields)
      ? section.fields
      : [];

  return (
    <details
      open
      className="group overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.12]"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <i
            className={`${platformDisplay.icon} text-sm ${platformDisplay.color}`}
          ></i>

          <div className="min-w-0">
            <span className="block truncate text-xs font-bold text-white/85">
              {section.profileName ||
                section.title ||
                "Destination"}
            </span>

            <span className="block text-[9px] font-semibold uppercase tracking-wide text-white/30">
              {platformDisplay.label}
            </span>
          </div>
        </div>

        <i className="fa-solid fa-chevron-down text-[9px] text-white/35 transition-transform group-open:rotate-180"></i>
      </summary>

      <div className="space-y-3 border-t border-white/[0.06] px-3 py-3">
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-white/30">
            Ordered media
          </p>

          <ConfirmationMediaPreview
            media={
              section.media || []
            }
          />
        </div>

        {fields.length > 0 && (
          <div className="space-y-2 border-t border-white/[0.05] pt-3">
            {fields.map(
              (field, index) => (
                <div
                  key={`${field.label}-${index}`}
                  className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 text-xs"
                >
                  <span className="font-semibold text-white/35">
                    {field.label}
                  </span>

                  <span className="break-words text-white/65">
                    {formatActionDetailValue(
                      field.value
                    )}
                  </span>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </details>
  );
};

const getAccountProfileId = (
  account = {}
) => {
  return String(
    account.profileId ||
      account.platformPageId ||
      account.platformId ||
      ""
  );
};

const ConnectedAccountIdentity = ({
  account = {},
  compact = false,
}) => {
  const profileDetails =
    useSelector(
      (state) =>
        state.groups
          ?.profileDetails || {}
    );

  const platform =
    String(
      account.platform || ""
    )
      .trim()
      .toUpperCase();

  const profileId =
    getAccountProfileId(
      account
    );

  const details =
    profileDetails[
      profileId
    ] || {};

  const imageUrl =
    details
      .profile_picture_blob_url ||
    details
      .profile_picture_url ||
    details
      .profile_picture_source_url ||
    details.profile_image ||
    account.profilePictureUrl ||
    (
      platform ===
        "FACEBOOK" &&
      profileId
        ? `https://graph.facebook.com/${profileId}/picture?type=large`
        : null
    );

  const profileName =
    account.profileName ||
    account.label ||
    details.name ||
    details.username ||
    profileId ||
    "Connected profile";

  const platformDisplay =
    getConfirmationPlatformDisplay(
      platform
    );

  const [imageFailed, setImageFailed] =
    useState(false);

  const initials =
    profileName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) =>
        part.charAt(0)
      )
      .join("")
      .toUpperCase() ||
    "?";

  const avatarSize =
    compact
      ? "h-8 w-8"
      : "h-10 w-10";

  return (
    <div className="flex min-w-0 items-center gap-3">
      <div
        className={`relative flex-shrink-0 ${avatarSize}`}
      >
        {imageUrl &&
        !imageFailed ? (
          <img
            src={imageUrl}
            alt={profileName}
            className={`${avatarSize} rounded-full border border-white/10 object-cover`}
            loading="lazy"
            onError={() =>
              setImageFailed(true)
            }
          />
        ) : (
          <div
            className={`${avatarSize} flex items-center justify-center rounded-full border border-white/10 bg-white/[0.08] text-[10px] font-black text-white/70`}
          >
            {initials}
          </div>
        )}

        <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#1b1d21] bg-[#24262c]">
          <i
            className={`${platformDisplay.icon} text-[9px] ${platformDisplay.color}`}
          ></i>
        </span>
      </div>

      <div className="min-w-0">
        <p className="mb-0 truncate text-xs font-bold text-white/85">
          {profileName}
        </p>

        <p className="mb-0 mt-0.5 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wide text-white/35">
          <i
            className={`${platformDisplay.icon} ${platformDisplay.color}`}
          ></i>
          {platformDisplay.label}
        </p>
      </div>
    </div>
  );
};

const ConfirmationAccountDetails = ({
  details = [],
}) => {
  const accountDetails =
    details.filter(
      (detail) =>
        detail?.account &&
        typeof detail.account ===
          "object"
    );

  if (
    accountDetails.length === 0
  ) {
    return null;
  }

  return (
    <div className="border-t border-white/[0.06] px-3.5 py-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {accountDetails.map(
          (detail, index) => (
            <div
              key={`${detail.account.platform}:${detail.account.profileId}:${index}`}
              className="rounded-xl border border-white/[0.07] bg-black/[0.14] px-3 py-3"
            >
              <ConnectedAccountIdentity
                account={
                  detail.account
                }
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

const getConfirmationTotalCredits = (
  summary = {}
) => {
  const details = Array.isArray(
    summary.details
  )
    ? summary.details
    : [];

  const totalCreditDetail =
    details.find((detail) => {
      return (
        String(
          detail?.label || ""
        )
          .trim()
          .toLowerCase() ===
        "total credits"
      );
    });

  const credits = Number(
    totalCreditDetail?.value
  );

  return Number.isFinite(credits)
    ? credits
    : null;
};

const getImageGenerationOutputImages = (
  transaction = null
) => {
  const images = Array.isArray(
    transaction?.output?.images
  )
    ? transaction.output.images
    : [];

  return images
    .map((image, index) => {
      if (
        typeof image === "string"
      ) {
        return {
          url: image,
          name:
            `Generated image ${index + 1}`,
          width: null,
          height: null,
        };
      }

      if (
        !image ||
        typeof image !== "object" ||
        !image.url
      ) {
        return null;
      }

      return {
        ...image,
        url: image.url,
        name:
          image.name ||
          `Generated image ${index + 1}`,
        width:
          image.width || null,
        height:
          image.height || null,
      };
    })
    .filter(Boolean);
};

const ImageGenerationTransactionPreview = ({
  pendingAction,
}) => {
  const executionResult =
    pendingAction?.executionResult &&
    typeof pendingAction.executionResult ===
      "object"
      ? pendingAction.executionResult
      : null;

  const transactionId =
    executionResult?.transactionId ||
    executionResult?.transaction?._id ||
    executionResult?.transaction?.id ||
    null;

  const [
    transaction,
    setTransaction,
  ] = useState(
    executionResult?.transaction ||
      null
  );

  const [
    pollingError,
    setPollingError,
  ] = useState(null);

  useEffect(() => {
    if (
      pendingAction?.toolName !==
        "apps.generateImage" ||
      !transactionId
    ) {
      return undefined;
    }

    const initialStatus =
      String(
        executionResult?.transaction
          ?.status || ""
      ).toLowerCase();

    if (
      [
        "completed",
        "failed",
        "refunded",
      ].includes(initialStatus)
    ) {
      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const pollTransaction =
      async () => {
        try {
          const latestTransaction =
            await fetchAppTransaction(
              transactionId
            );

          if (cancelled) {
            return;
          }

          setTransaction(
            latestTransaction
          );
          setPollingError(null);

          const latestStatus =
            String(
              latestTransaction?.status ||
                ""
            ).toLowerCase();

          if (
            [
              "completed",
              "failed",
              "refunded",
            ].includes(latestStatus)
          ) {
            return;
          }
        } catch (error) {
          if (cancelled) {
            return;
          }

          setPollingError(
            error.message ||
              "Could not refresh image-generation status."
          );
        }

        if (!cancelled) {
          timerId = window.setTimeout(
            pollTransaction,
            3000
          );
        }
      };

    pollTransaction();

    return () => {
      cancelled = true;

      if (timerId) {
        window.clearTimeout(
          timerId
        );
      }
    };
  }, [
    executionResult,
    pendingAction?.toolName,
    transactionId,
  ]);

  if (
    pendingAction?.toolName !==
      "apps.generateImage" ||
    !transactionId
  ) {
    return null;
  }

  const status =
    String(
      transaction?.status ||
        executionResult?.status ||
        "processing"
    ).toLowerCase();

  const images =
    getImageGenerationOutputImages(
      transaction
    );

  if (
    status === "failed" ||
    status === "refunded"
  ) {
    const errorMessage =
      transaction?.errorMessage ||
      transaction?.refundError ||
      (
        status === "refunded"
          ? "Image generation failed and the charged credits were refunded."
          : "Image generation failed."
      );

    return (
      <div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
        <div className="flex items-start gap-2.5">
          <i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

          <div className="min-w-0">
            <p className="mb-1 text-xs font-bold text-red-100">
              {status ===
              "refunded"
                ? "Generation failed · Credits refunded"
                : "Image generation failed"}
            </p>

            <p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
              {errorMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (
    status !== "completed"
  ) {
    return (
      <div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
        <div className="flex items-center gap-2.5">
          <i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

          <div className="min-w-0">
            <p className="mb-0 text-xs font-bold text-blue-100">
              Generating images
            </p>

            <p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
              Transaction{" "}
              {String(
                transactionId
              )}
            </p>
          </div>
        </div>

        {pollingError && (
          <p className="mb-0 mt-2 text-[10px] text-amber-100/60">
            {pollingError} Retrying…
          </p>
        )}
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
        <p className="mb-0 text-xs text-amber-100/70">
          Image generation completed, but no previewable images were returned.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <div>
          <p className="mb-0 text-xs font-bold text-emerald-100">
            Generation complete
          </p>

          <p className="mb-0 mt-0.5 text-[10px] text-white/35">
            {images.length} image
            {images.length === 1
              ? ""
              : "s"}{" "}
            saved to Media Library
          </p>
        </div>

        <i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
      </div>

      <div
        className={`grid gap-2 ${
          images.length === 1
            ? "grid-cols-1"
            : "grid-cols-2"
        }`}
      >
        {images.map(
          (image, index) => (
            <div
              key={`${image.url}-${index}`}
              className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20"
            >
              <a
                href={image.url}
                target="_blank"
                rel="noreferrer"
                className="block"
              >
                <img
                  src={image.url}
                  alt={
                    image.name ||
                    `Generated image ${index + 1}`
                  }
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              </a>

              <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                <div className="min-w-0">
                  <p className="mb-0 truncate text-[10px] font-semibold text-white/65">
                    {image.name}
                  </p>

                  {image.width &&
                    image.height && (
                      <p className="mb-0 mt-0.5 text-[9px] text-white/30">
                        {image.width} ×{" "}
                        {image.height}
                      </p>
                    )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-1">
                  <a
                    href={image.url}
                    target="_blank"
                    rel="noreferrer"
                    title="Open image"
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
                  </a>

                  <a
                    href={image.url}
                    download={
                      image.name ||
                      `generated-image-${index + 1}.png`
                    }
                    title="Download image"
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
                  >
                    <i className="fa-solid fa-download text-[9px]"></i>
                  </a>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const CarouselMakerTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;
const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;
const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);
const [
pollingError,
setPollingError,
] = useState(null);
const [
activeCardIndex,
setActiveCardIndex,
] = useState(0);
const sliderTrackRef =
useRef(null);
useEffect(() => {
setActiveCardIndex(0);
sliderTrackRef.current?.scrollTo({
left: 0,
behavior: "auto",
});
}, [
transactionId,
]);
useEffect(() => {
if (
pendingAction?.toolName !==
"apps.generateCarousel" ||
!transactionId
) {
return undefined;
}
const initialStatus =
String(
executionResult?.transaction
?.status || ""
).toLowerCase();
if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}
let cancelled = false;
let timerId = null;
const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);
if (cancelled) {
return;
}
setTransaction(
latestTransaction
);
setPollingError(null);
const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();
if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}
setPollingError(
error.message ||
"Could not refresh Carousel Maker status."
);
}
if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};
pollTransaction();
return () => {
cancelled = true;
if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);
if (
pendingAction?.toolName !==
"apps.generateCarousel" ||
!transactionId
) {
return null;
}
const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();
const rawCards =
Array.isArray(
transaction?.output?.cards
)
? transaction.output.cards
: [];
const cards =
rawCards
.map((card, index) => {
if (
typeof card ===
"string"
) {
return {
cardNumber:
index + 1,
url: card,
name:
`Carousel card ${index + 1}`,
};
}
if (
!card ||
typeof card !==
"object" ||
!card.url
) {
return null;
}
return {
...card,
cardNumber:
Number(
card.cardNumber ||
index + 1
),
url:
card.url,
name:
card.name ||
`Carousel card ${index + 1}`,
};
})
.filter(Boolean);
const scrollToCarouselCard = (
requestedIndex
) => {
if (cards.length === 0) {
return;
}
const normalizedIndex =
(
requestedIndex %
cards.length +
cards.length
) %
cards.length;
setActiveCardIndex(
normalizedIndex
);
const track =
sliderTrackRef.current;
const targetCard =
track?.children?.[
normalizedIndex
];
if (
!track ||
!targetCard
) {
return;
}
track.scrollTo({
left:
targetCard.offsetLeft -
track.offsetLeft,
behavior: "smooth",
});
};
const topic =
transaction?.output?.topic ||
transaction?.input?.topic ||
executionResult?.topic ||
"";
const cardCount =
Number(
transaction?.output?.cardCount ||
transaction?.input?.cardCount ||
executionResult?.cardCount ||
cards.length ||
0
);
const platform =
transaction?.output?.platform ||
transaction?.input?.platform ||
executionResult?.platform ||
null;
const aspectRatio =
transaction?.output?.aspectRatio ||
transaction?.input?.aspectRatio ||
executionResult?.aspectRatio ||
null;
const model =
transaction?.output?.model ||
transaction?.input?.model ||
executionResult?.model ||
null;
if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
status === "refunded"
? "Carousel generation could not be completed. The charged credits were refunded."
: "Carousel generation could not be completed. Please review the carousel settings and try again.";
return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>
<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status ===
"refunded"
? "Carousel generation failed · Credits refunded"
: "Carousel generation failed"}
</p>
<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}
if (
status !== "completed"
) {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>
<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Generating carousel
{cardCount > 0
? ` · ${cardCount} cards`
: ""}
</p>
<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>
{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}
if (
cards.length === 0
) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Carousel generation completed, but no previewable cards were returned.
</p>
</div>
);
}
return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-3 flex items-start justify-between gap-3">
<div className="min-w-0">
<p className="mb-0 text-sm font-bold text-emerald-100">
Carousel generation complete
</p>
<p className="mb-0 mt-0.5 text-[10px] text-white/35">
{cards.length} carousel card
{cards.length === 1
? ""
: "s"}{" "}
saved to Media Library
</p>
{topic && (
<p className="mb-0 mt-1 line-clamp-2 text-xs leading-relaxed text-white/45">
{topic}
</p>
)}
</div>
<i className="fa-solid fa-circle-check mt-0.5 text-sm text-emerald-300"></i>
</div>
<div
className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-black/20 outline-none focus-visible:ring-2 focus-visible:ring-violet-300/40"
tabIndex={0}
role="region"
aria-label={`Carousel cards. Showing card ${activeCardIndex + 1} of ${cards.length}.`}
onKeyDown={(event) => {
if (
cards.length <= 1
) {
return;
}
if (
event.key ===
"ArrowLeft"
) {
event.preventDefault();
scrollToCarouselCard(
activeCardIndex - 1
);
}
if (
event.key ===
"ArrowRight"
) {
event.preventDefault();
scrollToCarouselCard(
activeCardIndex + 1
);
}
}}
>
<div
ref={sliderTrackRef}
className="copilot-no-scrollbar flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth pr-[54%] sm:pr-[22%]"
onScroll={(event) => {
const track =
event.currentTarget;
const children =
Array.from(
track.children
);
if (
children.length === 0
) {
return;
}
const currentLeft =
track.scrollLeft;
let closestIndex = 0;
let closestDistance =
Number.POSITIVE_INFINITY;
children.forEach(
(child, index) => {
const childLeft =
child.offsetLeft -
track.offsetLeft;
const distance =
Math.abs(
childLeft -
currentLeft
);
if (
distance <
closestDistance
) {
closestDistance =
distance;
closestIndex =
index;
}
}
);
setActiveCardIndex(
closestIndex
);
}}
>
{cards.map(
(card, index) => (
<div
key={`${card.url}-${index}`}
className="w-[46%] flex-none snap-start sm:w-[32%]"
>
<div className="relative">
<a
href={card.url}
target="_blank"
rel="noreferrer"
className="block"
tabIndex={0}
>
<img
src={card.url}
alt={
card.name ||
`Carousel card ${index + 1}`
}
loading="lazy"
className="w-full bg-black object-contain"
style={
aspectRatio
? {
aspectRatio:
String(
aspectRatio
).replace(
":",
" / "
),
}
: undefined
}
/>
</a>
<span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-black text-white/85">
Card{" "}
{card.cardNumber ||
index + 1}{" "}
/{" "}
{cards.length}
</span>
</div>
<div className="flex items-center justify-between gap-2 px-2.5 py-2">
<div className="min-w-0">
<p className="mb-0 truncate text-[10px] font-semibold text-white/65">
{card.name}
</p>
</div>
<div className="flex flex-shrink-0 items-center gap-1">
<a
href={card.url}
target="_blank"
rel="noreferrer"
tabIndex={0}
title={`Open carousel card ${
card.cardNumber ||
index + 1
}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>
<a
href={card.url}
download={
card.name ||
`carousel-card-${index + 1}.png`
}
tabIndex={0}
title={`Download carousel card ${
card.cardNumber ||
index + 1
}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
</div>
)
)}
</div>
{cards.length > 1 && (
<>
<button
type="button"
onClick={() =>
scrollToCarouselCard(
activeCardIndex - 1
)
}
className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/80 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-black/90 hover:text-white"
aria-label="Show previous carousel card"
>
<i className="fa-solid fa-chevron-left text-xs"></i>
</button>
<button
type="button"
onClick={() =>
scrollToCarouselCard(
activeCardIndex + 1
)
}
className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/80 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-black/90 hover:text-white"
aria-label="Show next carousel card"
>
<i className="fa-solid fa-chevron-right text-xs"></i>
</button>
</>
)}
</div>
{cards.length > 1 && (
<div className="mt-2 flex items-center justify-between gap-3">
<button
type="button"
onClick={() =>
scrollToCarouselCard(
activeCardIndex - 1
)
}
className="inline-flex h-8 items-center gap-2 rounded-lg bg-white/[0.06] px-3 text-[10px] font-bold text-white/60 transition hover:bg-white/[0.11] hover:text-white"
>
<i className="fa-solid fa-chevron-left text-[8px]"></i>
Previous
</button>
<div className="copilot-no-scrollbar flex min-w-0 flex-1 items-center justify-center gap-1.5 overflow-x-auto px-2">
{cards.map(
(card, index) => (
<button
key={`carousel-dot-${card.url}-${index}`}
type="button"
onClick={() =>
scrollToCarouselCard(
index
)
}
className={`h-2 flex-shrink-0 rounded-full transition-all ${
index ===
activeCardIndex
? "w-5 bg-emerald-300"
: "w-2 bg-white/20 hover:bg-white/40"
}`}
aria-label={`Show carousel card ${index + 1}`}
aria-current={
index ===
activeCardIndex
? "true"
: undefined
}
></button>
)
)}
</div>
<button
type="button"
onClick={() =>
scrollToCarouselCard(
activeCardIndex + 1
)
}
className="inline-flex h-8 items-center gap-2 rounded-lg bg-white/[0.06] px-3 text-[10px] font-bold text-white/60 transition hover:bg-white/[0.11] hover:text-white"
>
Next
<i className="fa-solid fa-chevron-right text-[8px]"></i>
</button>
</div>
)}
<div className="mt-3 flex flex-wrap gap-2">
{platform && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
{platform}
</span>
)}
{aspectRatio && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Aspect ratio{" "}
{aspectRatio}
</span>
)}
{model && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
{model}
</span>
)}
</div>
</div>
);
};
const ImageResizeTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.resizeImage" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction
?.status || ""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);
setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh image-resize status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.resizeImage" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const inputUrl =
transaction?.inputAssetUrl ||
transaction?.input
?.savedInputAssetUrl ||
transaction?.input
?.originalImageUrl ||
executionResult
?.inputAssetUrl ||
null;

const outputUrl =
transaction?.outputAssetUrl ||
transaction?.output?.url ||
transaction?.output
?.outputAssetUrl ||
null;

const outputName =
transaction?.output?.name ||
"Resized image";

const outputWidth =
transaction?.output?.width ||
null;

const outputHeight =
transaction?.output?.height ||
null;

const aspectRatio =
transaction?.output
?.aspectRatio ||
transaction?.input
?.aspectRatio ||
executionResult
?.aspectRatio ||
null;

const targetSize =
transaction?.output
?.targetSize ||
null;

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Image resizing failed and the charged credits were refunded."
: "Image resizing failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status ===
"refunded"
? "Resize failed · Credits refunded"
: "Image resizing failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (
status !== "completed"
) {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Resizing image
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (!outputUrl) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Image resizing completed, but no previewable output was returned.
</p>
</div>
);
}

return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-2.5 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-xs font-bold text-emerald-100">
Resize complete
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
Resized image saved to Media Library
</p>
</div>

<i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
</div>

<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{inputUrl ? (
<a
href={inputUrl}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={inputUrl}
alt="Original image"
loading="lazy"
className="aspect-square w-full object-cover"
/>
</a>
) : (
<div className="flex aspect-square w-full items-center justify-center text-white/25">
<i className="fa-regular fa-image text-2xl"></i>
</div>
)}

<span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
Before
</span>
</div>

<div className="px-2.5 py-2">
<p className="mb-0 truncate text-[10px] font-semibold text-white/60">
Original image
</p>
</div>
</div>

<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={outputUrl}
alt={outputName}
loading="lazy"
className="aspect-square w-full object-cover"
style={
aspectRatio
? {
aspectRatio:
String(
aspectRatio
).replace(
":",
" / "
),
}
: undefined
}
/>
</a>

<span className="absolute left-2 top-2 rounded-full bg-emerald-500/85 px-2 py-1 text-[9px] font-bold text-white">
After
</span>
</div>

<div className="flex items-center justify-between gap-2 px-2.5 py-2">
<div className="min-w-0">
<p className="mb-0 truncate text-[10px] font-semibold text-white/65">
{outputName}
</p>

{outputWidth &&
outputHeight && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{outputWidth} ×{" "}
{outputHeight}
</p>
)}

{!outputWidth &&
!outputHeight &&
targetSize && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{String(
targetSize
)}
</p>
)}
</div>

<div className="flex flex-shrink-0 items-center gap-1">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
title="Open resized image"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>

<a
href={outputUrl}
download={
outputName ||
"resized-image.png"
}
title="Download resized image"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
</div>
</div>

{aspectRatio && (
<div className="mt-2 flex flex-wrap gap-2">
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Aspect ratio{" "}
{aspectRatio}
</span>
</div>
)}
</div>
);
};

const normalizePaletteColor = (
value
) => {
const normalized =
String(value || "")
.trim()
.toUpperCase();

if (
!/^#[0-9A-F]{6}$/.test(
normalized
)
) {
return null;
}

return normalized;
};

const copyPaletteColor = async (
color
) => {
try {
await navigator.clipboard.writeText(
color
);
} catch (_) {
const textarea =
document.createElement(
"textarea"
);

textarea.value = color;
textarea.setAttribute(
"readonly",
""
);
textarea.style.position =
"fixed";
textarea.style.opacity =
"0";

document.body.appendChild(
textarea
);
textarea.select();
document.execCommand(
"copy"
);
document.body.removeChild(
textarea
);
}
};

const ColorPaletteTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

const [
copiedColor,
setCopiedColor,
] = useState(null);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.extractColorPalette" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction
?.status || ""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);
setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh color-palette extraction status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.extractColorPalette" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const inputUrl =
transaction?.inputAssetUrl ||
transaction?.input
?.savedInputAssetUrl ||
transaction?.input
?.originalImageUrl ||
executionResult
?.inputAssetUrl ||
null;

const rawColors =
Array.isArray(
transaction?.output?.colors
)
? transaction.output.colors
: Array.isArray(
transaction?.output?.palette
)
? transaction.output.palette
: [];

const colors = [
...new Set(
rawColors
.map(
normalizePaletteColor
)
.filter(Boolean)
),
];

const handleCopyColor =
async (color) => {
await copyPaletteColor(
color
);

setCopiedColor(
color
);

window.setTimeout(() => {
setCopiedColor(
(current) =>
current === color
? null
: current
);
}, 1500);
};

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Color palette extraction failed and the charged credits were refunded."
: "Color palette extraction failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status ===
"refunded"
? "Palette extraction failed · Credits refunded"
: "Color palette extraction failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (
status !== "completed"
) {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Extracting color palette
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (
colors.length === 0
) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Color palette extraction completed, but no valid hexadecimal colors were returned.
</p>
</div>
);
}

return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-3 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-xs font-bold text-emerald-100">
Palette extraction complete
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
{colors.length} color
{colors.length === 1
? ""
: "s"}{" "}
extracted
</p>
</div>

<i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
</div>

<div className="grid grid-cols-1 gap-3 sm:grid-cols-[128px_minmax(0,1fr)]">
<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
{inputUrl ? (
<a
href={inputUrl}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={inputUrl}
alt="Palette source"
loading="lazy"
className="aspect-square w-full object-cover"
/>
</a>
) : (
<div className="flex aspect-square w-full items-center justify-center text-white/25">
<i className="fa-regular fa-image text-2xl"></i>
</div>
)}

<div className="px-2.5 py-2">
<p className="mb-0 truncate text-[10px] font-semibold text-white/60">
Source image
</p>
</div>
</div>

<div className="min-w-0 space-y-2">
{colors.map(
(color) => (
<button
key={color}
type="button"
onClick={() =>
handleCopyColor(
color
)
}
title={`Copy ${color}`}
className="flex w-full items-center gap-3 rounded-xl border border-white/[0.08] bg-black/20 p-2 text-left transition hover:border-white/[0.16] hover:bg-white/[0.06]"
>
<span
className="h-9 w-12 flex-shrink-0 rounded-lg border border-white/15"
style={{
backgroundColor:
color,
}}
></span>

<span className="min-w-0 flex-1 font-mono text-xs font-bold text-white/75">
{color}
</span>

<span
className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
copiedColor ===
color
? "bg-emerald-400/20 text-emerald-200"
: "bg-white/[0.07] text-white/45"
}`}
>
<i
className={`fa-solid ${
copiedColor ===
color
? "fa-check"
: "fa-copy"
} text-[9px]`}
></i>
</span>
</button>
)
)}
</div>
</div>

<button
type="button"
onClick={async () => {
const paletteText =
colors.join(", ");

await copyPaletteColor(
paletteText
);

setCopiedColor(
"__ALL__"
);

window.setTimeout(() => {
setCopiedColor(
(current) =>
current === "__ALL__"
? null
: current
);
}, 1500);
}}
className="mt-3 inline-flex h-8 items-center gap-2 rounded-lg bg-white/[0.07] px-3 text-[10px] font-bold text-white/60 transition hover:bg-white/[0.12] hover:text-white"
>
<i
className={`fa-solid ${
copiedColor ===
"__ALL__"
? "fa-check"
: "fa-copy"
} text-[9px]`}
></i>

{copiedColor ===
"__ALL__"
? "Palette copied"
: "Copy full palette"}
</button>
</div>
);
};

const normalizeColorGradePreviewColor = (
value
) => {
const normalized =
String(value || "")
.trim()
.toUpperCase();

if (
!/^#[0-9A-F]{6}$/.test(
normalized
)
) {
return null;
}

return normalized;
};

const getColorGradePreviewColors = ({
transaction = null,
executionResult = null,
} = {}) => {
const rawColors =
Array.isArray(
transaction?.output?.colors
)
? transaction.output.colors
: Array.isArray(
transaction?.input?.colors
)
? transaction.input.colors
: Array.isArray(
executionResult?.colors
)
? executionResult.colors
: [];

return [
...new Set(
rawColors
.map(
normalizeColorGradePreviewColor
)
.filter(Boolean)
),
];
};

const ImageColorGradeTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.colorGradeImage" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction
?.status || ""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);
setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh image-color-grade status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.colorGradeImage" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const inputUrl =
transaction?.inputAssetUrl ||
transaction?.input
?.savedInputAssetUrl ||
transaction?.input
?.originalImageUrl ||
executionResult
?.inputAssetUrl ||
null;

const outputUrl =
transaction?.outputAssetUrl ||
transaction?.output?.url ||
transaction?.output
?.outputAssetUrl ||
null;

const outputName =
transaction?.output?.name ||
"Color-graded image";

const outputSize =
transaction?.output
?.outputSize ||
null;

const outputFormat =
transaction?.output
?.outputFormat ||
null;

const colors =
getColorGradePreviewColors({
transaction,
executionResult,
});

const colorSource =
executionResult?.colorSource ===
"brand_kit"
? "Brand Kit colors"
: "Custom colors";

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Image color grading failed and the charged credits were refunded."
: "Image color grading failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status ===
"refunded"
? "Color grading failed · Credits refunded"
: "Image color grading failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (
status !== "completed"
) {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Color grading image
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (!outputUrl) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Image color grading completed, but no previewable output was returned.
</p>
</div>
);
}

return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-2.5 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-xs font-bold text-emerald-100">
Color grading complete
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
Color-graded image saved to Media Library
</p>
</div>

<i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
</div>

<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{inputUrl ? (
<a
href={inputUrl}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={inputUrl}
alt="Original image"
loading="lazy"
className="aspect-square w-full object-cover"
/>
</a>
) : (
<div className="flex aspect-square w-full items-center justify-center text-white/25">
<i className="fa-regular fa-image text-2xl"></i>
</div>
)}

<span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
Before
</span>
</div>

<div className="px-2.5 py-2">
<p className="mb-0 truncate text-[10px] font-semibold text-white/60">
Original image
</p>
</div>
</div>

<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={outputUrl}
alt={outputName}
loading="lazy"
className="aspect-square w-full object-cover"
/>
</a>

<span className="absolute left-2 top-2 rounded-full bg-emerald-500/85 px-2 py-1 text-[9px] font-bold text-white">
After
</span>
</div>

<div className="flex items-center justify-between gap-2 px-2.5 py-2">
<div className="min-w-0">
<p className="mb-0 truncate text-[10px] font-semibold text-white/65">
{outputName}
</p>

{outputSize && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{String(
outputSize
)}
</p>
)}

{!outputSize &&
outputFormat && (
<p className="mb-0 mt-0.5 text-[9px] uppercase text-white/30">
{String(
outputFormat
)}
</p>
)}
</div>

<div className="flex flex-shrink-0 items-center gap-1">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
title="Open color-graded image"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>

<a
href={outputUrl}
download={
outputName ||
"color-graded-image.png"
}
title="Download color-graded image"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
</div>
</div>

<div className="mt-3 rounded-xl border border-white/[0.07] bg-black/15 p-2.5">
<p className="mb-2 text-[9px] font-bold uppercase tracking-[0.08em] text-white/35">
{colorSource}
</p>

<div className="flex flex-wrap gap-2">
{colors.map(
(color) => (
<div
key={color}
className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-2 py-1.5"
>
<span
className="h-5 w-5 rounded-md border border-white/15"
style={{
backgroundColor:
color,
}}
></span>

<span className="font-mono text-[9px] font-bold text-white/60">
{color}
</span>
</div>
)
)}
</div>
</div>
</div>
);
};

const BackgroundRemovalTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.removeBackground" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);
setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh background-removal status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.removeBackground" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const mediaKind =
String(
transaction?.output?.mediaKind ||
transaction?.input?.mediaKind ||
executionResult?.mediaKind ||
"image"
).toLowerCase() ===
"video"
? "video"
: "image";

const inputUrl =
transaction?.inputAssetUrl ||
transaction?.input?.savedInputAssetUrl ||
transaction?.input?.originalUrl ||
transaction?.input?.originalImageUrl ||
executionResult?.inputAssetUrl ||
null;

const outputUrl =
transaction?.outputAssetUrl ||
transaction?.output?.url ||
transaction?.output?.outputAssetUrl ||
null;

const outputName =
transaction?.output?.name ||
(
mediaKind === "video"
? "Background-removed video"
: "Background-removed image"
);

const durationSeconds =
transaction?.output?.durationSeconds ||
transaction?.input?.durationSeconds ||
executionResult?.durationSeconds ||
null;

const billableSeconds =
transaction?.output?.billableSeconds ||
transaction?.input?.billableSeconds ||
executionResult?.billableSeconds ||
null;

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Background removal failed and the charged credits were refunded."
: "Background removal failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status === "refunded"
? "Background removal failed · Credits refunded"
: "Background removal failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (status !== "completed") {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Removing {mediaKind} background
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (!outputUrl) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Background removal completed, but no previewable output was returned.
</p>
</div>
);
}

const renderMedia = ({
url,
label,
isOutput = false,
}) => {
if (!url) {
return (
<div className="flex aspect-square w-full items-center justify-center text-white/25">
<i
className={`fa-solid ${
mediaKind === "video"
? "fa-video"
: "fa-image"
} text-2xl`}
></i>
</div>
);
}

if (mediaKind === "video") {
return (
<video
src={url}
controls
playsInline
preload="metadata"
className="aspect-video w-full bg-black object-contain"
aria-label={label}
/>
);
}

return (
<a
href={url}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={url}
alt={label}
loading="lazy"
className={`aspect-square w-full ${
isOutput
? "bg-[linear-gradient(45deg,#202020_25%,transparent_25%),linear-gradient(-45deg,#202020_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#202020_75%),linear-gradient(-45deg,transparent_75%,#202020_75%)] bg-[length:20px_20px] bg-[position:0_0,0_10px,10px_-10px,-10px_0px]"
: ""
} object-contain`}
/>
</a>
);
};

return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-2.5 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-xs font-bold text-emerald-100">
Background removal complete
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
Background-removed {mediaKind} saved to Media Library
</p>
</div>

<i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
</div>

<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{renderMedia({
url:
inputUrl,
label:
`Original ${mediaKind}`,
})}

<span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
Before
</span>
</div>

<div className="px-2.5 py-2">
<p className="mb-0 truncate text-[10px] font-semibold text-white/60">
Original {mediaKind}
</p>
</div>
</div>

<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{renderMedia({
url:
outputUrl,
label:
outputName,
isOutput:
true,
})}

<span className="absolute left-2 top-2 rounded-full bg-emerald-500/85 px-2 py-1 text-[9px] font-bold text-white">
After
</span>
</div>

<div className="flex items-center justify-between gap-2 px-2.5 py-2">
<div className="min-w-0">
<p className="mb-0 truncate text-[10px] font-semibold text-white/65">
{outputName}
</p>

{durationSeconds && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{Number(
durationSeconds
).toFixed(1)} seconds
{billableSeconds
? ` · ${billableSeconds} billable`
: ""}
</p>
)}
</div>

<div className="flex flex-shrink-0 items-center gap-1">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
title={`Open background-removed ${mediaKind}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>

<a
href={outputUrl}
download={
outputName ||
(
mediaKind === "video"
? "background-removed-video.webm"
: "background-removed-image.png"
)
}
title={`Download background-removed ${mediaKind}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
</div>
</div>
</div>
);
};

const UpscaleMediaTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.upscaleMedia" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(initialStatus)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);
setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(latestStatus)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh Upscaler status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.upscaleMedia" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const mediaKind =
String(
transaction?.output?.mediaKind ||
transaction?.input?.mediaKind ||
executionResult?.mediaKind ||
"image"
).toLowerCase() ===
"video"
? "video"
: "image";

const scale =
Number(
transaction?.output?.scale ||
transaction?.input?.scale ||
executionResult?.scale ||
0
);

const inputUrl =
transaction?.inputAssetUrl ||
transaction?.input?.savedInputAssetUrl ||
transaction?.input?.originalUrl ||
executionResult?.inputAssetUrl ||
null;

const outputUrl =
transaction?.outputAssetUrl ||
transaction?.output?.url ||
transaction?.output?.outputAssetUrl ||
null;

const outputName =
transaction?.output?.name ||
(
mediaKind === "video"
? "Upscaled video"
: "Upscaled image"
);

const durationSeconds =
transaction?.output?.durationSeconds ||
transaction?.input?.durationSeconds ||
executionResult?.durationSeconds ||
null;

const billableSeconds =
transaction?.output?.billableSeconds ||
transaction?.input?.billableSeconds ||
executionResult?.billableSeconds ||
null;

const inputWidth =
transaction?.output?.inputWidth ||
executionResult?.inputWidth ||
null;

const inputHeight =
transaction?.output?.inputHeight ||
executionResult?.inputHeight ||
null;

const outputWidth =
transaction?.output?.outputWidth ||
executionResult?.outputWidth ||
null;

const outputHeight =
transaction?.output?.outputHeight ||
executionResult?.outputHeight ||
null;

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Upscaling failed and the charged credits were refunded."
: "Upscaling failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status === "refunded"
? "Upscaling failed · Credits refunded"
: "Upscaling failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (status !== "completed") {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Upscaling {mediaKind}
{scale > 0
? ` ${scale}x`
: ""}
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (!outputUrl) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Upscaling completed, but no previewable output was returned.
</p>
</div>
);
}

const renderMedia = ({
url,
label,
}) => {
if (!url) {
return (
<div className="flex aspect-square w-full items-center justify-center text-white/25">
<i
className={`fa-solid ${
mediaKind === "video"
? "fa-video"
: "fa-image"
} text-2xl`}
></i>
</div>
);
}

if (mediaKind === "video") {
return (
<video
src={url}
controls
playsInline
preload="metadata"
className="aspect-video w-full bg-black object-contain"
aria-label={label}
/>
);
}

return (
<a
href={url}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={url}
alt={label}
loading="lazy"
className="aspect-square w-full bg-black object-contain"
/>
</a>
);
};

return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-2.5 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-xs font-bold text-emerald-100">
Upscaling complete
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
Upscaled {mediaKind}
{scale > 0
? ` at ${scale}x`
: ""}{" "}
saved to Media Library
</p>
</div>

<i className="fa-solid fa-circle-check text-sm text-emerald-300"></i>
</div>

<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{renderMedia({
url:
inputUrl,
label:
`Original ${mediaKind}`,
})}

<span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
Before
</span>
</div>

<div className="px-2.5 py-2">
<p className="mb-0 truncate text-[10px] font-semibold text-white/60">
Original {mediaKind}
</p>

{inputWidth &&
inputHeight && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{inputWidth} ×{" "}
{inputHeight}
</p>
)}
</div>
</div>

<div className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/20">
<div className="relative">
{renderMedia({
url:
outputUrl,
label:
outputName,
})}

<span className="absolute left-2 top-2 rounded-full bg-emerald-500/85 px-2 py-1 text-[9px] font-bold text-white">
After
</span>
</div>

<div className="flex items-center justify-between gap-2 px-2.5 py-2">
<div className="min-w-0">
<p className="mb-0 truncate text-[10px] font-semibold text-white/65">
{outputName}
</p>

{outputWidth &&
outputHeight && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{outputWidth} ×{" "}
{outputHeight}
</p>
)}

{durationSeconds && (
<p className="mb-0 mt-0.5 text-[9px] text-white/30">
{Number(
durationSeconds
).toFixed(1)} seconds
{billableSeconds
? ` · ${billableSeconds} billable`
: ""}
</p>
)}
</div>

<div className="flex flex-shrink-0 items-center gap-1">
<a
href={outputUrl}
target="_blank"
rel="noreferrer"
title={`Open upscaled ${mediaKind}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>

<a
href={outputUrl}
download={
outputName ||
(
mediaKind === "video"
? "upscaled-video.mp4"
: "upscaled-image.png"
)
}
title={`Download upscaled ${mediaKind}`}
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.07] text-white/55 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
</div>
</div>

<div className="mt-2 flex flex-wrap gap-2">
{scale > 0 && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Upscale {scale}x
</span>
)}

{outputWidth &&
outputHeight && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Output {outputWidth} ×{" "}
{outputHeight}
</span>
)}
</div>
</div>
);
};

const MoodboardMarkdownPreview = ({
markdown = "",
}) => {
const lines =
String(
markdown || ""
)
.replace(
/\r\n/g,
"\n"
)
.split("\n");

const elements = [];
let listItems = [];
let listType = null;

const flushList = () => {
if (listItems.length === 0) {
return;
}

const ListTag =
listType === "ordered"
? "ol"
: "ul";

elements.push(
<ListTag
key={`moodboard-list-${elements.length}`}
className={`space-y-1 pl-5 text-xs leading-relaxed text-white/65 ${
listType === "ordered"
? "list-decimal"
: "list-disc"
}`}
>
{listItems.map(
(item, index) => (
<li
key={`${item}-${index}`}
className="pl-1"
>
{item}
</li>
)
)}
</ListTag>
);

listItems = [];
listType = null;
};

lines.forEach(
(rawLine, index) => {
const line =
String(
rawLine || ""
);

const trimmed =
line.trim();

if (!trimmed) {
flushList();
return;
}

const headingMatch =
trimmed.match(
/^(#{1,6})\s+(.+)$/
);

if (headingMatch) {
flushList();

const level =
headingMatch[1].length;

const headingText =
headingMatch[2];

const headingClasses = {
1:
"text-base font-black text-white",
2:
"text-sm font-black text-white/90",
3:
"text-xs font-bold uppercase tracking-wide text-white/75",
4:
"text-xs font-bold text-white/70",
5:
"text-[11px] font-bold text-white/65",
6:
"text-[10px] font-bold uppercase tracking-wide text-white/55",
};

const HeadingTag =
`h${level}`;

elements.push(
<HeadingTag
key={`moodboard-heading-${index}`}
className={`mb-0 mt-3 first:mt-0 ${
headingClasses[level]
}`}
>
{headingText}
</HeadingTag>
);

return;
}

const unorderedMatch =
trimmed.match(
/^[-*+]\s+(.+)$/
);

if (unorderedMatch) {
if (
listType &&
listType !== "unordered"
) {
flushList();
}

listType =
"unordered";

listItems.push(
unorderedMatch[1]
);

return;
}

const orderedMatch =
trimmed.match(
/^\d+[.)]\s+(.+)$/
);

if (orderedMatch) {
if (
listType &&
listType !== "ordered"
) {
flushList();
}

listType =
"ordered";

listItems.push(
orderedMatch[1]
);

return;
}

flushList();

if (
trimmed.startsWith(
"> "
)
) {
elements.push(
<blockquote
key={`moodboard-quote-${index}`}
className="border-l-2 border-violet-300/30 pl-3 text-xs italic leading-relaxed text-white/55"
>
{trimmed.slice(2)}
</blockquote>
);

return;
}

if (
trimmed === "---" ||
trimmed === "***" ||
trimmed === "___"
) {
elements.push(
<hr
key={`moodboard-rule-${index}`}
className="border-white/[0.08]"
/>
);

return;
}

elements.push(
<p
key={`moodboard-paragraph-${index}`}
className="mb-0 whitespace-pre-wrap text-xs leading-relaxed text-white/65"
>
{trimmed}
</p>
);
}
);

flushList();

return (
<div className="space-y-2">
{elements}
</div>
);
};

const cleanMoodboardStyleName = (
value = ""
) => {
return String(
value ||
""
)
.replace(
/^#{1,6}\s*/,
""
)
.replace(
/^\*+|\*+$/g,
""
)
.replace(
/^["']|["']$/g,
""
)
.trim();
};

const isGenericMoodboardStyleName = (
value = ""
) => {
const normalized =
cleanMoodboardStyleName(
value
)
.toLowerCase();

return [
"",
"moodboard",
"moodboard style",
"generated moodboard",
"visual style guide",
].includes(
normalized
);
};

const extractMoodboardStyleName = ({
markdown = "",
explicitName = "",
} = {}) => {
const raw =
String(
markdown ||
""
).trim();

const objectNameMatch =
raw.match(
/['"]Moodboard Name['"]\s*:\s*['"]([^'"]+)['"]/i
);

if (
objectNameMatch?.[1]
) {
const objectName =
cleanMoodboardStyleName(
objectNameMatch[1]
);

if (
!isGenericMoodboardStyleName(
objectName
)
) {
return objectName;
}
}

const headingMatch =
raw.match(
/^#{1,6}\s*(.+)$/m
);

if (
headingMatch?.[1]
) {
const headingName =
cleanMoodboardStyleName(
headingMatch[1]
);

if (
!isGenericMoodboardStyleName(
headingName
)
) {
return headingName;
}
}

const cleanedExplicitName =
cleanMoodboardStyleName(
explicitName
);

if (
!isGenericMoodboardStyleName(
cleanedExplicitName
)
) {
return cleanedExplicitName;
}

return "Moodboard Style";
};

const buildBrandStyleKey = (
value = ""
) => {
return String(
value ||
""
)
.trim()
.replace(
/\s+/g,
"_"
)
.replace(
/[^a-zA-Z0-9_-]/g,
""
);
};


const MoodboardTransactionPreview = ({
pendingAction,
}) => {
const executionResult =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object"
? pendingAction.executionResult
: null;

const transactionId =
executionResult?.transactionId ||
executionResult?.transaction?._id ||
executionResult?.transaction?.id ||
null;

const [
transaction,
setTransaction,
] = useState(
executionResult?.transaction ||
null
);

const [
pollingError,
setPollingError,
] = useState(null);

const [
copied,
setCopied,
] = useState(false);

const [
styleName,
setStyleName,
] = useState("");

const [
styleSaveStatus,
setStyleSaveStatus,
] = useState("idle");

const [
styleSaveError,
setStyleSaveError,
] = useState(null);

const {
selectedGroup,
groups,
} = useSelector(
(state) =>
state.groups ||
{}
);

const selectedGroupId =
useMemo(() => {
if (
!selectedGroup ||
!Array.isArray(
groups
)
) {
return null;
}

const selectedGroupRecord =
groups.find(
(group) =>
String(
group?.name ||
""
) ===
String(
selectedGroup
)
);

return (
selectedGroupRecord?._id ||
selectedGroupRecord?.id ||
null
);
}, [
groups,
selectedGroup,
]);

const moodboardForStyleName =
String(
transaction?.output?.moodboard ||
executionResult?.moodboard ||
""
).trim();

const explicitMoodboardName =
transaction?.output?.name ||
executionResult?.name ||
"";

useEffect(() => {
const extractedName =
extractMoodboardStyleName({
markdown:
moodboardForStyleName,
explicitName:
explicitMoodboardName,
});

setStyleName(
extractedName
);

setStyleSaveStatus(
"idle"
);

setStyleSaveError(
null
);
}, [
transactionId,
moodboardForStyleName,
explicitMoodboardName,
]);

useEffect(() => {
if (
pendingAction?.toolName !==
"apps.generateMoodboard" ||
!transactionId
) {
return undefined;
}

const initialStatus =
String(
executionResult?.transaction
?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(
initialStatus
)
) {
return undefined;
}

let cancelled = false;
let timerId = null;

const pollTransaction =
async () => {
try {
const latestTransaction =
await fetchAppTransaction(
transactionId
);

if (cancelled) {
return;
}

setTransaction(
latestTransaction
);

setPollingError(null);

const latestStatus =
String(
latestTransaction?.status ||
""
).toLowerCase();

if (
[
"completed",
"failed",
"refunded",
].includes(
latestStatus
)
) {
return;
}
} catch (error) {
if (cancelled) {
return;
}

setPollingError(
error.message ||
"Could not refresh Moodboard status."
);
}

if (!cancelled) {
timerId =
window.setTimeout(
pollTransaction,
3000
);
}
};

pollTransaction();

return () => {
cancelled = true;

if (timerId) {
window.clearTimeout(
timerId
);
}
};
}, [
executionResult,
pendingAction?.toolName,
transactionId,
]);

if (
pendingAction?.toolName !==
"apps.generateMoodboard" ||
!transactionId
) {
return null;
}

const status =
String(
transaction?.status ||
executionResult?.status ||
"processing"
).toLowerCase();

const moodboard =
String(
transaction?.output?.moodboard ||
executionResult?.moodboard ||
""
).trim();

const moodboardName =
transaction?.output?.name ||
executionResult?.name ||
"Moodboard";

const moodboardDescription =
transaction?.output?.description ||
executionResult?.description ||
null;

const imageUrls =
Array.isArray(
transaction?.input?.imageUrls
)
? transaction.input.imageUrls
: Array.isArray(
executionResult?.imageUrls
)
? executionResult.imageUrls
: [];

const imageCount =
Number(
transaction?.output?.imageCount ||
transaction?.input?.totalImages ||
executionResult?.imageCount ||
imageUrls.length ||
0
);

const brandContextSource =
transaction?.input
?.brandContextSource ||
executionResult
?.brandContextSource ||
null;

if (
status === "failed" ||
status === "refunded"
) {
const errorMessage =
transaction?.errorMessage ||
transaction?.refundError ||
(
status === "refunded"
? "Moodboard generation failed and the charged credits were refunded."
: "Moodboard generation failed."
);

return (
<div className="border-t border-red-400/10 bg-red-400/[0.05] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-xs text-red-300"></i>

<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-red-100">
{status === "refunded"
? "Moodboard generation failed · Credits refunded"
: "Moodboard generation failed"}
</p>

<p className="mb-0 break-words text-xs leading-relaxed text-red-100/65">
{errorMessage}
</p>
</div>
</div>
</div>
);
}

if (
status !== "completed"
) {
return (
<div className="border-t border-blue-400/10 bg-blue-400/[0.04] px-3.5 py-3">
<div className="flex items-center gap-2.5">
<i className="fa-solid fa-spinner fa-spin text-xs text-blue-200"></i>

<div className="min-w-0">
<p className="mb-0 text-xs font-bold text-blue-100">
Generating moodboard
</p>

<p className="mb-0 mt-0.5 text-[10px] text-blue-100/45">
Transaction{" "}
{String(
transactionId
)}
</p>
</div>
</div>

{pollingError && (
<p className="mb-0 mt-2 text-[10px] text-amber-100/60">
{pollingError} Retrying…
</p>
)}
</div>
);
}

if (!moodboard) {
return (
<div className="border-t border-amber-400/10 bg-amber-400/[0.04] px-3.5 py-3">
<p className="mb-0 text-xs text-amber-100/70">
Moodboard generation completed, but no Markdown result was returned.
</p>
</div>
);
}

const copyMoodboard =
async () => {
try {
await navigator.clipboard.writeText(
moodboard
);
} catch (_) {
const textarea =
document.createElement(
"textarea"
);

textarea.value =
moodboard;

textarea.setAttribute(
"readonly",
""
);

textarea.style.position =
"fixed";

textarea.style.opacity =
"0";

document.body.appendChild(
textarea
);

textarea.select();

document.execCommand(
"copy"
);

document.body.removeChild(
textarea
);
}

setCopied(true);

window.setTimeout(() => {
setCopied(false);
}, 1500);
};

const saveMoodboardAsBrandStyle =
async () => {
const cleanedStyleName =
cleanMoodboardStyleName(
styleName
);

const styleKey =
buildBrandStyleKey(
cleanedStyleName
);

if (!selectedGroupId) {
setStyleSaveStatus(
"error"
);

setStyleSaveError(
"Select a brand before saving this style."
);

return;
}

if (!styleKey) {
setStyleSaveStatus(
"error"
);

setStyleSaveError(
"Enter a valid style name."
);

return;
}

if (!moodboard) {
setStyleSaveStatus(
"error"
);

setStyleSaveError(
"The Moodboard does not contain a style guide to save."
);

return;
}

setStyleSaveStatus(
"saving"
);

setStyleSaveError(
null
);

try {
const latestBrandKit =
await getOnboardingForGroup(
selectedGroupId
);

const existingStyles =
latestBrandKit
?.Visual_Style_Guides &&
typeof latestBrandKit
.Visual_Style_Guides ===
"object" &&
!Array.isArray(
latestBrandKit
.Visual_Style_Guides
)
? latestBrandKit
.Visual_Style_Guides
: {};

const updatedStyles = {
...existingStyles,
[styleKey]:
moodboard,
};

await updateOnboardingData({
groupId:
selectedGroupId,
Visual_Style_Guides:
updatedStyles,
});

setStyleSaveStatus(
"saved"
);

setStyleName(
cleanedStyleName
);
} catch (error) {
setStyleSaveStatus(
"error"
);

setStyleSaveError(
error.response?.data?.message ||
error.message ||
"Failed to add the Moodboard to Brand Styles."
);
}
};


return (
<div className="border-t border-emerald-400/10 bg-emerald-400/[0.035] px-3.5 py-3">
<div className="mb-3 flex items-start justify-between gap-3">
<div className="min-w-0">
<p className="mb-0 truncate text-sm font-bold text-emerald-100">
{moodboardName}
</p>

<p className="mb-0 mt-0.5 text-[10px] text-white/35">
Moodboard generation complete
{imageCount > 0
? ` · ${imageCount} reference image${
imageCount === 1
? ""
: "s"
}`
: ""}
</p>

{moodboardDescription && (
<p className="mb-0 mt-1 text-xs leading-relaxed text-white/45">
{moodboardDescription}
</p>
)}
</div>

<i className="fa-solid fa-circle-check mt-0.5 text-sm text-emerald-300"></i>
</div>

{imageUrls.length > 0 && (
<div className="mb-3">
<p className="mb-2 text-[9px] font-bold uppercase tracking-[0.08em] text-white/35">
Reference images
</p>

<div className="flex flex-wrap gap-2">
{imageUrls
.slice(
0,
12
)
.map(
(url, index) => (
<a
key={`${url}-${index}`}
href={url}
target="_blank"
rel="noreferrer"
className="relative block h-20 w-20 overflow-hidden rounded-lg border border-white/[0.08] bg-black/25"
>
<img
src={url}
alt={`Moodboard reference ${index + 1}`}
loading="lazy"
className="h-full w-full object-cover"
/>

<span className="absolute left-1 top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-black/75 px-1 text-[8px] font-black text-white">
{index + 1}
</span>
</a>
)
)}

{imageUrls.length >
12 && (
<div className="flex h-20 w-20 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-xs font-bold text-white/45">
+{imageUrls.length - 12}
</div>
)}
</div>
</div>
)}

<div className="max-h-[520px] overflow-y-auto rounded-xl border border-white/[0.08] bg-black/20 p-3.5">
<MoodboardMarkdownPreview
markdown={
moodboard
}
/>
</div>

<div className="mt-3 rounded-xl border border-white/[0.08] bg-black/15 p-3">
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/35">
Brand Style name
</label>

<div className="flex flex-col gap-2 sm:flex-row">
<input
type="text"
value={
styleName
}
onChange={(event) => {
setStyleName(
event.target.value
);

setStyleSaveStatus(
"idle"
);

setStyleSaveError(
null
);
}}
maxLength={180}
placeholder="Enter a Brand Style name"
className="h-9 min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs font-bold text-white outline-none placeholder:text-white/25"
/>

<button
type="button"
disabled={
styleSaveStatus ===
"saving" ||
!String(
styleName ||
""
).trim()
}
onClick={
saveMoodboardAsBrandStyle
}
className="inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-white px-3.5 text-[10px] font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
>
<i
className={`fa-solid ${
styleSaveStatus ===
"saving"
? "fa-spinner fa-spin"
: styleSaveStatus ===
"saved"
? "fa-check"
: "fa-bookmark"
} text-[9px]`}
></i>

{styleSaveStatus ===
"saving"
? "Saving..."
: styleSaveStatus ===
"saved"
? "Added to Brand Styles"
: "Add to Brand Styles"}
</button>
</div>

{styleSaveStatus ===
"saved" && (
<p className="mb-0 mt-2 text-[10px] font-semibold text-emerald-200/70">
This visual direction is now available in the selected brand’s Brand Styles.
</p>
)}

{styleSaveError && (
<p className="mb-0 mt-2 text-[10px] font-semibold text-red-200/75">
{styleSaveError}
</p>
)}
</div>

<div className="mt-3 flex flex-wrap items-center gap-2">
<button
type="button"
onClick={
copyMoodboard
}
className="inline-flex h-8 items-center gap-2 rounded-lg bg-white/[0.07] px-3 text-[10px] font-bold text-white/60 transition hover:bg-white/[0.12] hover:text-white"
>
<i
className={`fa-solid ${
copied
? "fa-check"
: "fa-copy"
} text-[9px]`}
></i>

{copied
? "Moodboard copied"
: "Copy Markdown"}
</button>

{brandContextSource && (
<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Context:{" "}
{String(
brandContextSource
)
.replace(
/_/g,
" "
)}
</span>
)}

<span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-semibold text-white/45">
Markdown style guide
</span>
</div>
</div>
);
};


const DraftBatchExecutionResults = ({
pendingAction,
}) => {
const result =
pendingAction?.executionResult &&
typeof pendingAction.executionResult ===
"object" &&
!Array.isArray(
pendingAction.executionResult
)
? pendingAction.executionResult
: null;

if (
result?.operation !==
"create_draft_batch"
) {
return null;
}

const results =
Array.isArray(
result.results
)
? result.results
: [];
const successfulCount =
Number(
result.successfulDraftCount ||
results.filter(
(item) =>
item?.success === true
).length ||
0
);
const failedCount =
Number(
result.failedDraftCount ||
results.filter(
(item) =>
item?.success !== true
).length ||
0
);
const requestedCount =
Number(
result.requestedDraftCount ||
results.length ||
successfulCount +
failedCount
);
const partialSuccess =
result.partialSuccess ===
true ||
(
successfulCount > 0 &&
failedCount > 0
);

return (
<div className="border-t border-white/[0.06] px-3.5 py-3">
<div
className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-3 ${
failedCount === 0
? "border-emerald-300/[0.12] bg-emerald-400/[0.05]"
: partialSuccess
? "border-amber-300/[0.12] bg-amber-400/[0.05]"
: "border-red-300/[0.12] bg-red-400/[0.05]"
}`}
>
<div>
<p className="mb-0 text-xs font-bold text-white/80">
{failedCount === 0
? "Batch creation complete"
: partialSuccess
? "Batch partially completed"
: "Batch creation failed"}
</p>
<p className="mb-0 mt-1 text-[10px] text-white/40">
{successfulCount} of {requestedCount} draft proposal
{requestedCount === 1
? ""
: "s"}{" "}
created successfully.
</p>
</div>
<div className="flex items-center gap-2">
<span className="rounded-full bg-emerald-400/12 px-2.5 py-1 text-[9px] font-bold text-emerald-100/70">
{successfulCount} successful
</span>
{failedCount > 0 && (
<span className="rounded-full bg-red-400/12 px-2.5 py-1 text-[9px] font-bold text-red-100/70">
{failedCount} failed
</span>
)}
</div>
</div>

{results.length > 0 && (
<div className="space-y-2">
{results.map(
(item, index) => {
const successful =
item?.success === true;
const errorMessage =
item?.error?.message ||
item?.message ||
(
successful
? null
: "Draft creation failed."
);
const createdCount =
Number(
item?.createdCount ||
0
);

return (
<div
key={
item?.draftId ||
`draft-result-${index}`
}
className={`rounded-xl border px-3 py-3 ${
successful
? "border-emerald-300/[0.09] bg-emerald-400/[0.035]"
: "border-red-300/[0.1] bg-red-400/[0.04]"
}`}
>
<div className="flex items-start gap-3">
<span
className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
successful
? "bg-emerald-400/15 text-emerald-200"
: "bg-red-400/15 text-red-200"
}`}
>
<i
className={`fa-solid ${
successful
? "fa-check"
: "fa-xmark"
} text-[9px]`}
></i>
</span>
<div className="min-w-0 flex-1">
<p className="mb-0 text-[11px] font-bold text-white/75">
{item?.label ||
`Draft ${index + 1}`}
</p>
<p className="mb-0 mt-1 text-[10px] leading-relaxed text-white/40">
{successful
? `${createdCount} draft record${
createdCount === 1
? ""
: "s"
} created.`
: errorMessage}
</p>
{item?.draftId && (
<p className="mb-0 mt-1 text-[9px] text-white/25">
Draft ID:{" "}
{String(
item.draftId
)}
</p>
)}
</div>
</div>
</div>
);
}
)}
</div>
)}
</div>
);
};

const PendingActionCard = ({
pendingAction,
onConfirmAction,
onCancelAction,
onEditAction,
activeActionId,
actionStatus,
}) => {
  if (
    !pendingAction ||
    typeof pendingAction !== "object"
  ) {
    return null;
  }

  const actionId =
    getPendingActionId(
      pendingAction
    );

  const status =
    pendingAction.status ||
    "pending_confirmation";

  const summary =
    pendingAction.confirmationSummary ||
    {};

  const statusStyles =
    getActionStatusStyles(status);

  const isPending =
    status ===
    "pending_confirmation";

  const isCurrentAction =
    actionId &&
    activeActionId &&
    String(actionId) ===
      String(activeActionId);

  const isLoading =
isCurrentAction &&
actionStatus === "loading";

const isSelectionAction =
  [
    "posts.delete",
    "posts.duplicate",
    "assets.delete",
    "accounts.disconnect",
  ].includes(
    pendingAction.toolName
  );

const isAssetRename =
pendingAction.toolName ===
"assets.rename";

const isBrandKitUpdate =
pendingAction.toolName ===
"brandKit.update";

const isRemoveBackground =
pendingAction.toolName ===
"apps.removeBackground";

const isUpscaleMedia =
pendingAction.toolName ===
"apps.upscaleMedia";

const isGenerateMoodboard =
pendingAction.toolName ===
"apps.generateMoodboard";

const isGenerateImage =
pendingAction.toolName ===
"apps.generateImage";  
const isGenerateCarousel =
pendingAction.toolName ===
"apps.generateCarousel";  
const isResizeImage =
pendingAction.toolName ===
"apps.resizeImage";

const isExtractColorPalette =
pendingAction.toolName ===
"apps.extractColorPalette";

const isColorGradeImage =
pendingAction.toolName ===
"apps.colorGradeImage";

const isCreditAppAction =
isRemoveBackground ||
isUpscaleMedia ||
isGenerateMoodboard ||
isGenerateImage ||
isGenerateCarousel ||
isResizeImage ||
isExtractColorPalette ||
isColorGradeImage;

const totalCredits =
isCreditAppAction
? getConfirmationTotalCredits(
summary
)
: null;

const assetDetails =
Array.isArray(
  summary.details
)
  ? summary.details.filter(
      (detail) =>
        detail?.asset &&
        typeof detail.asset ===
          "object"
    )
  : [];

const details =
  Array.isArray(
    summary.details
  )
    ? summary.details
    : [];

const accountDetails =
  details.filter(
    (detail) =>
      detail?.account &&
      typeof detail.account ===
        "object"
  );

const sections =
    Array.isArray(
      summary.sections
    )
      ? summary.sections
      : [];

  return (
    <div
      className={`mt-3 overflow-hidden rounded-xl border ${statusStyles.wrapper}`}
    >
      <div className="flex items-start justify-between gap-3 px-3.5 py-3">
        <div className="min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold ${statusStyles.badge}`}
            >
              <i
                className={`${statusStyles.icon} text-[9px]`}
              ></i>
              {statusStyles.label}
            </span>

            {pendingAction.displayName && (
              <span className="text-[10px] font-semibold text-white/35">
                {pendingAction.displayName}
              </span>
            )}
          </div>

          <h4 className="mb-1 text-sm font-bold text-white/90">
            {summary.title ||
              pendingAction.displayName ||
              "Confirm action"}
          </h4>

          {summary.description && (
            <p className="mb-0 whitespace-pre-wrap text-xs leading-relaxed text-white/55">
              {summary.description}
            </p>
          )}
        </div>

        {summary.destructive && (
          <i className="fa-solid fa-triangle-exclamation mt-1 text-sm text-red-300/70"></i>
        )}
      </div>

      {isCreditAppAction &&
totalCredits !== null && (
          <div className="border-t border-violet-400/10 bg-violet-400/[0.055] px-3.5 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-400/15 text-violet-100">
                  <i className="fa-solid fa-coins text-xs"></i>
                </span>

                <div>
                  <p className="mb-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                    {isRemoveBackground
? "Background Remover cost"
: isUpscaleMedia
? "Upscaler cost"
: isGenerateMoodboard
? "Moodboard cost"
: isGenerateCarousel
? "Carousel Maker cost"
: isResizeImage
? "Image Resizer cost"
: isExtractColorPalette
? "Image Color Palette cost"
: isColorGradeImage
? "Image Color Grade cost"
: "Image Generator cost"}
                  </p>

                  <p className="mb-0 mt-0.5 text-xs text-white/55">
                    {isRemoveBackground
? "Charged when background removal is queued"
: isUpscaleMedia
? "Charged when upscaling is queued"
: isGenerateMoodboard
? "Charged when moodboard generation is queued"
: isGenerateCarousel
? "Charged when carousel generation is queued"
: isResizeImage
? "Charged when resizing is queued"
: isExtractColorPalette
? "Charged when extraction is queued"
: isColorGradeImage
? "Charged when color grading is queued"
: "Charged when generation is queued"}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <span className="text-lg font-black text-violet-100">
                  {totalCredits}
                </span>

                <span className="ml-1 text-xs font-semibold text-violet-100/60">
                  credit
                  {totalCredits === 1
                    ? ""
                    : "s"}
                </span>
              </div>
            </div>
          </div>
        )}

      {assetDetails.length > 0 ? (
  <ConfirmationAssetDetails
    details={details}
  />
) : accountDetails.length > 0 ? (
  <ConfirmationAccountDetails
    details={details}
  />
) : details.length > 0 ? (
        <div className="border-t border-white/[0.06] px-3.5 py-3">
          <div className="space-y-2">
            {details.map(
              (
                detail,
                index
              ) => (
                <div
                  key={`${getActionDetailLabel(
                    detail,
                    index
                  )}-${index}`}
                  className="grid grid-cols-[92px_minmax(0,1fr)] gap-2 text-xs"
                >
                  <span className="font-semibold text-white/35">
                    {getActionDetailLabel(
                      detail,
                      index
                    )}
                  </span>

                  <span className="break-words text-white/65">
                    {getActionDetailValue(
                      detail
                    )}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      ) : null}

      {sections.length > 0 && (
        <div className="space-y-2 border-t border-white/[0.06] px-3.5 py-3">
          {sections.map(
            (section, index) => (
              <ConfirmationDestinationSection
                key={
                  section.key ||
                  `${section.platform}-${section.profileId}-${index}`
                }
                section={
                  section
                }
              />
            )
          )}
        </div>
      )}

      {summary.warning && (
        <div
          className={`border-t px-3.5 py-2.5 text-xs ${
            summary.destructive
              ? "border-red-400/10 bg-red-400/[0.05] text-red-100/75"
              : "border-amber-400/10 bg-amber-400/[0.04] text-amber-100/65"
          }`}
        >
          <i className="fa-solid fa-triangle-exclamation mr-2 text-[10px]"></i>
          {summary.warning}
        </div>
      )}

      <BackgroundRemovalTransactionPreview
pendingAction={
pendingAction
}/>

<UpscaleMediaTransactionPreview
pendingAction={
pendingAction
}/>

<MoodboardTransactionPreview
pendingAction={
pendingAction
}/>

<ImageGenerationTransactionPreview
pendingAction={
pendingAction
}
/>  
<CarouselMakerTransactionPreview
pendingAction={
pendingAction
}
/>  
<ImageResizeTransactionPreview
pendingAction={
pendingAction
}
/>

<ColorPaletteTransactionPreview
pendingAction={
pendingAction
}
/>

<ImageColorGradeTransactionPreview
pendingAction={
pendingAction
}
/>

<DraftBatchExecutionResults
pendingAction={
pendingAction
}
/>  
{isPending && (
        <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-3.5 py-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={() =>
              onConfirmAction?.(
                pendingAction
              )
            }
            className={`inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${
              summary.destructive
                ? "bg-red-500 text-white hover:bg-red-400"
                : "bg-white text-black hover:bg-white/90"
            }`}
          >
            <i
              className={`fa-solid ${
                isLoading
                  ? "fa-spinner fa-spin"
                  : summary.destructive
                  ? "fa-trash"
                  : "fa-check"
              } text-[10px]`}
            ></i>

            {isLoading
              ? "Processing..."
              : summary.confirmLabel ||
                "Confirm"}
          </button>

          <button
type="button"
disabled={isLoading}
onClick={() =>
onEditAction?.(
pendingAction
)
}
className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-violet-400/15 px-3.5 text-xs font-bold text-violet-100 transition hover:bg-violet-400/25 disabled:cursor-not-allowed disabled:opacity-50"
>
<i
  className={`fa-solid ${
    isSelectionAction
      ? "fa-list-check"
      : "fa-pen"
  } text-[10px]`}
></i>
{isSelectionAction
? "Change selection"
: isAssetRename
? "Edit rename"
: isBrandKitUpdate
? "Edit values"
: isRemoveBackground
? "Edit background removal"
: isUpscaleMedia
? "Edit upscaling"
: isGenerateMoodboard
? "Edit moodboard"
: isGenerateImage
? "Edit generation"
: isGenerateCarousel
? "Edit carousel"
: isResizeImage
? "Edit resize"
: isExtractColorPalette
? "Edit palette extraction"
: isColorGradeImage
? "Edit color grade"
: "Edit post"}
</button>

<button
type="button"
disabled={isLoading}
onClick={() =>
onCancelAction?.(
pendingAction
)
}
className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-white/[0.07] px-3.5 text-xs font-bold text-white/65 transition hover:bg-white/[0.11] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
>
<i className="fa-solid fa-xmark text-[10px]"></i>
{summary.cancelLabel ||
"Cancel"}
</button>

          <span className="text-[10px] font-medium text-white/30">
            You can also type “yes” to confirm.
          </span>
        </div>
      )}
    </div>
  );
};

const ConnectedProfileSelectionCard = ({
  interaction,
  message,
  enabled,
  loading,
  onSubmit,
}) => {
  const [selectedIds, setSelectedIds] =
    useState([]);
  const [submitted, setSubmitted] =
    useState(false);

  const options =
    Array.isArray(interaction?.options)
      ? interaction.options
      : [];

  if (
    interaction?.type !==
      "connected_profile_selection" ||
    options.length === 0
  ) {
    return null;
  }

  const selectionDisabled =
    !enabled ||
    loading ||
    submitted;

  const toggleProfile = (
    profileId
  ) => {
    if (selectionDisabled) {
      return;
    }

    setSelectedIds(
      (currentSelection) => {
        if (
          currentSelection.includes(
            profileId
          )
        ) {
          return currentSelection.filter(
            (currentId) =>
              currentId !== profileId
          );
        }

        if (
          interaction.multiple ===
          false
        ) {
          return [profileId];
        }

        return [
          ...currentSelection,
          profileId,
        ];
      }
    );
  };

  const submitSelection = async () => {
    if (
      selectionDisabled ||
      selectedIds.length === 0
    ) {
      return;
    }

    const selectedProfiles =
      options.filter((option) =>
        selectedIds.includes(
          option.id
        )
      );

    setSubmitted(true);

    try {
      await onSubmit?.({
        message,
        profiles:
          selectedProfiles,
      });
    } catch (_) {
      setSubmitted(false);
    }
  };

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-violet-400/20 bg-violet-400/[0.06]">
      <div className="px-3.5 py-3">
        <div className="mb-1 flex items-center gap-2">
          <i className="fa-solid fa-users text-xs text-violet-200/70"></i>

          <h4 className="mb-0 text-sm font-bold text-white/90">
            {interaction.title ||
              "Select connected profiles"}
          </h4>
        </div>

        {interaction.description && (
          <p className="mb-0 text-xs leading-relaxed text-white/50">
            {interaction.description}
          </p>
        )}
      </div>

      <div className="border-t border-white/[0.06] px-3.5 py-3">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => {
            const optionId =
              option.id ||
              `${option.platform}:${option.profileId}`;

            const selected =
              selectedIds.includes(
                optionId
              );

            return (
              <button
                key={optionId}
                type="button"
                disabled={
                  selectionDisabled
                }
                onClick={() =>
                  toggleProfile(
                    optionId
                  )
                }
                className={`inline-flex min-w-[150px] items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  selected
                    ? "border-violet-300/45 bg-violet-400/20 text-white"
                    : "border-white/[0.08] bg-white/[0.04] text-white/60 hover:border-white/[0.16] hover:bg-white/[0.08] hover:text-white"
                }`}
              >
                <span
                  className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                    selected
                      ? "bg-violet-300 text-violet-950"
                      : "bg-white/[0.07] text-white/45"
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      selected
                        ? "fa-check"
                        : "fa-user"
                    } text-[9px]`}
                  ></i>
                </span>

                <span className="min-w-0">
                  <span className="block truncate text-xs font-bold">
                    {option.profileName ||
                      option.profileId}
                  </span>

                  <span className="mt-0.5 block text-[10px] font-semibold text-white/35">
                    {String(
                      option.platform ||
                        ""
                    ).replace(
                      /_/g,
                      " "
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-3.5 py-3">
        <button
          type="button"
          disabled={
            selectionDisabled ||
            selectedIds.length === 0
          }
          onClick={
            submitSelection
          }
          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i
            className={`fa-solid ${
              loading
                ? "fa-spinner fa-spin"
                : submitted
                ? "fa-check"
                : "fa-arrow-right"
            } text-[10px]`}
          ></i>

          {loading
            ? "Continuing..."
            : submitted
            ? "Submitted"
            : interaction.submitLabel ||
              "Continue"}
        </button>

        <span className="text-[10px] font-medium text-white/30">
          {selectedIds.length} selected
        </span>

        {!enabled && (
          <span className="text-[10px] font-medium text-white/25">
            This selection has been superseded by a later response.
          </span>
        )}
      </div>
    </div>
  );
};

const PublishingRequirementsCard = ({
  interaction,
  message,
  enabled,
  loading,
  loadPinterestBoards,
  uploadPublishingMedia,
  onSubmit,
}) => {
  const options =
    Array.isArray(interaction?.options)
      ? interaction.options
      : [];

  const requirements =
    Array.isArray(
      interaction?.requirements
    )
      ? interaction.requirements
      : [];

  const initialSelectedIds =
    Array.isArray(
      interaction?.selectedProfileIds
    )
      ? interaction.selectedProfileIds
      : [];

  const [selectedIds, setSelectedIds] =
    useState(initialSelectedIds);

  const [boardsByProfile, setBoardsByProfile] =
    useState({});

  const [loadingBoards, setLoadingBoards] =
    useState({});

  const [fieldValues, setFieldValues] =
    useState({});

  const [submitted, setSubmitted] =
    useState(false);

  const [mediaItems, setMediaItems] =
    useState([]);

  const [mediaUploading, setMediaUploading] =
    useState(false);

  const [mediaUploadProgress, setMediaUploadProgress] =
    useState(0);

  const [mediaUploadError, setMediaUploadError] =
    useState(null);

  const mediaInputRef =
    React.useRef(null);

  const selectionDisabled =
    !enabled ||
    loading ||
    mediaUploading ||
    submitted;

  React.useEffect(() => {
    if (
      interaction?.type !==
      "publishing_requirements"
    ) {
      return;
    }

    const pinterestProfileIds = [
      ...new Set(
        requirements
          .filter(
            (requirement) =>
              requirement.control ===
              "pinterest_board"
          )
          .map(
            (requirement) =>
              requirement.profileId
          )
          .filter(Boolean)
      ),
    ];

    pinterestProfileIds.forEach(
      async (profileId) => {
        if (
          boardsByProfile[profileId] ||
          loadingBoards[profileId]
        ) {
          return;
        }

        setLoadingBoards((current) => ({
          ...current,
          [profileId]: true,
        }));

        const result =
          await loadPinterestBoards?.(
            profileId
          );

        setBoardsByProfile((current) => ({
          ...current,
          [profileId]:
            result?.error
              ? []
              : result?.data || [],
        }));

        setLoadingBoards((current) => ({
          ...current,
          [profileId]: false,
        }));
      }
    );
  }, [
    interaction,
    requirements,
    loadPinterestBoards,
    boardsByProfile,
    loadingBoards,
  ]);

  if (
    interaction?.type !==
    "publishing_requirements"
  ) {
    return null;
  }

  const toggleProfile = (optionId) => {
    if (selectionDisabled) {
      return;
    }

    setSelectedIds((current) => {
      return current.includes(optionId)
        ? current.filter(
            (item) =>
              item !== optionId
          )
        : [...current, optionId];
    });
  };

  const setFieldValue = (
    profileId,
    field,
    value
  ) => {
    setFieldValues((current) => ({
      ...current,
      [profileId]: {
        ...(current[profileId] || {}),
        [field]: value,
      },
    }));
  };

  const selectedProfiles =
    options.filter((option) =>
      selectedIds.includes(option.id)
    );

  const getMediaKind = (
    mediaItem = {}
  ) => {
    const explicitType = String(
      mediaItem.mediaType ||
        mediaItem.metadata
          ?.libraryAsset?.type ||
        ""
    )
      .trim()
      .toLowerCase();

    if (
      ["image", "video", "document"].includes(
        explicitType
      )
    ) {
      return explicitType;
    }

    const mimeType = String(
      mediaItem.type || ""
    ).toLowerCase();

    if (
      mimeType.startsWith(
        "image/"
      )
    ) {
      return "image";
    }

    if (
      mimeType.startsWith(
        "video/"
      )
    ) {
      return "video";
    }

    if (
      mimeType.includes("pdf") ||
      mimeType.includes(
        "document"
      ) ||
      mimeType.includes(
        "presentation"
      )
    ) {
      return "document";
    }

    return "other";
  };

  const uploadedImageCount =
    mediaItems.filter(
      (item) =>
        getMediaKind(item) ===
        "image"
    ).length;

  const uploadedVideoCount =
    mediaItems.filter(
      (item) =>
        getMediaKind(item) ===
        "video"
    ).length;

  const uploadedDocumentCount =
    mediaItems.filter(
      (item) =>
        getMediaKind(item) ===
        "document"
    ).length;

  const existingMedia =
    interaction.media || {};

  const mediaSummary = {
    total:
      Number(
        existingMedia.total || 0
      ) + mediaItems.length,
    images:
      Number(
        existingMedia.images || 0
      ) + uploadedImageCount,
    videos:
      Number(
        existingMedia.videos || 0
      ) + uploadedVideoCount,
    documents:
      Number(
        existingMedia.documents ||
          0
      ) + uploadedDocumentCount,
  };

  const isMediaRequirementResolved = (
    requirement
  ) => {
    switch (requirement.code) {
      case "INSTAGRAM_MEDIA_REQUIRED":
        return (
          mediaSummary.images +
            mediaSummary.videos >
          0
        );

      case "INSTAGRAM_CAROUSEL_LIMIT":
        return (
          mediaSummary.total <= 10
        );

      case "PINTEREST_SINGLE_MEDIA_REQUIRED":
        return (
          mediaSummary.total === 1 &&
          mediaSummary.images +
            mediaSummary.videos ===
            1
        );

      case "YOUTUBE_SINGLE_VIDEO_REQUIRED":
        return (
          mediaSummary.total === 1 &&
          mediaSummary.videos === 1
        );

      case "FACEBOOK_VIDEO_LIMIT":
        return (
          mediaSummary.videos <= 1
        );

      case "FACEBOOK_MIXED_MEDIA_UNSUPPORTED":
        return !(
          mediaSummary.images > 0 &&
          mediaSummary.videos > 0
        );

      case "LINKEDIN_CAROUSEL_LIMIT":
        return (
          mediaSummary.images <= 9
        );

      case "LINKEDIN_MIXED_MEDIA_UNSUPPORTED":
        return !(
          mediaSummary.images > 1 &&
          mediaSummary.videos > 0
        );

      case "TIKTOK_MEDIA_REQUIRED":
        return (
          mediaSummary.images > 0 ||
          mediaSummary.videos === 1
        );

      case "TIKTOK_VIDEO_LIMIT":
        return (
          mediaSummary.videos <= 1
        );

      case "TIKTOK_PHOTO_LIMIT":
        return (
          mediaSummary.images <= 35
        );

      case "TIKTOK_MIXED_MEDIA_UNSUPPORTED":
        return !(
          mediaSummary.images > 0 &&
          mediaSummary.videos > 0
        );

      case "TIKTOK_DOCUMENT_UNSUPPORTED":
        return (
          mediaSummary.documents === 0
        );

      case "MIXED_DOCUMENT_MEDIA_UNSUPPORTED":
        return !(
          mediaSummary.documents > 0 &&
          (
            mediaSummary.images > 0 ||
            mediaSummary.videos > 0
          )
        );

      default:
        return false;
    }
  };

  const handleMediaSelection = async (
    event
  ) => {
    const files = Array.from(
      event.target.files || []
    );

    event.target.value = "";

    if (
      selectionDisabled ||
      files.length === 0
    ) {
      return;
    }

    setMediaUploading(true);
    setMediaUploadProgress(0);
    setMediaUploadError(null);

    try {
      const uploadedItems =
        await uploadPublishingMedia?.({
          files,
          onProgress: ({
            progress,
          }) => {
            setMediaUploadProgress(
              progress
            );
          },
        });

      if (
        !Array.isArray(
          uploadedItems
        ) ||
        uploadedItems.length === 0
      ) {
        throw new Error(
          "No media was uploaded."
        );
      }

      setMediaItems((current) => {
        const existingKeys =
          new Set(
            current.map(
              (item) =>
                item.assetId ||
                item.url
            )
          );

        return [
          ...current,
          ...uploadedItems.filter(
            (item) => {
              const key =
                item.assetId ||
                item.url;

              return (
                key &&
                !existingKeys.has(key)
              );
            }
          ),
        ];
      });

      setMediaUploadProgress(100);
    } catch (error) {
      setMediaUploadError(
        error.message ||
          "Media upload failed."
      );
    } finally {
      setMediaUploading(false);
    }
  };

  const removeMediaItem = (
    itemId
  ) => {
    if (selectionDisabled) {
      return;
    }

    setMediaItems((current) =>
      current.filter((item) => {
        return (
          String(
            item.id ||
              item.assetId ||
              item.url
          ) !==
          String(itemId)
        );
      })
    );
  };

  const selectedProfileIdSet =
    new Set(
      selectedProfiles.map(
        (profile) =>
          profile.profileId
      )
    );

  const unresolvedRequirements =
    requirements.filter(
      (requirement) => {
        if (
          requirement.profileId &&
          !selectedProfileIdSet.has(
            requirement.profileId
          )
        ) {
          return false;
        }

        const values =
          fieldValues[
            requirement.profileId
          ] || {};

        if (
          requirement.control ===
          "pinterest_board"
        ) {
          return !values.board;
        }

        if (
          [
            "title",
            "document_title",
            "privacy",
          ].includes(
            requirement.control
          )
        ) {
          return !String(
            values[
              requirement.control
            ] || ""
          ).trim();
        }

        if (
          requirement.control ===
          "media"
        ) {
          return !isMediaRequirementResolved(
            requirement
          );
        }

        return requirement.blocking !==
          false;
      }
    );

  const submitRequirements = async () => {
    if (
      selectionDisabled ||
      selectedProfiles.length === 0 ||
      unresolvedRequirements.length > 0
    ) {
      return;
    }

    const platformOptions = {
      PINTEREST: {},
      YOUTUBE: {},
      linkedinOptions: {},
      tiktokOptions: {},
    };

    selectedProfiles.forEach(
      (profile) => {
        const values =
          fieldValues[
            profile.profileId
          ] || {};

        if (
          profile.platform ===
          "PINTEREST"
        ) {
          platformOptions.PINTEREST[
            profile.profileId
          ] = {
            board:
              values.board || null,
            title:
              values.title || null,
          };
        }

        if (
          profile.platform ===
          "YOUTUBE"
        ) {
          platformOptions.YOUTUBE[
            profile.profileId
          ] = {
            title:
              values.title || null,
          };
        }

        if (
          [
            "LINKEDIN",
            "LINKEDIN_COMPANY",
          ].includes(
            profile.platform
          ) &&
          values.document_title
        ) {
          platformOptions.linkedinOptions.documentTitle =
            values.document_title;
        }

        if (
          profile.platform ===
            "TIKTOK" &&
          values.privacy
        ) {
          platformOptions.tiktokOptions.privacy_level =
            values.privacy;
        }
      }
    );

    if (
      Object.keys(
        platformOptions.PINTEREST
      ).length === 0
    ) {
      delete platformOptions.PINTEREST;
    }

    if (
      Object.keys(
        platformOptions.YOUTUBE
      ).length === 0
    ) {
      delete platformOptions.YOUTUBE;
    }

    if (
      Object.keys(
        platformOptions.linkedinOptions
      ).length === 0
    ) {
      delete platformOptions.linkedinOptions;
    }

    if (
      Object.keys(
        platformOptions.tiktokOptions
      ).length === 0
    ) {
      delete platformOptions.tiktokOptions;
    }

    setSubmitted(true);

    try {
      await onSubmit?.({
        message,
        profiles:
          selectedProfiles,
        platformOptions,
        attachments:
          mediaItems,
      });
    } catch (_) {
      setSubmitted(false);
    }
  };

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-amber-400/20 bg-amber-400/[0.05]">
      <div className="px-3.5 py-3">
        <h4 className="mb-1 text-sm font-bold text-white/90">
          {interaction.title ||
            "Complete publishing requirements"}
        </h4>

        <p className="mb-0 text-xs leading-relaxed text-white/50">
          {interaction.description}
        </p>
      </div>

      <div className="space-y-2 border-t border-white/[0.06] px-3.5 py-3">
        {options.map((option) => {
          const selected =
            selectedIds.includes(
              option.id
            );

          return (
            <button
              key={option.id}
              type="button"
              disabled={
                selectionDisabled
              }
              onClick={() =>
                toggleProfile(option.id)
              }
              className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left ${
                selected
                  ? "border-violet-300/35 bg-violet-400/15"
                  : "border-white/[0.07] bg-white/[0.025] opacity-55"
              }`}
            >
              <span>
                <span className="block text-xs font-bold text-white/80">
                  {option.profileName}
                </span>
                <span className="text-[10px] text-white/35">
                  {String(
                    option.platform
                  ).replace(/_/g, " ")}
                </span>
              </span>

              <i
                className={`fa-solid ${
                  selected
                    ? "fa-check-circle text-violet-200"
                    : "fa-circle text-white/20"
                }`}
              ></i>
            </button>
          );
        })}
      </div>

      {requirements.some(
        (requirement) => {
          return (
            requirement.control ===
              "media" &&
            (
              !requirement.profileId ||
              selectedProfileIdSet.has(
                requirement.profileId
              )
            )
          );
        }
      ) && (
        <div className="border-t border-white/[0.06] px-3.5 py-3">
          <input
            ref={mediaInputRef}
            type="file"
            accept="image/*,video/*,.pdf,.docx,.pptx"
            multiple
            className="hidden"
            onChange={
              handleMediaSelection
            }
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={
                selectionDisabled
              }
              onClick={() =>
                mediaInputRef.current?.click()
              }
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i
                className={`fa-solid ${
                  mediaUploading
                    ? "fa-spinner fa-spin"
                    : "fa-paperclip"
                } text-[10px]`}
              ></i>

              {mediaUploading
                ? `Uploading ${mediaUploadProgress}%`
                : "Attach media"}
            </button>

            <span className="text-[10px] font-medium text-white/35">
              {mediaSummary.images} image
              {mediaSummary.images === 1
                ? ""
                : "s"}
              {" · "}
              {mediaSummary.videos} video
              {mediaSummary.videos === 1
                ? ""
                : "s"}
              {" · "}
              {mediaSummary.documents} document
              {mediaSummary.documents === 1
                ? ""
                : "s"}
            </span>
          </div>

          {mediaUploadError && (
            <p className="mb-0 mt-2 text-xs font-medium text-red-200/80">
              {mediaUploadError}
            </p>
          )}

          {mediaItems.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {mediaItems.map(
                (item) => {
                  const itemId =
                    item.id ||
                    item.assetId ||
                    item.url;

                  return (
                    <div
                      key={itemId}
                      className="flex max-w-[220px] items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-2.5 py-2"
                    >
                      <i
                        className={`fa-solid ${
                          getMediaKind(
                            item
                          ) === "video"
                            ? "fa-video"
                            : getMediaKind(
                                item
                              ) ===
                              "document"
                            ? "fa-file"
                            : "fa-image"
                        } text-[10px] text-white/45`}
                      ></i>

                      <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-white/60">
                        {item.name ||
                          "Uploaded media"}
                      </span>

                      <button
                        type="button"
                        disabled={
                          selectionDisabled
                        }
                        onClick={() =>
                          removeMediaItem(
                            itemId
                          )
                        }
                        className="text-white/35 transition hover:text-red-300 disabled:cursor-not-allowed"
                      >
                        <i className="fa-solid fa-xmark text-[10px]"></i>
                      </button>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3 border-t border-white/[0.06] px-3.5 py-3">
        {requirements.map(
          (requirement, index) => {
            if (
              requirement.profileId &&
              !selectedProfileIdSet.has(
                requirement.profileId
              )
            ) {
              return null;
            }

            const values =
              fieldValues[
                requirement.profileId
              ] || {};

            return (
              <div
                key={`${requirement.code}-${requirement.profileId}-${index}`}
                className="rounded-lg bg-black/[0.12] p-3"
              >
                <p className="mb-2 text-xs font-semibold text-amber-100/75">
                  {requirement.message}
                </p>

                {requirement.control ===
                  "pinterest_board" && (
                  <select
                    value={
                      values.board?.id ||
                      ""
                    }
                    disabled={
                      selectionDisabled ||
                      loadingBoards[
                        requirement
                          .profileId
                      ]
                    }
                    onChange={(event) => {
                      const boards =
                        boardsByProfile[
                          requirement
                            .profileId
                        ] || [];

                      const board =
                        boards.find(
                          (item) =>
                            String(
                              item.id
                            ) ===
                            event.target
                              .value
                        ) || null;

                      setFieldValue(
                        requirement.profileId,
                        "board",
                        board
                      );
                    }}
                    className="h-9 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none"
                  >
                    <option value="">
                      {loadingBoards[
                        requirement
                          .profileId
                      ]
                        ? "Loading boards..."
                        : "Select a board"}
                    </option>

                    {(
                      boardsByProfile[
                        requirement
                          .profileId
                      ] || []
                    ).map((board) => (
                      <option
                        key={board.id}
                        value={board.id}
                      >
                        {board.name}
                      </option>
                    ))}
                  </select>
                )}

                {[
                  "title",
                  "document_title",
                ].includes(
                  requirement.control
                ) && (
                  <input
                    type="text"
                    value={
                      values[
                        requirement.control
                      ] || ""
                    }
                    disabled={
                      selectionDisabled
                    }
                    onChange={(event) =>
                      setFieldValue(
                        requirement.profileId,
                        requirement.control,
                        event.target.value
                      )
                    }
                    placeholder={
                      requirement.control ===
                      "document_title"
                        ? "Document title"
                        : "Post title"
                    }
                    className="h-9 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25"
                  />
                )}

                {requirement.control ===
                  "privacy" && (
                  <input
                    type="text"
                    value={
                      values.privacy ||
                      ""
                    }
                    disabled={
                      selectionDisabled
                    }
                    onChange={(event) =>
                      setFieldValue(
                        requirement.profileId,
                        "privacy",
                        event.target.value
                      )
                    }
                    placeholder="TikTok privacy level"
                    className="h-9 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25"
                  />
                )}

                {requirement.control ===
                  "media" && (
                  <p
                    className={`mb-0 text-[10px] ${
                      isMediaRequirementResolved(
                        requirement
                      )
                        ? "text-emerald-200/70"
                        : "text-white/35"
                    }`}
                  >
                    {isMediaRequirementResolved(
                      requirement
                    )
                      ? "Resolved by the selected media."
                      : "Attach compatible media above or remove this destination."}
                  </p>
                )}

                {requirement.control ===
                  "content_conflict" && (
                  <p className="mb-0 text-[10px] text-white/35">
                    Change the content or remove this destination.
                  </p>
                )}
              </div>
            );
          }
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-3.5 py-3">
        <button
          type="button"
          disabled={
            selectionDisabled ||
            mediaUploading ||
            selectedProfiles.length === 0 ||
            unresolvedRequirements.length >
              0
          }
          onClick={
            submitRequirements
          }
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i
            className={`fa-solid ${
              loading
                ? "fa-spinner fa-spin"
                : submitted
                ? "fa-check"
                : "fa-arrow-right"
            } text-[10px]`}
          ></i>

          {loading
            ? "Continuing..."
            : submitted
            ? "Submitted"
            : interaction.submitLabel ||
              "Continue"}
        </button>

        {unresolvedRequirements.length >
          0 && (
          <span className="text-[10px] font-medium text-amber-100/55">
            {
              unresolvedRequirements.length
            } requirement
            {unresolvedRequirements.length ===
            1
              ? ""
              : "s"}{" "}
            unresolved
          </span>
        )}
      </div>
    </div>
  );
};

const setValueAtPath = (
  source,
  path,
  value
) => {
  const keys = String(path || "")
    .split(".")
    .filter(Boolean);

  if (keys.length === 0) {
    return source;
  }

  const result = {
    ...(source || {}),
  };

  let current = result;

  keys.forEach((key, index) => {
    if (
      index ===
      keys.length - 1
    ) {
      current[key] = value;
      return;
    }

    current[key] = {
      ...(current[key] || {}),
    };

    current = current[key];
  });

  return result;
};

const getFieldInitialValue = (
  field,
  fields
) => {
  if (
    field.control ===
      "timezone_select" &&
    field.value ===
      "__DEVICE_TIMEZONE__"
  ) {
    return (
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone ||
      "UTC"
    );
  }

    if (
    field.control ===
    "timezone_datetime_list"
  ) {
    if (
      Array.isArray(
        field.localValues
      ) &&
      field.localValues.length >
        0
    ) {
      return [
        ...field.localValues,
      ];
    }

    const timezoneField =
      fields.find(
        (candidate) =>
          candidate.control ===
          "timezone_select"
      );

    const timezone =
      timezoneField?.value &&
      timezoneField.value !==
        "__DEVICE_TIMEZONE__"
        ? timezoneField.value
        : null;

    return Array.isArray(
      field.value
    )
      ? field.value.map(
          (dateValue) => {
            const parsed =
              moment(dateValue);

            if (!parsed.isValid()) {
              return "";
            }

            return timezone
              ? parsed
                  .tz(timezone)
                  .format(
                    "YYYY-MM-DDTHH:mm"
                  )
              : parsed
                  .utc()
                  .format(
                    "YYYY-MM-DDTHH:mm"
                  );
          }
        )
      : [];
  }

  if (
    [
  "connected_profile_multi_select",
  "delete_post_selection",
  "asset_selection",
  "account_selection",
].includes(
  field.control
)
  ) {
    return Array.isArray(
      field.value
    )
      ? field.value
      : [];
  }

  if (
    field.control ===
    "media_library_upload"
  ) {
    return Array.isArray(
      field.value
    )
      ? field.value.map(
          (item) => ({
            ...item,
            url:
              getActionMediaUrl(
                item
              ),
            type:
              getActionMediaType(
                item
              ),
            mediaType:
              getActionMediaType(
                item
              ),
            thumbnailUrl:
              getActionMediaPreviewUrl(
                item
              ),
          })
        )
      : [];
  }

  return field.value ?? "";
};

const getActionMediaUrl = (
  item = {}
) => {
  return (
    item.url ||
    item.permanentUrl ||
    item.localUrl ||
    item.imageUrl ||
    item.videoUrl ||
    item.assetUrl ||
    item.metadata
      ?.libraryAsset?.url ||
    null
  );
};

const getActionMediaPreviewUrl = (
  item = {}
) => {
  return (
    item.thumbnailUrl ||
    item.metadata
      ?.thumbnail?.url ||
    getActionMediaUrl(item)
  );
};

const getActionMediaType = (
  item = {}
) => {
  const explicitType =
    String(
      item.mediaType ||
      item.type ||
      item.metadata
        ?.libraryAsset?.type ||
      ""
    )
      .trim()
      .toLowerCase();

  const mimeType =
    String(
      item.mimeType ||
      item.contentType ||
      ""
    )
      .trim()
      .toLowerCase();

  const url =
    String(
      getActionMediaUrl(item) ||
      ""
    ).toLowerCase();

  if (
    explicitType === "image" ||
    explicitType.startsWith(
      "image/"
    ) ||
    mimeType.startsWith(
      "image/"
    ) ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(
      url
    )
  ) {
    return "image";
  }

  if (
    explicitType === "video" ||
    explicitType.startsWith(
      "video/"
    ) ||
    mimeType.startsWith(
      "video/"
    ) ||
    /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i.test(
      url
    )
  ) {
    return "video";
  }

  if (
    explicitType === "document" ||
    mimeType.includes("pdf") ||
    mimeType.includes(
      "document"
    ) ||
    mimeType.includes(
      "presentation"
    )
  ) {
    return "document";
  }

  return "other";
};

const normalizeUploadedMediaItem = (
  item = {}
) => {
  const mimeType =
    item.type ||
    item.mimeType ||
    null;

  let mediaType =
    item.mediaType ||
    item.metadata
      ?.libraryAsset?.type ||
    null;

  if (!mediaType) {
    const normalizedMime =
      String(
        mimeType || ""
      ).toLowerCase();

    if (
      normalizedMime.startsWith(
        "image/"
      )
    ) {
      mediaType = "image";
    } else if (
      normalizedMime.startsWith(
        "video/"
      )
    ) {
      mediaType = "video";
    } else if (
      normalizedMime.includes(
        "pdf"
      ) ||
      normalizedMime.includes(
        "document"
      ) ||
      normalizedMime.includes(
        "presentation"
      )
    ) {
      mediaType = "document";
    } else {
      mediaType = "other";
    }
  }

  return {
    assetId:
      item.assetId ||
      item.libraryAssetId ||
      item.metadata
        ?.libraryAssetId ||
      null,
    url:
      item.url || null,
    name:
      item.name ||
      item.metadata
        ?.originalName ||
      null,
    type:
      mediaType,
    mimeType,
    thumbnailUrl:
      item.thumbnailUrl ||
      item.metadata
        ?.thumbnail?.url ||
      null,
    altText:
      item.altText || null,
    title:
      item.title || null,
    metadata:
      item.metadata || {},
  };
};

const PLATFORM_DISPLAY = {
  FACEBOOK: {
    label: "Facebook",
    icon:
      "fa-brands fa-facebook",
    iconColor:
      "text-blue-400",
  },
  INSTAGRAM: {
    label: "Instagram",
    icon:
      "fa-brands fa-instagram",
    iconColor:
      "text-pink-400",
  },
  PINTEREST: {
    label: "Pinterest",
    icon:
      "fa-brands fa-pinterest",
    iconColor:
      "text-red-400",
  },
  LINKEDIN: {
    label: "LinkedIn",
    icon:
      "fa-brands fa-linkedin",
    iconColor:
      "text-blue-400",
  },
  LINKEDIN_COMPANY: {
    label:
      "LinkedIn Company",
    icon:
      "fa-brands fa-linkedin",
    iconColor:
      "text-blue-400",
  },
  YOUTUBE: {
    label: "YouTube",
    icon:
      "fa-brands fa-youtube",
    iconColor:
      "text-red-400",
  },
  TIKTOK: {
    label: "TikTok",
    icon:
      "fa-brands fa-tiktok",
    iconColor:
      "text-white/75",
  },
  GMB: {
    label:
      "Google Business",
    icon:
      "fa-brands fa-google",
    iconColor:
      "text-blue-300",
  },
  TWITTER: {
    label: "X",
    icon:
      "fa-brands fa-x-twitter",
    iconColor:
      "text-white/75",
  },
};

const TagsEditor = ({
  value = [],
  disabled = false,
  onChange,
}) => {
  const [inputValue, setInputValue] =
    useState("");

  const tags =
    Array.isArray(value)
      ? value
      : [];

  const addTags = (
    rawValue
  ) => {
    const incomingTags =
      String(rawValue || "")
        .split(",")
        .map((tag) =>
          tag.trim()
        )
        .filter(Boolean);

    if (
      incomingTags.length === 0
    ) {
      return;
    }

    const existingKeys =
      new Set(
        tags.map((tag) =>
          String(tag)
            .trim()
            .toLowerCase()
        )
      );

    const nextTags = [
      ...tags,
    ];

    incomingTags.forEach(
      (tag) => {
        const key =
          tag.toLowerCase();

        if (
          existingKeys.has(key)
        ) {
          return;
        }

        existingKeys.add(key);
        nextTags.push(tag);
      }
    );

    onChange?.(
      nextTags.slice(0, 100)
    );

    setInputValue("");
  };

  const removeTag = (
    tagToRemove
  ) => {
    onChange?.(
      tags.filter(
        (tag) =>
          tag !== tagToRemove
      )
    );
  };

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[#202228] p-2">
      {tags.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 rounded-full bg-violet-400/15 px-2.5 py-1 text-[10px] font-semibold text-violet-100"
            >
              {tag}

              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  removeTag(tag)
                }
                className="text-violet-100/45 transition hover:text-white"
              >
                <i className="fa-solid fa-xmark text-[9px]"></i>
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={inputValue}
        disabled={disabled}
        onChange={(event) => {
          const nextValue =
            event.target.value;

          if (
            nextValue.includes(
              ","
            )
          ) {
            addTags(
              nextValue
            );
            return;
          }

          setInputValue(
            nextValue
          );
        }}
        onKeyDown={(event) => {
          if (
            event.key ===
              "Enter" ||
            event.key === ","
          ) {
            event.preventDefault();
            addTags(
              inputValue
            );
          }

          if (
            event.key ===
              "Backspace" &&
            !inputValue &&
            tags.length > 0
          ) {
            removeTag(
              tags[
                tags.length - 1
              ]
            );
          }
        }}
        onBlur={() => {
          if (
            inputValue.trim()
          ) {
            addTags(
              inputValue
            );
          }
        }}
        placeholder="Type a tag and press Enter"
        className="h-7 w-full border-0 bg-transparent px-1 text-xs text-white outline-none placeholder:text-white/25"
      />
    </div>
  );
};

const BrandKitFieldSelection = ({
field,
value = [],
disabled = false,
onChange,
}) => {
const selectedFields =
Array.isArray(value)
? value
: [];

const categories =
Array.isArray(
field?.categories
)
? field.categories
: [];

const [
expandedCategories,
setExpandedCategories,
] = useState(() => {
return categories.reduce(
(result, category) => {
result[category.key] =
selectedFields.some(
(fieldKey) =>
(
category.fields || []
).some(
(candidate) =>
candidate.key ===
fieldKey
)
);
return result;
},
{}
);
});

useEffect(() => {
setExpandedCategories(
categories.reduce(
(result, category) => {
result[category.key] =
selectedFields.some(
(fieldKey) =>
(
category.fields || []
).some(
(candidate) =>
candidate.key ===
fieldKey
)
);
return result;
},
{}
)
);
}, [
field?.key,
categories,
selectedFields,
]);

const toggleCategory = (
categoryKey
) => {
setExpandedCategories(
(current) => ({
...current,
[categoryKey]:
!current[categoryKey],
})
);
};

const toggleField = (
fieldKey
) => {
if (disabled) {
return;
}

if (
selectedFields.includes(
fieldKey
)
) {
onChange?.(
selectedFields.filter(
(item) =>
item !== fieldKey
)
);
return;
}

onChange?.([
...selectedFields,
fieldKey,
]);
};

return (
<div className="space-y-2">
{categories.map(
(category) => {
const categoryFields =
Array.isArray(
category.fields
)
? category.fields
: [];

const selectedCount =
categoryFields.filter(
(candidate) =>
selectedFields.includes(
candidate.key
)
).length;

const expanded =
expandedCategories[
category.key
] === true;

return (
<div
key={category.key}
className="overflow-hidden rounded-xl border border-white/[0.08] bg-black/[0.12]"
>
<button
type="button"
disabled={disabled}
onClick={() =>
toggleCategory(
category.key
)
}
className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition hover:bg-white/[0.04] disabled:cursor-not-allowed"
>
<span className="min-w-0">
<span className="block text-xs font-bold text-white/80">
{category.label}
</span>
{category.description && (
<span className="mt-0.5 block text-[10px] leading-relaxed text-white/35">
{category.description}
</span>
)}
</span>

<span className="flex flex-shrink-0 items-center gap-2">
{selectedCount > 0 && (
<span className="rounded-full bg-violet-400/15 px-2 py-1 text-[9px] font-bold text-violet-100">
{selectedCount} selected
</span>
)}
<i
className={`fa-solid fa-chevron-down text-[9px] text-white/35 transition-transform ${
expanded
? "rotate-180"
: ""
}`}
></i>
</span>
</button>

{expanded && (
<div className="grid grid-cols-1 gap-2 border-t border-white/[0.06] p-2 sm:grid-cols-2">
{categoryFields.map(
(candidate) => {
const selected =
selectedFields.includes(
candidate.key
);

return (
<button
key={candidate.key}
type="button"
disabled={disabled}
onClick={() =>
toggleField(
candidate.key
)
}
className={`flex min-w-0 items-start gap-2.5 rounded-lg border p-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
selected
? "border-violet-300/35 bg-violet-400/15"
: "border-white/[0.06] bg-white/[0.025] hover:border-white/[0.12] hover:bg-white/[0.05]"
}`}
>
<span
className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
selected
? "border-violet-200/60 bg-violet-500 text-white"
: "border-white/15 bg-white/[0.03] text-transparent"
}`}
>
<i className="fa-solid fa-check text-[8px]"></i>
</span>

<span className="min-w-0">
<span className="block text-[11px] font-bold text-white/75">
{candidate.label}
</span>
{candidate.description && (
<span className="mt-0.5 block text-[9px] leading-relaxed text-white/30">
{candidate.description}
</span>
)}
</span>
</button>
);
}
)}
</div>
)}
</div>
);
}
)}

<div className="flex items-center justify-between gap-3 rounded-lg bg-violet-400/[0.07] px-3 py-2">
<span className="text-[10px] font-semibold text-violet-100/65">
Only selected values will be shown and updated.
</span>
<span className="flex-shrink-0 text-[10px] font-bold text-violet-100">
{selectedFields.length} selected
</span>
</div>
</div>
);
};

const normalizeCopilotHexColor = (
value
) => {
const normalized =
String(value || "")
.trim()
.toUpperCase();

if (
!/^#(?:[0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/.test(
normalized
)
) {
return null;
}

return normalized;
};

const ColorInputEditor = ({
value = "",
disabled = false,
onChange,
}) => {
const normalized =
normalizeCopilotHexColor(
value
);

const pickerValue =
normalized &&
normalized.length === 7
? normalized
: "#000000";

return (
<div className="grid grid-cols-[46px_minmax(0,1fr)] gap-2">
<input
type="color"
value={pickerValue}
disabled={disabled}
onChange={(event) =>
onChange?.(
event.target.value.toUpperCase()
)
}
className="h-10 w-full cursor-pointer rounded-lg border border-white/[0.12] bg-[#202228] p-1 disabled:cursor-not-allowed"
aria-label="Select color"
/>

<input
type="text"
value={value || ""}
disabled={disabled}
onChange={(event) =>
onChange?.(
event.target.value
)
}
placeholder="#0B132B"
className={`h-10 min-w-0 rounded-lg border bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25 ${
value &&
!normalized
? "border-red-400/40"
: "border-white/[0.08]"
}`}
/>
</div>
);
};

const ColorListEditor = ({
value = [],
disabled = false,
onChange,
}) => {
const colors =
Array.isArray(value)
? value
: [];

const [
colorInput,
setColorInput,
] = useState("#000000");

const addColor = () => {
const normalized =
normalizeCopilotHexColor(
colorInput
);

if (!normalized) {
return;
}

const existing =
new Set(
colors.map((color) =>
String(color)
.toUpperCase()
)
);

if (
existing.has(normalized)
) {
return;
}

onChange?.([
...colors,
normalized,
].slice(0, 50));
};

const removeColor = (
colorToRemove
) => {
onChange?.(
colors.filter(
(color) =>
color !== colorToRemove
)
);
};

return (
<div className="space-y-3">
{colors.length > 0 && (
<div className="flex flex-wrap gap-2">
{colors.map(
(color, index) => {
const normalized =
normalizeCopilotHexColor(
color
);

return (
<div
key={`${color}-${index}`}
className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] p-1.5 pr-2"
>
<span
className="h-7 w-7 rounded-md border border-white/15"
style={{
backgroundColor:
normalized ||
"#000000",
}}
></span>

<span className="text-[10px] font-bold text-white/65">
{color}
</span>

<button
type="button"
disabled={disabled}
onClick={() =>
removeColor(color)
}
className="text-white/35 transition hover:text-red-300 disabled:opacity-40"
aria-label={`Remove ${color}`}
>
<i className="fa-solid fa-xmark text-[9px]"></i>
</button>
</div>
);
}
)}
</div>
)}

<div className="grid grid-cols-[46px_minmax(0,1fr)_64px] gap-2">
<input
type="color"
value={
normalizeCopilotHexColor(
colorInput
) || "#000000"
}
disabled={disabled}
onChange={(event) =>
setColorInput(
event.target.value.toUpperCase()
)
}
className="h-10 w-full cursor-pointer rounded-lg border border-white/[0.12] bg-[#202228] p-1 disabled:cursor-not-allowed"
aria-label="Select palette color"
/>

<input
type="text"
value={colorInput}
disabled={disabled}
onChange={(event) =>
setColorInput(
event.target.value
)
}
onKeyDown={(event) => {
if (
event.key === "Enter"
) {
event.preventDefault();
addColor();
}
}}
placeholder="#0B132B"
className="h-10 min-w-0 rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25"
/>

<button
type="button"
disabled={
disabled ||
!normalizeCopilotHexColor(
colorInput
)
}
onClick={addColor}
className="h-10 rounded-lg bg-white px-3 text-xs font-bold text-black disabled:cursor-not-allowed disabled:opacity-40"
>
Add
</button>
</div>
</div>
);
};

const ContentTopicsEditor = ({
value = [],
disabled = false,
onChange,
}) => {
const topics =
Array.isArray(value)
? value
: [];

const addTopic = () => {
onChange?.([
...topics,
{
text: "",
enabled: true,
},
]);
};

const updateTopic = (
index,
patch
) => {
onChange?.(
topics.map(
(topic, topicIndex) =>
topicIndex === index
? {
...topic,
...patch,
}
: topic
)
);
};

const removeTopic = (
index
) => {
onChange?.(
topics.filter(
(
_,
topicIndex
) =>
topicIndex !== index
)
);
};

return (
<div className="space-y-2">
{topics.map(
(topic, index) => (
<div
key={`topic-${index}`}
className="grid grid-cols-[34px_minmax(0,1fr)_32px] items-center gap-2 rounded-lg border border-white/[0.08] bg-[#202228] p-2"
>
<button
type="button"
disabled={disabled}
onClick={() =>
updateTopic(
index,
{
enabled:
topic?.enabled === false,
}
)
}
className={`flex h-7 w-7 items-center justify-center rounded-full transition ${
topic?.enabled === false
? "bg-white/[0.07] text-white/30"
: "bg-emerald-400/20 text-emerald-100"
}`}
title={
topic?.enabled === false
? "Enable topic"
: "Disable topic"
}
>
<i
className={`fa-solid ${
topic?.enabled === false
? "fa-toggle-off"
: "fa-toggle-on"
} text-sm`}
></i>
</button>

<input
type="text"
value={
topic?.text || ""
}
disabled={disabled}
onChange={(event) =>
updateTopic(
index,
{
text:
event.target.value,
}
)
}
placeholder="Content topic"
className="h-8 min-w-0 border-0 bg-transparent px-1 text-xs text-white outline-none placeholder:text-white/25"
/>

<button
type="button"
disabled={disabled}
onClick={() =>
removeTopic(index)
}
className="flex h-7 w-7 items-center justify-center rounded-lg text-white/35 transition hover:bg-red-400/10 hover:text-red-200 disabled:opacity-40"
aria-label="Remove content topic"
>
<i className="fa-solid fa-xmark text-[10px]"></i>
</button>
</div>
)
)}

<button
type="button"
disabled={disabled}
onClick={addTopic}
className="inline-flex h-9 items-center gap-2 rounded-lg border border-dashed border-white/[0.14] bg-white/[0.025] px-3 text-[10px] font-bold text-white/55 transition hover:border-violet-300/30 hover:bg-violet-400/10 hover:text-violet-100 disabled:opacity-40"
>
<i className="fa-solid fa-plus text-[9px]"></i>
Add content topic
</button>
</div>
);
};

const StructuredStylesEditor = ({
value = {},
disabled = false,
onChange,
}) => {
const styles =
value &&
typeof value ===
"object" &&
!Array.isArray(value)
? value
: {};

const nextRowIdRef =
useRef(1);

const createRowsFromStyles = (
sourceStyles
) => {
return Object.entries(
sourceStyles || {}
).map(
([
name,
description,
]) => ({
id:
`visual-style-row-${nextRowIdRef.current++}`,
name:
String(name || "")
.replace(/_/g, " "),
description:
String(
description || ""
),
})
);
};

const [
rows,
setRows,
] = useState(() =>
createRowsFromStyles(styles)
);

const lastEmittedSignatureRef =
useRef(
JSON.stringify(styles)
);

useEffect(() => {
const incomingSignature =
JSON.stringify(styles);

if (
incomingSignature ===
lastEmittedSignatureRef.current
) {
return;
}

lastEmittedSignatureRef.current =
incomingSignature;

setRows(
createRowsFromStyles(
styles
)
);
}, [
styles,
]);

const buildStylesObject = (
nextRows
) => {
return nextRows.reduce(
(
result,
row
) => {
const normalizedName =
String(row.name || "")
.trim()
.replace(/\s+/g, "_")
.replace(
/[^a-zA-Z0-9_-]/g,
""
);

if (!normalizedName) {
return result;
}

result[normalizedName] =
String(
row.description || ""
);

return result;
},
{}
);
};

const commitRows = (
nextRows
) => {
setRows(nextRows);

const nextStyles =
buildStylesObject(
nextRows
);

lastEmittedSignatureRef.current =
JSON.stringify(
nextStyles
);

onChange?.(
nextStyles
);
};

const addStyle = () => {
const existingNames =
new Set(
rows.map((row) =>
String(row.name || "")
.trim()
.toLowerCase()
)
);

let index = 1;
let name =
`New style ${index}`;

while (
existingNames.has(
name.toLowerCase()
)
) {
index += 1;
name =
`New style ${index}`;
}

commitRows([
...rows,
{
id:
`visual-style-row-${nextRowIdRef.current++}`,
name,
description: "",
},
]);
};

const updateStyle = (
rowId,
patch
) => {
commitRows(
rows.map((row) =>
row.id === rowId
? {
...row,
...patch,
}
: row
)
);
};

const removeStyle = (
rowId
) => {
commitRows(
rows.filter(
(row) =>
row.id !== rowId
)
);
};

return (
<div className="space-y-3">
{rows.map(
(row) => (
<div
key={row.id}
className="space-y-2 rounded-xl border border-white/[0.08] bg-[#202228] p-3"
>
<div className="flex items-center gap-2">
<input
type="text"
value={row.name}
disabled={disabled}
onChange={(event) =>
updateStyle(
row.id,
{
name:
event.target.value,
}
)
}
placeholder="Style name"
className="h-9 min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-black/20 px-3 text-xs font-bold text-white outline-none placeholder:text-white/25"
/>

<button
type="button"
disabled={disabled}
onClick={() =>
removeStyle(
row.id
)
}
className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white/35 transition hover:bg-red-400/10 hover:text-red-200 disabled:opacity-40"
aria-label="Remove visual style"
>
<i className="fa-solid fa-trash text-[9px]"></i>
</button>
</div>

<textarea
value={
row.description
}
disabled={disabled}
onChange={(event) =>
updateStyle(
row.id,
{
description:
event.target.value,
}
)
}
rows={4}
placeholder="Describe the visual style"
className="w-full resize-y rounded-lg border border-white/[0.08] bg-black/20 px-3 py-2 text-xs leading-relaxed text-white outline-none placeholder:text-white/25"
/>
</div>
)
)}

<button
type="button"
disabled={disabled}
onClick={addStyle}
className="inline-flex h-9 items-center gap-2 rounded-lg border border-dashed border-white/[0.14] bg-white/[0.025] px-3 text-[10px] font-bold text-white/55 transition hover:border-violet-300/30 hover:bg-violet-400/10 hover:text-violet-100 disabled:opacity-40"
>
<i className="fa-solid fa-plus text-[9px]"></i>
Add visual style
</button>
</div>
);
};

const SearchableSelectEditor = ({
field,
value = "",
options = [],
loading = false,
disabled = false,
onChange,
}) => {
const [
query,
setQuery,
] = useState(
String(value || "")
);
const [
isOpen,
setIsOpen,
] = useState(false);

useEffect(() => {
setQuery(
String(value || "")
);
}, [
value,
field?.key,
]);

const valueField =
field?.dataSource
?.valueField ||
"id";

const labelField =
field?.dataSource
?.labelField ||
"name";

const normalizedOptions =
useMemo(() => {
const sourceOptions =
Array.isArray(options)
? options
: [];

return sourceOptions
.map((option) => {
if (
typeof option ===
"string"
) {
return {
value: option,
label: option,
};
}

if (
!option ||
typeof option !==
"object"
) {
return null;
}

const optionValue =
option[valueField] ??
option.value ??
option.id ??
option[labelField] ??
option.label ??
option.name ??
null;

const optionLabel =
option[labelField] ??
option.label ??
option.name ??
optionValue;

if (
optionValue === null ||
optionValue === undefined
) {
return null;
}

return {
value:
String(optionValue),
label:
String(
optionLabel ||
optionValue
),
};
})
.filter(Boolean);
}, [
options,
valueField,
labelField,
]);

const normalizedQuery =
String(query || "")
.trim()
.toLowerCase();

const visibleOptions =
useMemo(() => {
if (!normalizedQuery) {
return normalizedOptions
.slice(0, 100);
}

const exactValue =
String(value || "")
.trim()
.toLowerCase();

const filterQuery =
normalizedQuery ===
exactValue
? ""
: normalizedQuery;

if (!filterQuery) {
return normalizedOptions
.slice(0, 100);
}

return normalizedOptions
.filter((option) => {
return (
option.label
.toLowerCase()
.includes(filterQuery) ||
option.value
.toLowerCase()
.includes(filterQuery)
);
})
.slice(0, 100);
}, [
normalizedOptions,
normalizedQuery,
value,
]);

const selectOption = (
option
) => {
if (disabled) {
return;
}

onChange?.(
option.value
);
setQuery(
option.label
);
setIsOpen(false);
};

const clearSelection = () => {
if (disabled) {
return;
}

onChange?.("");
setQuery("");
setIsOpen(true);
};

const showFontPreview =
field?.dataSource?.type ===
"google_fonts";

return (
<div className="space-y-2">
<div className="relative">
<input
type="text"
value={query}
disabled={disabled}
onFocus={() =>
setIsOpen(true)
}
onChange={(event) => {
setQuery(
event.target.value
);
setIsOpen(true);
}}
onKeyDown={(event) => {
if (
event.key === "Enter" &&
visibleOptions.length >
0
) {
event.preventDefault();
selectOption(
visibleOptions[0]
);
}

if (
event.key === "Escape"
) {
setIsOpen(false);
setQuery(
String(value || "")
);
}
}}
placeholder={
loading
? `Loading ${String(
field?.label ||
"options"
).toLowerCase()}...`
: `Search ${String(
field?.label ||
"options"
).toLowerCase()}`
}
className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 pr-9 text-xs text-white outline-none placeholder:text-white/25 disabled:cursor-not-allowed disabled:opacity-45"
/>

{query && !disabled && (
<button
type="button"
onMouseDown={(event) =>
event.preventDefault()
}
onClick={
clearSelection
}
className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-white/35 transition hover:bg-white/[0.07] hover:text-white"
aria-label={`Clear ${field?.label || "selection"}`}
>
<i className="fa-solid fa-xmark text-[9px]"></i>
</button>
)}
</div>

{isOpen && (
<div className="copilot-no-scrollbar max-h-48 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#202228] p-1.5">
{loading ? (
<div className="flex items-center gap-2 px-2.5 py-3 text-xs font-semibold text-white/40">
<i className="fa-solid fa-spinner fa-spin text-[10px]"></i>
Loading options...
</div>
) : visibleOptions.length >
0 ? (
visibleOptions.map(
(option) => {
const selected =
String(value || "") ===
option.value;

return (
<button
key={
option.value
}
type="button"
disabled={disabled}
onMouseDown={(event) =>
event.preventDefault()
}
onClick={() =>
selectOption(option)
}
className={`flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-xs transition ${
selected
? "bg-violet-400/20 text-violet-100"
: "text-white/65 hover:bg-white/[0.07] hover:text-white"
}`}
style={
showFontPreview
? {
fontFamily:
option.label,
}
: undefined
}
>
<span className="min-w-0 flex-1 truncate">
{option.label}
</span>

{selected && (
<i className="fa-solid fa-check text-[9px] text-violet-200"></i>
)}
</button>
);
}
)
) : (
<div className="px-2.5 py-3 text-xs font-semibold text-white/35">
No matching options.
</div>
)}
</div>
)}

{value && (
<p className="mb-0 text-[10px] font-medium text-white/30">
Selected:{" "}
<span
className="text-white/55"
style={
showFontPreview
? {
fontFamily:
String(value),
}
: undefined
}
>
{String(value)}
</span>
</p>
)}
</div>
);
};

const normalizeColorGradeBrandKitColor = (
value
) => {
const normalized =
String(value || "")
.trim()
.toUpperCase();

if (
!/^#[0-9A-F]{6}$/.test(
normalized
)
) {
return null;
}

return normalized;
};

const extractColorGradeBrandKitColors = (
brandKit = null
) => {
if (
!brandKit ||
typeof brandKit !==
"object"
) {
return [];
}

const palette =
Array.isArray(
brandKit.palette
)
? brandKit.palette
: String(
brandKit.palette || ""
)
.split(
/[\s,]+/
);

const candidates = [
...palette,
brandKit.brand_color,
brandKit.bg_color,
brandKit.text_color,
];

return [
...new Set(
candidates
.map(
normalizeColorGradeBrandKitColor
)
.filter(Boolean)
),
].slice(0, 8);
};

const GenericActionDraftForm = ({
  interaction,
  enabled,
  loading,
  loadRemoteOptions,
  uploadMedia,
  onSubmit,
  onCancel,
}) => {
  const fields =
Array.isArray(
interaction?.fields
)
? interaction.fields
: [];

const {
selectedGroup,
groups,
} = useSelector(
(state) =>
state.groups || {}
);

const selectedGroupId =
useMemo(() => {
if (
!selectedGroup ||
!Array.isArray(groups)
) {
return null;
}

const selectedGroupRecord =
groups.find(
(group) =>
String(
group?.name || ""
) ===
String(selectedGroup)
);

return (
selectedGroupRecord?._id ||
selectedGroupRecord?.id ||
null
);
}, [
groups,
selectedGroup,
]);

const isColorGradeAction =
interaction?.toolName ===
"apps.colorGradeImage";

const [
brandKitColors,
setBrandKitColors,
] = useState([]);

const [
brandKitColorsLoading,
setBrandKitColorsLoading,
] = useState(false);

useEffect(() => {
if (
!isColorGradeAction ||
!selectedGroupId
) {
setBrandKitColors([]);
setBrandKitColorsLoading(false);
return undefined;
}

let cancelled = false;

const loadBrandKitColors =
async () => {
setBrandKitColorsLoading(true);

try {
const brandKit =
await getOnboardingForGroup(
selectedGroupId
);

if (cancelled) {
return;
}

setBrandKitColors(
extractColorGradeBrandKitColors(
brandKit
)
);
} catch (_) {
if (!cancelled) {
setBrandKitColors([]);
}
} finally {
if (!cancelled) {
setBrandKitColorsLoading(false);
}
}
};

loadBrandKitColors();

return () => {
cancelled = true;
};
}, [
interaction?.actionDraftId,
isColorGradeAction,
selectedGroupId,
]);

const [values, setValues] =
    useState(() => {
      return fields.reduce(
        (result, field) => {
          result[field.key] =
            getFieldInitialValue(
              field,
              fields
            );

          return result;
        },
        {}
      );
    });

  useEffect(() => {
  setValues(
    fields.reduce(
      (result, field) => {
        result[field.key] =
          getFieldInitialValue(
            field,
            fields
          );

        return result;
      },
      {}
    )
  );

  setSubmitted(false);

setAttemptedSubmit(false);
}, [
interaction?.actionDraftId,
interaction?.revision,
]);

const [
remoteOptions,
setRemoteOptions,
] = useState({});

  const [
    remoteLoading,
    setRemoteLoading,
  ] = useState({});

  const [
    uploadState,
    setUploadState,
  ] = useState({
    fieldKey: null,
    loading: false,
    progress: 0,
    error: null,
  });

  const [submitted, setSubmitted] =
  useState(false);

const [
  attemptedSubmit,
  setAttemptedSubmit,
] = useState(false);

const fieldElementRefs =
  useRef({});

  const mediaInputRef =
    useRef(null);

  const activeMediaFieldRef =
    useRef(null);

  const mediaInputModeRef =
    useRef("add");

  const replacementIndexRef =
    useRef(null);

  const [
draggedMedia,
setDraggedMedia,
] = useState(null);

const disabled =
    !enabled ||
    loading ||
    uploadState.loading ||
    submitted;

  useEffect(() => {
fields.forEach((field) => {
if (
![
"remote_select",
"searchable_select",
].includes(
field.control
) ||
!field.dataSource ||
remoteOptions[field.key] ||
remoteLoading[field.key]
) {
return;
}

      setRemoteLoading(
        (current) => ({
          ...current,
          [field.key]: true,
        })
      );

      Promise.resolve(
        loadRemoteOptions?.(
          field.dataSource
        )
      )
        .then((result) => {
          setRemoteOptions(
            (current) => ({
              ...current,
              [field.key]:
                result?.error
                  ? []
                  : result?.data ||
                    [],
            })
          );
        })
        .finally(() => {
          setRemoteLoading(
            (current) => ({
              ...current,
              [field.key]:
                false,
            })
          );
        });
    });
  }, [
    fields,
    loadRemoteOptions,
    remoteLoading,
    remoteOptions,
  ]);

  const updateValue = (
fieldKey,
value
) => {
if (disabled) {
return;
}

setValues((current) => {
if (
isColorGradeAction &&
fieldKey ===
"appColorGradeUseBrandKit"
) {
const useBrandKit =
Boolean(value);

return {
...current,
appColorGradeUseBrandKit:
useBrandKit,
appColorGradeColors:
useBrandKit
? [
...brandKitColors,
]
: [],
};
}

return {
...current,
[fieldKey]:
value,
};
});

setAttemptedSubmit(false);
};

  const timezoneField =
    fields.find(
      (field) =>
        field.control ===
        "timezone_select"
    );

    const selectedTimezone = (() => {
    const rawValue =
      timezoneField
        ? values[
            timezoneField.key
          ]
        : null;

    if (
      rawValue ===
      "__DEVICE_TIMEZONE__"
    ) {
      return (
        Intl.DateTimeFormat()
          .resolvedOptions()
          .timeZone ||
        "UTC"
      );
    }

    return rawValue || "";
  })();

  const timezoneOptions =
useMemo(() => {
return moment.tz.names();
}, []);

const timezoneSelectOptions =
useMemo(() => {
const deviceTimezone =
Intl.DateTimeFormat()
.resolvedOptions()
.timeZone ||
"UTC";

const suggestionOptions =
(
timezoneField
?.suggestions ||
[]
).map((suggestion) => {
const resolvedValue =
suggestion.value ===
"__DEVICE_TIMEZONE__"
? deviceTimezone
: suggestion.value;

return {
id:
resolvedValue,
name:
suggestion.label
? `${suggestion.label} · ${resolvedValue}`
: resolvedValue,
};
});

const allOptions = [
...suggestionOptions,
...timezoneOptions.map(
(timezone) => ({
id:
timezone,
name:
timezone,
})
),
];

const optionsByValue =
new Map();

allOptions.forEach(
(option) => {
const optionValue =
String(
option.id || ""
).trim();

if (
!optionValue ||
optionsByValue.has(
optionValue
)
) {
return;
}

optionsByValue.set(
optionValue,
option
);
}
);

return Array.from(
optionsByValue.values()
);
}, [
timezoneField,
timezoneOptions,
]);

  const hasFieldValue = (
value
) => {
if (Array.isArray(value)) {
return value.length > 0;
}

if (
value &&
typeof value === "object"
) {
return Object.keys(value).length > 0;
}

return Boolean(
String(value ?? "").trim()
);
};

const getFieldByPatchPath = (
patchPath
) => {
return fields.find(
(candidate) =>
candidate.patchPath ===
patchPath
);
};

const getValueByPatchPath = (
patchPath
) => {
const relatedField =
getFieldByPatchPath(
patchPath
);

if (!relatedField) {
return undefined;
}

return values[
relatedField.key
];
};

const getMediaItemType = (
item = {}
) => {
return String(
item.type ||
item.mediaType ||
item.metadata
?.libraryAsset?.type ||
""
)
.trim()
.toLowerCase();
};

const isVisibilityConditionMet = (
condition = {}
) => {
const relatedValue =
getValueByPatchPath(
condition.patchPath
);

if (
condition.operator ===
"empty"
) {
return !hasFieldValue(
relatedValue
);
}

if (
condition.operator ===
"not_empty"
) {
return hasFieldValue(
relatedValue
);
}

if (
condition.operator ===
"contains_media_type"
) {
return (
Array.isArray(
relatedValue
) &&
relatedValue.some(
(item) =>
getMediaItemType(
item
) ===
String(
condition.value ||
""
)
.toLowerCase()
)
);
}

return true;
};

const isFieldVisible = (
field
) => {
const conditions =
Array.isArray(
field?.visibleWhen
)
? field.visibleWhen
: [];

if (
conditions.length === 0
) {
return true;
}

return conditions.every(
isVisibilityConditionMet
);
};

const isFieldRequired = (
field
) => {
return (
field.required === true ||
(
field.requiredWhenVisible ===
true &&
isFieldVisible(field)
)
);
};

const getConflictingField = (
field
) => {
if (
!field?.conflictsWithPatchPath
) {
return null;
}

return getFieldByPatchPath(
field.conflictsWithPatchPath
);
};

const hasFieldConflict = (
field
) => {
const conflictingField =
getConflictingField(field);

if (!conflictingField) {
return false;
}

return (
hasFieldValue(
values[field.key]
) &&
hasFieldValue(
values[
conflictingField.key
]
)
);
};

const isFieldBlockedByConflict = (
field
) => {
const conflictingField =
getConflictingField(field);

if (!conflictingField) {
return false;
}

return (
!hasFieldValue(
values[field.key]
) &&
hasFieldValue(
values[
conflictingField.key
]
)
);
};

const isFieldResolved = (
field
) => {
if (
field.control ===
"instruction"
) {
return (
field.blocking !== true
);
}

if (
!isFieldVisible(field)
) {
return true;
}

if (hasFieldConflict(field)) {
return false;
}

if (
!isFieldRequired(field)
) {
return true;
}

const value =
values[field.key];

if (
[
"connected_profile_multi_select",
"delete_post_selection",
"asset_selection",
"account_selection",
"brand_kit_field_selection",
].includes(
field.control
)
) {
      const selectedValues =
        Array.isArray(value)
          ? value
          : [];

      if (
        field.control ===
          "asset_selection" &&
        field.multiple === false
      ) {
        return (
          selectedValues.length === 1
        );
      }

      return (
        selectedValues.length > 0
      );
    }

    if (
      field.control ===
      "timezone_datetime_list"
    ) {
      return (
        Boolean(
          selectedTimezone
        ) &&
        moment.tz.zone(
          selectedTimezone
        ) !== null &&
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(
          (dateValue) =>
            Boolean(dateValue) &&
            moment(
              dateValue,
              "YYYY-MM-DDTHH:mm",
              true
            ).isValid()
        )
      );
    }

if (
[
"media_library_upload",
"single_image_upload",
"brand_image_list",
].includes(
field.control
)
) {
const items =
Array.isArray(value)
? value
: [];

      const minimum =
        Number(
          field.constraints
            ?.minimum || 1
        );

      const maximum =
        Number(
          field.constraints
            ?.maximum || 100
        );

      const acceptedTypes =
        Array.isArray(
          field.constraints
            ?.acceptedTypes
        )
          ? field.constraints
              .acceptedTypes
          : [];

      const mediaTypes =
  items.map((item) => {
    return String(
      item.type ||
      item.mediaType ||
      ""
    )
      .trim()
      .toLowerCase();
  });

const accepted =
  acceptedTypes.length === 0
    ? items.length === 0
    : mediaTypes.every(
        (mediaType) =>
          acceptedTypes.includes(
            mediaType
          )
      );

return (
  items.length >= minimum &&
  items.length <= maximum &&
  accepted
);
    }

if (
field.valueType ===
"boolean"
) {
return (
value === true ||
value === false
);
}

if (
field.allowEmpty === true
) {
return (
value !== undefined &&
value !== null
);
}

return Boolean(value);
};

  const unresolvedFields =
    fields.filter(
      (field) =>
        !isFieldResolved(field)
    );

    const handleMediaSelect =
    async (event) => {
      const files = Array.from(
        event.target.files || []
      );

      event.target.value = "";

      const fieldKey =
        activeMediaFieldRef.current;

      const inputMode =
        mediaInputModeRef.current;

      const replacementIndex =
        replacementIndexRef.current;

      if (
        !fieldKey ||
        files.length === 0
      ) {
        return;
      }

      setUploadState({
        fieldKey,
        loading: true,
        progress: 0,
        error: null,
      });

      try {
const field =
fields.find(
(candidate) =>
candidate.key ===
fieldKey
);

if (!field) {
throw new Error(
"The selected media field is no longer available."
);
}

const maximum =
Math.max(
Number(
field.constraints
?.maximum ||
100
),
1
);

const existing =
Array.isArray(
values[fieldKey]
)
? [
...values[
fieldKey
],
]
: [];

const remainingSlots =
inputMode ===
"replace"
? 1
: Math.max(
maximum -
existing.length,
0
);

if (remainingSlots === 0) {
throw new Error(
`This field already contains its maximum of ${maximum} media item${
maximum === 1
? ""
: "s"
}.`
);
}

const filesToUpload =
inputMode ===
"replace"
? files.slice(
0,
1
)
: files.slice(
0,
remainingSlots
);

const uploaded =
await uploadMedia?.({
            files:
              filesToUpload,
            onProgress: ({
              progress,
            }) => {
              setUploadState(
                (current) => ({
                  ...current,
                  progress,
                })
              );
            },
          });

        const normalized =
          Array.isArray(uploaded)
            ? uploaded
                .map(
                  normalizeUploadedMediaItem
                )
                .filter(
                  (item) =>
                    item.assetId ||
                    item.url
                )
            : [];

        if (
          normalized.length === 0
        ) {
          throw new Error(
            "No valid media was uploaded."
          );
        }

      
        let nextMediaItems;

        if (
          inputMode ===
            "replace" &&
          Number.isInteger(
            replacementIndex
          ) &&
          replacementIndex >= 0 &&
          replacementIndex <
            existing.length
        ) {
          nextMediaItems = [
            ...existing,
          ];

          nextMediaItems[
            replacementIndex
          ] = normalized[0];
        } else {
          const merged = [];
          const seen = new Set();

          [
            ...existing,
            ...normalized,
          ].forEach((item) => {
            const identity =
              item.assetId
                ? `asset:${item.assetId}`
                : item.url
                ? `url:${item.url}`
                : null;

            if (
              !identity ||
              seen.has(identity)
            ) {
              return;
            }

            seen.add(identity);
            merged.push(item);
          });

          nextMediaItems = merged;
        }

        setValues((current) => ({
...current,
[fieldKey]:
nextMediaItems,
}));

        setAttemptedSubmit(false);

        setUploadState({
          fieldKey,
          loading: false,
          progress: 100,
          error: null,
        });
      } catch (error) {
        setUploadState({
          fieldKey,
          loading: false,
          progress: 0,
          error:
            error.message ||
            "Media upload failed.",
        });
      } finally {
        mediaInputModeRef.current =
          "add";

        replacementIndexRef.current =
          null;
      }
    };

  const moveMediaItem = ({
    fieldKey,
    fromIndex,
    toIndex,
  }) => {
    if (
      disabled ||
      fromIndex === toIndex
    ) {
      return;
    }

    setValues((current) => {
      const items =
        Array.isArray(
          current[fieldKey]
        )
          ? [
              ...current[
                fieldKey
              ],
            ]
          : [];

      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >=
          items.length ||
        toIndex >=
          items.length
      ) {
        return current;
      }

      const [
        movedItem,
      ] = items.splice(
        fromIndex,
        1
      );

      items.splice(
        toIndex,
        0,
        movedItem
      );

      return {
        ...current,
        [fieldKey]: items,
      };
    });

    setAttemptedSubmit(false);
  };

  const requestMediaReplacement = ({
    field,
    index,
  }) => {
    if (disabled) {
      return;
    }

    activeMediaFieldRef.current =
      field.key;

    mediaInputModeRef.current =
      "replace";

    replacementIndexRef.current =
      index;

    if (
      mediaInputRef.current
    ) {
      const acceptedTypes =
        field.constraints
          ?.acceptedTypes ||
        [];

      const acceptValues = [];

      if (
        acceptedTypes.includes(
          "image"
        )
      ) {
        acceptValues.push(
          "image/*"
        );
      }

      if (
        acceptedTypes.includes(
          "video"
        )
      ) {
        acceptValues.push(
          "video/*"
        );
      }

      if (
        acceptedTypes.includes(
          "document"
        )
      ) {
        acceptValues.push(
          ".pdf",
          ".doc",
          ".docx",
          ".ppt",
          ".pptx"
        );
      }

      mediaInputRef.current.accept =
        acceptValues.join(",");

      mediaInputRef.current.multiple =
        false;

      mediaInputRef.current.click();
    }
  };

  const getMediaFormatLabel = ({
    field,
    items,
  }) => {
    const mediaItems =
      Array.isArray(items)
        ? items
        : [];

    if (
      mediaItems.length === 0
    ) {
      return (
        field.editor
          ?.formatLabel ||
        "Media"
      );
    }

    if (
      mediaItems.length > 1
    ) {
      return (
        field.platform ===
          "TIKTOK"
          ? "Photo slideshow"
          : "Carousel"
      );
    }

    return (
      mediaItems[0]?.type ===
      "video"
        ? "Single video"
        : mediaItems[0]?.type ===
          "document"
        ? "Document"
        : "Single image"
    );
  };

  const buildPatch = () => {
    let patch = {};

    fields.forEach((field) => {
      if (
        !field.patchPath ||
        field.patchPath.startsWith(
          "__"
        ) ||
        field.control ===
          "instruction"
      ) {
        return;
      }

      let value =
isFieldVisible(field)
? values[field.key]
: Object.prototype
.hasOwnProperty.call(
field,
"clearValueWhenHidden"
)
? field.clearValueWhenHidden
: values[field.key];

if (
field.control ===
"timezone_select"
) {
        value =
          value ===
          "__DEVICE_TIMEZONE__"
            ? Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone ||
              "UTC"
            : value;
      }

      if (
        field.control ===
        "timezone_datetime_list"
      ) {
        if (
          !selectedTimezone ||
          moment.tz.zone(
            selectedTimezone
          ) === null
        ) {
          throw new Error(
            "Select a valid IANA timezone before continuing."
          );
        }

        value = (
          Array.isArray(value)
            ? value
            : []
        ).map((localDateTime) => {
          const parsed =
            moment.tz(
              localDateTime,
              "YYYY-MM-DDTHH:mm",
              selectedTimezone
            );

          if (!parsed.isValid()) {
            throw new Error(
              `Invalid scheduled date and time: ${localDateTime}`
            );
          }

          return parsed
            .utc()
            .toISOString();
        });
      }

      if (
field.control ===
"single_image_upload"
) {
const imageItems =
Array.isArray(value)
? value
: [];

value =
imageItems[0]?.url ||
"";
}

if (
field.control ===
"brand_image_list"
) {
value = (
Array.isArray(value)
? value
: []
)
.map((item) =>
item?.url ||
null
)
.filter(Boolean);
}

if (
field.control ===
"remote_select" &&
field.valueTransform ===
"object_id"
) {
  value =
    value?.id ||
    value ||
    null;
}

if (
  field.control ===
    "select" &&
  field.valueType ===
    "boolean"
) {
  if (
    value === "true"
  ) {
    value = true;
  } else if (
    value === "false"
  ) {
    value = false;
  }
}

if (
  field.control ===
  "account_selection"
) {
  const selectedValues =
    Array.isArray(value)
      ? value
      : [];

  value = (
    field.options || []
  )
    .filter((option) =>
      selectedValues.includes(
        option.value
      )
    )
    .map((option) => ({
      platform:
        option.platform,
      profileId:
        option.profileId,
      platformId:
        option.platformId ||
        null,
      connectionId:
        option.connectionId ||
        null,
      profileName:
        option.profileName ||
        option.label ||
        null,
    }));
}


if (
field.control ===
"connected_profile_multi_select"
) {
        const selectedValues =
          Array.isArray(value)
            ? value
            : [];

        value = (
          field.options || []
        )
          .filter((option) =>
            selectedValues.includes(
              option.value
            )
          )
          .map((option) => ({
            platform:
              option.platform,
            profileId:
              option.profileId,
            connectionId:
              option.connectionId ||
              null,
            profileName:
              option.profileName ||
              option.label ||
              null,
          }));
      }

      patch = setValueAtPath(
        patch,
        field.patchPath,
        value
      );
    });

    return patch;
};


const submitDeleteView =
async ({
field,
source,
month,
page,
preserveSelection = false,
}) => {
if (disabled) {
return;
}

const duplicateMode =
field.actionMode ===
"duplicate";

const normalizedSource =
[
"all",
"draft",
"scheduled",
"published",
].includes(source)
? source
: "all";

const normalizedMonth =
month &&
month !== "all"
? month
: null;

const normalizedPage =
Math.max(
Number.parseInt(
page,
10
) || 1,
1
);

const currentSelectedPostIds =
preserveSelection &&
Array.isArray(
values[field.key]
)
? [
...new Set(
values[field.key]
.map(String)
.filter(Boolean)
),
]
: [];

setSubmitted(true);

try {
await onSubmit?.({
actionDraftId:
interaction.actionDraftId,
revision:
interaction.revision,
patch:
duplicateMode
? {
source: {
postId: null,
postIds: [],
selectedPostIds:
currentSelectedPostIds,
allMatches: false,
source:
normalizedSource,
month:
normalizedMonth,
page:
normalizedPage,
pageSize: 20,
},
}
: {
targets: {
postIds: [],
selector: {
selectedPostIds:
currentSelectedPostIds,
source:
normalizedSource,
month:
normalizedMonth,
page:
normalizedPage,
pageSize: 20,
},
},
},
metadata: {
presentationMode:
"replace_interaction",
interactionOperation:
duplicateMode
? "duplicate_view_change"
: "delete_view_change",
},
});
} catch (error) {
setSubmitted(false);
}
};


const submitDeleteSelection =
async (
postIds,
field,
allMatches = false
) => {
if (disabled) {
return;
}

const duplicateMode =
field?.actionMode ===
"duplicate";

const normalizedPostIds = [
...new Set(
(
Array.isArray(postIds)
? postIds
: []
)
.map(String)
.filter(Boolean)
),
];

if (
normalizedPostIds.length === 0 &&
!allMatches
) {
setAttemptedSubmit(true);
return;
}

const selectedSource =
field?.selectedSource ||
"all";

const selectedMonth =
field?.selectedMonth ||
"all";

setSubmitted(true);

try {
await onSubmit?.({
actionDraftId:
interaction.actionDraftId,
revision:
interaction.revision,
patch:
duplicateMode
? {
source: {
postId: null,
postIds:
allMatches
? []
: normalizedPostIds,
selectedPostIds:
allMatches
? []
: normalizedPostIds,
allMatches,
source:
selectedSource,
month:
selectedMonth ===
"all"
? null
: selectedMonth,
page:
field?.pagination?.page ||
1,
pageSize: 20,
},
}
: {
targets: {
postIds:
normalizedPostIds,
selector: {
selectedPostIds:
normalizedPostIds,
source:
selectedSource,
month:
selectedMonth ===
"all"
? null
: selectedMonth,
page:
field?.pagination?.page ||
1,
pageSize: 20,
},
},
},
});
} catch (error) {
setSubmitted(false);
}
};


  const submitForm = async () => {
  if (disabled) {
    return;
  }

  if (
    unresolvedFields.length > 0
  ) {
    setAttemptedSubmit(true);

    const firstField =
      unresolvedFields[0];

    fieldElementRefs.current[
      firstField.key
    ]?.scrollIntoView?.({
      behavior: "smooth",
      block: "center",
    });

    return;
  }

  setSubmitted(true);

  try {
    await onSubmit?.({
      actionDraftId:
        interaction.actionDraftId,
      revision:
        interaction.revision,
      patch:
        buildPatch(),
    });
  } catch (error) {
    setSubmitted(false);
  }
};

  return (
    <div className="mt-3 w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-violet-400/20 bg-violet-400/[0.06]">
    <input
    ref={mediaInputRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
        onChange={
          handleMediaSelect
        }
      />

      <div className="px-3.5 py-3">
        <h4 className="mb-1 text-sm font-bold text-white/90">
          {interaction.title ||
            "Complete this action"}
        </h4>

        <p className="mb-0 text-xs leading-relaxed text-white/50">
          {interaction.description ||
            "Provide the required information, then continue."}
        </p>
      </div>

      <div className="space-y-4 border-t border-white/[0.06] px-3.5 py-3">
        {fields.map(
  (field, fieldIndex) => {
    const value =
      values[field.key];

    const isFirstInSection =
      Boolean(
        field.sectionKey
      ) &&
      fields.findIndex(
        (candidate) =>
          candidate.sectionKey ===
          field.sectionKey
      ) === fieldIndex;

    const fieldUnresolved =
      !isFieldResolved(
        field
      );

    if (
field.control ===
"instruction"
) {
return (
<div
key={field.key}
className="rounded-lg border border-amber-400/15 bg-amber-400/[0.05] p-3 text-xs text-amber-100/70"
>
{field.description}
</div>
);
}

if (
!isFieldVisible(field)
) {
return null;
}

return (
<div
  key={field.key}
  ref={(element) => {
    fieldElementRefs.current[
      field.key
    ] = element;
  }}
>
{isFirstInSection && (
  <div className="mb-3 mt-2 rounded-xl border border-white/[0.07] bg-white/[0.035] px-3 py-2.5">
    <div className="flex items-center gap-2">
  <i
    className={`${
      field.sectionIcon ||
      PLATFORM_DISPLAY[
        field.platform
      ]?.icon ||
      "fa-solid fa-share-nodes"
    } text-sm ${
      PLATFORM_DISPLAY[
        field.platform
      ]?.iconColor ||
      "text-violet-200/65"
    }`}
  ></i>

  <span className="text-xs font-bold text-white/80">
    {field.sectionLabel}
  </span>
</div>

<span className="mt-0.5 block text-[10px] font-semibold text-white/35">
  {PLATFORM_DISPLAY[
    field.platform
  ]?.label ||
    field.sectionDescription}
</span>
  </div>
)}

<label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-white/40">
                {field.label}
{isFieldRequired(field)
? " *"
: ""}
              </label>

              {field.description && (
<p className="mb-2 text-[10px] leading-relaxed text-white/30">
{field.description}
</p>
)}
{field.proposedValue ===
true && (
<div className="mb-2 rounded-lg border border-violet-300/15 bg-violet-400/[0.055] px-2.5 py-2">
<div className="flex flex-wrap items-center gap-2">
<span className="inline-flex items-center gap-1.5 rounded-full bg-violet-400/15 px-2 py-1 text-[9px] font-bold text-violet-100">
<i className="fa-solid fa-wand-magic-sparkles text-[8px]"></i>
Copilot proposal
</span>
<span className="text-[9px] font-medium text-white/35">
Editable before confirmation
</span>
</div>
{field.currentValue !==
undefined &&
field.currentValue !==
null && (
<p className="mb-0 mt-2 break-words text-[10px] leading-relaxed text-white/40">
<span className="font-bold text-white/50">
Current value:
</span>{" "}
{formatActionDetailValue(
field.currentValue
)}
</p>
)}
</div>
)}
{field.inherited &&
String(value || "") ===
String(
field.inheritedValue ||
""
) && (
    <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/[0.08] px-2 py-1 text-[9px] font-semibold text-emerald-100/65">
      <i className="fa-solid fa-link text-[8px]"></i>
      {field.inheritedLabel ||
        "Using shared value"}
    </div>
  )}

{hasFieldConflict(field) && (
<p className="mb-2 rounded-lg border border-red-400/15 bg-red-400/[0.06] px-2.5 py-2 text-[10px] font-semibold text-red-100/80">
{field.conflictMessage ||
"Remove one of the conflicting values before continuing."}
</p>
)}

{isFieldBlockedByConflict(
field
) && (
<p className="mb-2 rounded-lg border border-amber-400/15 bg-amber-400/[0.05] px-2.5 py-2 text-[10px] font-semibold text-amber-100/70">
This field is unavailable until the conflicting content is removed.
</p>
)}

{attemptedSubmit &&
fieldUnresolved &&
!hasFieldConflict(field) && (
<p className="mb-2 rounded-lg border border-red-400/15 bg-red-400/[0.06] px-2.5 py-2 text-[10px] font-semibold text-red-100/75">
{field.control ===
"asset_selection"
? field.multiple === false
  ? "Select exactly one asset."
  : "Select at least one asset."
: [
"media_library_upload",
"single_image_upload",
"brand_image_list",
].includes(
field.control
)
? `Select between ${
field.constraints
?.minimum || 1
} and ${
field.constraints
?.maximum || 100
} compatible media item${
Number(
field.constraints
?.maximum || 100
) === 1
? ""
: "s"
}. Accepted types: ${
(
field.constraints
?.acceptedTypes ||
[]
).join(", ") ||
"none"
}.`
: `${field.label} is required.`}
</p>
)}

{field.control ===
"brand_kit_field_selection" && (
<BrandKitFieldSelection
field={field}
value={
Array.isArray(value)
? value
: []
}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

{field.control ===
"color_input" && (
<ColorInputEditor
value={value || ""}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

{field.control ===
"color_list" && (
<ColorListEditor
value={
Array.isArray(value)
? value
: []
}
disabled={
disabled ||
(
isColorGradeAction &&
field.key ===
"appColorGradeColors" &&
values
.appColorGradeUseBrandKit ===
true
)
}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

{field.control ===
"structured_topics" && (
<ContentTopicsEditor
value={
Array.isArray(value)
? value
: []
}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

{field.control ===
"structured_styles" && (
<StructuredStylesEditor
value={
value &&
typeof value ===
"object" &&
!Array.isArray(value)
? value
: {}
}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

              {field.control ===
                "timezone_select" && (
                <>
                  {Array.isArray(
                    field.suggestions
                  ) &&
                    field.suggestions
                      .length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {field.suggestions.map(
                          (
                            suggestion
                          ) => {
                            const resolvedValue =
                              suggestion.value ===
                              "__DEVICE_TIMEZONE__"
                                ? Intl.DateTimeFormat()
                                    .resolvedOptions()
                                    .timeZone ||
                                  "UTC"
                                : suggestion.value;

                            const selected =
                              value ===
                              resolvedValue;

                            return (
                              <button
                                key={
                                  suggestion.value
                                }
                                type="button"
                                disabled={
                                  disabled
                                }
                                onClick={() =>
                                  updateValue(
                                    field.key,
                                    resolvedValue
                                  )
                                }
                                className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold transition ${
                                  selected
                                    ? "border-violet-300/40 bg-violet-400/20 text-white"
                                    : "border-white/[0.08] bg-white/[0.04] text-white/50 hover:bg-white/[0.08]"
                                }`}
                              >
                                {
                                  suggestion.label
                                }
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}

                  <SearchableSelectEditor
field={{
...field,
dataSource: {
...(field.dataSource || {}),
type:
"iana_timezones",
valueField: "id",
labelField: "name",
searchable: true,
},
}}
value={
value || ""
}
options={
timezoneSelectOptions
}
loading={false}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
                </>
              )}

              {field.control ===
                "timezone_datetime_list" && (
                <div className="space-y-2">
                  {(
                    Array.isArray(value) &&
                    value.length > 0
                      ? value
                      : [""]
                  ).map(
                    (
                      dateValue,
                      index
                    ) => (
                      <div
                        key={`${field.key}-${index}`}
                        className="flex items-center gap-2"
                      >
                        <input
                          type="datetime-local"
                          value={
                            dateValue ||
                            ""
                          }
                          disabled={
                            disabled
                          }
                          onChange={(
                            event
                          ) => {
                            const next =
                              Array.isArray(
                                value
                              ) &&
                              value.length >
                                0
                                ? [
                                    ...value,
                                  ]
                                : [""];

                            next[index] =
                              event.target.value;

                            updateValue(
                              field.key,
                              next
                            );
                          }}
                          className="h-10 min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none"
                        />

                        {index > 0 && (
                          <button
                            type="button"
                            disabled={
                              disabled
                            }
                            onClick={() =>
                              updateValue(
                                field.key,
                                value.filter(
                                  (
                                    _,
                                    itemIndex
                                  ) =>
                                    itemIndex !==
                                    index
                                )
                              )
                            }
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.06] text-white/45"
                          >
                            <i className="fa-solid fa-xmark text-[10px]"></i>
                          </button>
                        )}
                      </div>
                    )
                  )}

                  {field.multiple && (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() =>
                        updateValue(
                          field.key,
                          [
                            ...(
                              Array.isArray(
                                value
                              )
                                ? value
                                : []
                            ),
                            "",
                          ]
                        )
                      }
                      className="text-[10px] font-bold text-violet-200/70 hover:text-violet-100"
                    >
                      + Add another time
                    </button>
                  )}

                  <p className="mb-0 text-[10px] text-white/30">
                    Timezone:{" "}
                    {selectedTimezone ||
                      "Select a timezone"}
                  </p>
                </div>
              )}


              {field.control ===
  "account_selection" && (
  <div className="space-y-3">
    {Array.isArray(
      field.options
    ) &&
    field.options.length > 0 ? (
      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        {field.options.map(
          (
            option,
            optionIndex
          ) => {
            const optionValue =
              String(
                option.value ||
                option.id ||
                ""
              );

            const selected =
              Array.isArray(
                value
              ) &&
              value.includes(
                optionValue
              );

            return (
              <button
                key={
                  optionValue ||
                  optionIndex
                }
                type="button"
                disabled={disabled}
                onClick={() => {
                  const current =
                    Array.isArray(
                      value
                    )
                      ? value
                      : [];

                  if (selected) {
                    updateValue(
                      field.key,
                      current.filter(
                        (item) =>
                          item !==
                          optionValue
                      )
                    );
                    return;
                  }

                  updateValue(
                    field.key,
                    field.multiple ===
                      false
                      ? [
                          optionValue,
                        ]
                      : [
                          ...current,
                          optionValue,
                        ]
                  );
                }}
                className={`flex min-w-0 items-center gap-3 rounded-xl border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  selected
                    ? "border-red-300/45 bg-red-400/15 text-white"
                    : "border-white/[0.08] bg-white/[0.035] text-white/65 hover:border-white/[0.16] hover:bg-white/[0.07]"
                }`}
              >
                <span className="min-w-0 flex-1">
  <ConnectedAccountIdentity
    account={option}
  />
</span>

<span
  className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${
    selected
      ? "border-red-200/70 bg-red-500 text-white"
      : "border-white/15 bg-white/[0.04] text-transparent"
  }`}
>
  <i className="fa-solid fa-check text-[9px]"></i>
</span>
              </button>
            );
          }
        )}
      </div>
    ) : (
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-3 py-4 text-center text-xs font-semibold text-white/45">
        {field.emptyMessage ||
          "No connected profiles matched this request."}
      </div>
    )}

    <span className="inline-flex rounded-full bg-red-400/10 px-2.5 py-1 text-[10px] font-bold text-red-100/70">
      {Array.isArray(value)
        ? value.length
        : 0}{" "}
      selected
    </span>
  </div>
)}




                            {field.control ===
                "asset_selection" && (
                <div className="space-y-3">
                  {Array.isArray(
                    field.options
                  ) &&
                  field.options.length >
                    0 ? (
                    <div className="grid min-w-0 grid-cols-1 items-start gap-3 sm:grid-cols-2">
                      {field.options.map(
                        (
                          option,
                          optionIndex
                        ) => {
                          const assetId =
                            String(
                              option.assetId ||
                                option.id ||
                                option.value ||
                                ""
                            );

                          const selected =
                            Array.isArray(
                              value
                            ) &&
                            value.includes(
                              assetId
                            );

                          const destructive =
                            field.actionMode ===
                            "delete";

                          return (
                            <div
                              key={
                              assetId ||
                              option.url ||
                              optionIndex
                              }
                              className="min-w-0 max-w-full overflow-hidden"
                              >
                              <AssetPreviewTile
                                asset={
                                  option
                                }
                                selected={
                                  selected
                                }
                                disabled={
                                  disabled
                                }
                                selectable
                                destructive={
                                  destructive
                                }
                                onClick={() => {
                                  const current =
                                    Array.isArray(
                                      value
                                    )
                                      ? value
                                      : [];

                                  if (
                                    selected
                                  ) {
                                    updateValue(
                                      field.key,
                                      current.filter(
                                        (
                                          item
                                        ) =>
                                          item !==
                                          assetId
                                      )
                                    );

                                    return;
                                  }

                                  updateValue(
                                    field.key,
                                    field.multiple ===
                                      false
                                      ? [
                                          assetId,
                                        ]
                                      : [
                                          ...current,
                                          assetId,
                                        ]
                                  );
                                }}
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-3 py-4 text-center text-xs font-semibold text-white/45">
                      {field.emptyMessage ||
                        "No current Media Library assets matched this request."}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                        field.actionMode ===
                        "delete"
                          ? "bg-red-400/10 text-red-100/70"
                          : "bg-violet-400/10 text-violet-100/70"
                      }`}
                    >
                      {Array.isArray(
                        value
                      )
                        ? value.length
                        : 0}{" "}
                      selected
                    </span>

                    {field.multiple ===
                      false && (
                      <span className="text-[10px] font-medium text-white/35">
                        Select exactly one asset.
                      </span>
                    )}
                  </div>
                </div>
              )}


              {field.control ===
"delete_post_selection" && (
<div className="space-y-3">
<div className="space-y-2 rounded-xl border border-white/[0.07] bg-white/[0.025] p-2.5">
<div className="flex flex-wrap gap-2">
{(
field.sourceOptions ||
[]
).map(
(option) => {
const selected =
(
field.selectedSource ||
"all"
) ===
option.value;

return (
<button
key={
option.value
}
type="button"
disabled={disabled}
onClick={() =>
submitDeleteView({
field,
source:
option.value,
month:
field.selectedMonth ||
"all",
page: 1,
})
}
className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition ${
selected
? "border-violet-300/40 bg-violet-400/20 text-white"
: "border-white/[0.08] bg-white/[0.035] text-white/45"
}`}
>
{option.label}
</button>
);
}
)}
</div>

<select
value={
field.selectedMonth ||
"all"
}
disabled={disabled}
onChange={(event) =>
submitDeleteView({
field,
source:
field.selectedSource ||
"all",
month:
event.target.value,
page: 1,
})
}
className="h-9 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs font-semibold text-white outline-none"
>
{(
field.monthOptions ||
[]
).map(
(option) => (
<option
key={
option.value
}
value={
option.value
}
>
{option.label}
</option>
)
)}
</select>

<p className="mb-0 text-[10px] font-semibold text-white/35">
{Number(
field.pagination?.total ||
0
).toLocaleString()}{" "}
matching post
{Number(
field.pagination?.total ||
0
) === 1
? ""
: "s"}
{" · "}
Page{" "}
{field.pagination?.page ||
1}{" "}
of{" "}
{field.pagination?.pages ||
1}
</p>
</div>

{Array.isArray(
field.options
) &&
field.options.length > 0 ? (
<div className="space-y-2">
{field.options.map(
(option, optionIndex) => {
const postId =
String(
option.sourceQualifiedId ||
(
option.source &&
option.postId
? `${option.source}:${option.postId}`
: option.postId ||
""
)
);

const selected =
Array.isArray(
value
) &&
value.includes(
postId
);

const firstMedia =
Array.isArray(
option.media
)
? option.media[0]
: null;

const visibleNumber =
optionIndex + 1;

const duplicateMode =
field.actionMode ===
"duplicate";

return (
<div
key={postId}
className={`overflow-hidden rounded-xl border transition ${
selected
? field.actionMode ===
"duplicate"
? "border-violet-300/35 bg-violet-400/[0.08]"
: "border-red-300/35 bg-red-400/[0.08]"
: "border-white/[0.08] bg-black/[0.12]"
}`}
>
<div className="flex items-start gap-3 p-3">
<button
type="button"
disabled={disabled}
onClick={() => {
const current =
Array.isArray(
value
)
? value
: [];

updateValue(
field.key,
selected
? current.filter(
(item) =>
item !==
postId
)
: [
...current,
postId,
]
);
}}
className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border transition ${
selected
? field.actionMode ===
"duplicate"
? "border-violet-300/60 bg-violet-400 text-white"
: "border-red-300/60 bg-red-400 text-white"
: "border-white/[0.12] bg-white/[0.04] text-transparent"
}`}
aria-label={`Select post ${visibleNumber}`}
>
<i className="fa-solid fa-check text-[9px]"></i>
</button>

<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-xs font-black text-white/75">
{visibleNumber}
</div>

{firstMedia?.url && (
<div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-black/30">
{firstMedia.type ===
"video" ? (
<video
src={
firstMedia.url
}
muted
playsInline
preload="metadata"
className="h-full w-full object-cover"
/>
) : (
<img
src={
firstMedia.url
}
alt=""
className="h-full w-full object-cover"
/>
)}
</div>
)}

<div className="min-w-0 flex-1">
<div className="mb-1 flex flex-wrap items-center gap-2">
<span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-[9px] font-bold uppercase text-white/50">
{option.category ||
option.status}
</span>

{Array.isArray(
option.platforms
) &&
option.platforms.length >
0 && (
<span className="text-[9px] font-semibold uppercase text-white/30">
{option.platforms.join(
", "
)}
</span>
)}
</div>

<p className="mb-0 line-clamp-2 text-xs font-semibold leading-relaxed text-white/75">
{option.caption ||
"No caption"}
</p>

{(
option.displayDate ||
option.scheduledTime
) && (
<p className="mb-0 mt-1 text-[10px] font-medium text-white/35">
{new Date(
option.displayDate ||
option.scheduledTime
).toLocaleString()}
</p>
)}
</div>

<button
type="button"
disabled={disabled}
onClick={() =>
submitDeleteSelection(
[
postId,
],
field
)
}
className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-40 ${
duplicateMode
? "bg-violet-500 hover:bg-violet-400"
: "bg-red-500 hover:bg-red-400"
}`}
>
<i
className={`fa-solid ${
duplicateMode
? "fa-copy"
: "fa-trash"
} text-[9px]`}
></i>
{duplicateMode
? "Duplicate"
: "Delete"}
</button>
</div>
</div>
);
}
)}
</div>
) : (
<div className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-3 py-4 text-center text-xs font-semibold text-white/45">
{field.emptyMessage ||
"No deletable posts match this filter."}
</div>
)}

{Number(
field.pagination?.pages ||
1
) > 1 && (
<div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.07] bg-white/[0.025] p-2.5">
<button
type="button"
disabled={
disabled ||
field.pagination
?.hasPrevious !==
true
}
onClick={() =>
submitDeleteView({
field,
source:
field.selectedSource ||
"all",
month:
field.selectedMonth ||
"all",
page:
Number(
field.pagination?.page ||
1
) - 1,
preserveSelection: true,
})
}
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.06] px-3 text-[10px] font-bold text-white/65 disabled:cursor-not-allowed disabled:opacity-35"
>
<i className="fa-solid fa-chevron-left text-[8px]"></i>
Previous
</button>

<span className="text-[10px] font-semibold text-white/40">
Page{" "}
{field.pagination?.page ||
1}{" "}
of{" "}
{field.pagination?.pages ||
1}
</span>

<button
type="button"
disabled={
disabled ||
field.pagination
?.hasNext !==
true
}
onClick={() =>
submitDeleteView({
field,
source:
field.selectedSource ||
"all",
month:
field.selectedMonth ||
"all",
page:
Number(
field.pagination?.page ||
1
) + 1,
preserveSelection: true,
})
}
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.06] px-3 text-[10px] font-bold text-white/65 disabled:cursor-not-allowed disabled:opacity-35"
>
Next
<i className="fa-solid fa-chevron-right text-[8px]"></i>
</button>
</div>
)}

<div className="flex flex-wrap items-center gap-2">
<button
type="button"
disabled={
disabled ||
!Array.isArray(
value
) ||
value.length === 0
}
onClick={() =>
submitDeleteSelection(
value,
field
)
}
className={`inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-xs font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-40 ${
field.actionMode ===
"duplicate"
? "bg-violet-500 hover:bg-violet-400"
: "bg-red-500 hover:bg-red-400"
}`}
>
<i
className={`fa-solid ${
field.actionMode ===
"duplicate"
? "fa-copy"
: "fa-trash"
} text-[10px]`}
></i>
{field.actionMode ===
"duplicate"
? "Duplicate selected"
: "Delete selected"}
</button>

{field.actionMode ===
"duplicate" && (
<button
type="button"
disabled={
disabled ||
Number(
field.pagination?.total ||
0
) === 0
}
onClick={() =>
submitDeleteSelection(
[],
field,
true
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg border border-violet-300/25 bg-violet-400/10 px-3.5 text-xs font-bold text-violet-100 transition hover:bg-violet-400/20 disabled:cursor-not-allowed disabled:opacity-40"
>
<i className="fa-solid fa-copy text-[10px]"></i>
Duplicate all matching
</button>
)}

<span className="text-[10px] font-semibold text-white/35">
{Array.isArray(
value
)
? value.length
: 0}{" "}
selected
</span>
</div>

<p className="mb-0 text-[10px] leading-relaxed text-white/35">
{field.actionMode ===
"duplicate"
? "Numbers refer only to this page. You can type \"duplicate 1\" or \"duplicate 1, 2 and 3.\""
: "Numbers refer only to this page. You can type \"delete 1\" or \"delete 1, 2 and 3.\""}
</p>
</div>
)}



              {field.control ===
                "connected_profile_multi_select" && (
                <div className="flex flex-wrap gap-2">
                  {(
                    field.options || []
                  ).map((option) => {
                    const selected =
                      Array.isArray(
                        value
                      ) &&
                      value.includes(
                        option.value
                      );

                    return (
                      <button
                        key={
                          option.value
                        }
                        type="button"
                        disabled={
                          disabled
                        }
                        onClick={() => {
                          const current =
                            Array.isArray(
                              value
                            )
                              ? value
                              : [];

                          updateValue(
                            field.key,
                            selected
                              ? current.filter(
                                  (
                                    item
                                  ) =>
                                    item !==
                                    option.value
                                )
                              : [
                                  ...current,
                                  option.value,
                                ]
                          );
                        }}
                        className={`rounded-lg border px-3 py-2 text-left text-xs transition ${
                          selected
                            ? "border-violet-300/40 bg-violet-400/20 text-white"
                            : "border-white/[0.08] bg-white/[0.04] text-white/55"
                        }`}
                      >
                        <span className="block font-bold">
                          {option.label}
                        </span>

                        <span className="text-[10px] text-white/35">
                          {option.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {[
"media_library_upload",
"single_image_upload",
"brand_image_list",
].includes(
field.control
) && (
                <div>
                  <button
type="button"
disabled={
disabled ||
isFieldBlockedByConflict(
field
)
}
onClick={() => {
activeMediaFieldRef.current =
field.key;

  mediaInputModeRef.current =
    "add";

  replacementIndexRef.current =
    null;

  if (
    mediaInputRef.current
  ) {
    const acceptedTypes =
[
"single_image_upload",
"brand_image_list",
].includes(
field.control
)
? ["image"]
: field.constraints
?.acceptedTypes ||
[];

    const acceptValues = [];

    if (
      acceptedTypes.includes(
        "image"
      )
    ) {
      acceptValues.push(
        "image/*"
      );
    }

    if (
      acceptedTypes.includes(
        "video"
      )
    ) {
      acceptValues.push(
        "video/*"
      );
    }

    if (
      acceptedTypes.includes(
        "document"
      )
    ) {
      acceptValues.push(
        ".pdf",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx"
      );
    }

    mediaInputRef.current.accept =
      acceptValues.join(",");

    mediaInputRef.current.multiple =
field.control ===
"brand_image_list" ||
(
field.control !==
"single_image_upload" &&
field.multiple !==
false
);

    mediaInputRef.current.click();
  }
}}
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black disabled:opacity-40"
                  >
                    <i
                      className={`fa-solid ${
                        uploadState.loading &&
                        uploadState.fieldKey ===
                          field.key
                          ? "fa-spinner fa-spin"
                          : "fa-paperclip"
                      } text-[10px]`}
                    ></i>

                    {uploadState.loading &&
                    uploadState.fieldKey ===
                      field.key
                      ? `Uploading ${uploadState.progress}%`
                      : "Add media"}
                  </button>

                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-white/35">
                    <span className="rounded-full bg-white/[0.05] px-2 py-1">
                      {getMediaFormatLabel({
                        field,
                        items:
                          value,
                      })}
                    </span>

                    <span>
                      {Array.isArray(
                        value
                      )
                        ? value.length
                        : 0}
                      {" / "}
                      {Number(
                        field.constraints
                          ?.maximum ||
                          100
                      )}{" "}
                      media item
                      {Number(
                        field.constraints
                          ?.maximum ||
                          100
                      ) === 1
                        ? ""
                        : "s"}
                    </span>

                    {field.editor
?.supportsReorder &&
Array.isArray(
value
) &&
value.length > 1 && (
<span>
Drag items to reorder
</span>
)}
</div>

{Array.isArray(value) &&
value.length >
Number(
field.constraints
?.maximum ||
100
) && (
<p className="mb-0 mt-2 rounded-lg border border-red-400/15 bg-red-400/[0.06] px-2.5 py-2 text-[10px] font-semibold text-red-100/80">
Remove{" "}
{value.length -
Number(
field.constraints
?.maximum ||
100
)}{" "}
media item
{value.length -
Number(
field.constraints
?.maximum ||
100
) === 1
? ""
: "s"}{" "}
to meet the{" "}
{Number(
field.constraints
?.maximum ||
100
)}-item limit.
</p>
)}

{Array.isArray(value) &&
value.length > 0 && (
                      <div className="copilot-no-scrollbar mt-2 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2">
                        {value.map(
                          (
                            item,
                            index
                          ) => {
                            const itemKey =
                              item.assetId ||
                              item.url ||
                              index;

                            return (
                              <div
  key={itemKey}
  draggable={
    !disabled &&
    field.editor
      ?.supportsReorder &&
    value.length > 1
  }
  onDragStart={() => {
    setDraggedMedia({
      fieldKey:
        field.key,
      index,
    });
  }}
  onDragOver={(event) => {
    if (
      draggedMedia
        ?.fieldKey ===
      field.key
    ) {
      event.preventDefault();
    }
  }}
  onDrop={(event) => {
    event.preventDefault();

    if (
      draggedMedia
        ?.fieldKey !==
      field.key
    ) {
      return;
    }

    moveMediaItem({
      fieldKey:
        field.key,
      fromIndex:
        draggedMedia.index,
      toIndex:
        index,
    });

    setDraggedMedia(null);
  }}
  onDragEnd={() =>
    setDraggedMedia(null)
  }
  className={`group relative aspect-[4/5] w-[46%] min-w-[180px] max-w-[240px] flex-none snap-start overflow-hidden rounded-xl border bg-black/30 transition sm:w-[31%] ${
    draggedMedia
      ?.fieldKey ===
      field.key &&
    draggedMedia.index ===
      index
      ? "scale-95 border-violet-300/60 opacity-60"
      : "border-white/[0.08]"
  } ${
    field.editor
      ?.supportsReorder &&
    value.length > 1
      ? "cursor-grab active:cursor-grabbing"
      : ""
  }`}
>
  {getActionMediaType(
    item
  ) === "image" &&
  getActionMediaPreviewUrl(
    item
  ) ? (
    <img
      src={
        getActionMediaPreviewUrl(
          item
        )
      }
      alt={
        item.name ||
        "Selected image"
      }
      loading={
        index < 3
          ? "eager"
          : "lazy"
      }
      className="h-full w-full bg-black object-contain"
      onError={(event) => {
        const fallbackUrl =
          getActionMediaUrl(
            item
          );

        if (
          fallbackUrl &&
          event.currentTarget
            .src !==
            fallbackUrl
        ) {
          event.currentTarget.src =
            fallbackUrl;
        }
      }}
    />
  ) : getActionMediaType(
      item
    ) === "video" &&
    getActionMediaUrl(
      item
    ) ? (
    <video
      src={
        getActionMediaUrl(
          item
        )
      }
      muted
      playsInline
      preload="metadata"
      className="h-full w-full bg-black object-contain"
    />
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
      <i className="fa-solid fa-file text-xl text-white/40"></i>

      <span className="line-clamp-2 text-[9px] font-semibold text-white/45">
        {item.name ||
          "Document"}
      </span>
    </div>
  )}

  {field.editor
    ?.showPosition && (
    <span className="absolute left-1.5 top-1.5 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-black/75 px-1.5 text-[9px] font-black text-white">
      {index + 1}
    </span>
  )}

  {getActionMediaType(
    item
  ) === "video" && (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white">
        <i className="fa-solid fa-play text-[10px]"></i>
      </span>
    </div>
  )}

  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/75 to-transparent px-2 pb-2 pt-8">
    <span className="block truncate text-[9px] font-semibold text-white/85">
      {item.name ||
        item.type ||
        "Media"}
    </span>

    <span className="mt-0.5 block text-[8px] font-semibold uppercase text-white/40">
      {item.type ||
        "media"}
    </span>
  </div>

  <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-100 transition">
    {field.editor
      ?.supportsReplace && (
      <button
        type="button"
        disabled={disabled}
        onClick={() =>
          requestMediaReplacement({
            field,
            index,
          })
        }
        title="Replace media"
        className="flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white/70 transition hover:bg-violet-500 hover:text-white"
      >
        <i className="fa-solid fa-rotate text-[8px]"></i>
      </button>
    )}

    <button
      type="button"
      disabled={disabled}
      onClick={() =>
        updateValue(
          field.key,
          value.filter(
            (
              _,
              itemIndex
            ) =>
              itemIndex !==
              index
          )
        )
      }
      title="Remove media"
      className="flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white/70 transition hover:bg-red-500 hover:text-white"
    >
      <i className="fa-solid fa-xmark text-[9px]"></i>
    </button>
  </div>

  {field.editor
    ?.supportsReorder &&
    value.length > 1 && (
    <div className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-0 transition group-hover:opacity-100">
      <button
        type="button"
        disabled={
          disabled ||
          index === 0
        }
        onClick={() =>
          moveMediaItem({
            fieldKey:
              field.key,
            fromIndex:
              index,
            toIndex:
              index - 1,
          })
        }
        title="Move earlier"
        className="flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white/70 disabled:opacity-30"
      >
        <i className="fa-solid fa-arrow-left text-[8px]"></i>
      </button>

      <button
        type="button"
        disabled={
          disabled ||
          index ===
            value.length - 1
        }
        onClick={() =>
          moveMediaItem({
            fieldKey:
              field.key,
            fromIndex:
              index,
            toIndex:
              index + 1,
          })
        }
        title="Move later"
        className="flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white/70 disabled:opacity-30"
      >
        <i className="fa-solid fa-arrow-right text-[8px]"></i>
      </button>
    </div>
  )}
</div>
                            );
                          }
                        )}
                      </div>
                    )}

                  {uploadState.error &&
                    uploadState.fieldKey ===
                      field.key && (
                      <p className="mb-0 mt-2 text-xs text-red-200/75">
                        {
                          uploadState.error
                        }
                      </p>
                    )}
                </div>
              )}


{field.control ===
"searchable_select" && (
<SearchableSelectEditor
field={field}
value={
value || ""
}
options={
remoteOptions[
field.key
] || []
}
loading={
remoteLoading[
field.key
] === true
}
disabled={disabled}
onChange={(nextValue) =>
updateValue(
field.key,
nextValue
)
}
/>
)}

              {field.control ===
                "remote_select" && (
                <select
                  value={
                    value?.id ||
                    value ||
                    ""
                  }
                  disabled={
                    disabled ||
                    remoteLoading[
                      field.key
                    ]
                  }
                  onChange={(event) => {
                    const options =
                      remoteOptions[
                        field.key
                      ] || [];

                    const selected =
                      options.find(
                        (option) =>
                          String(
                            option[
                              field
                                .dataSource
                                ?.valueField ||
                                "id"
                            ]
                          ) ===
                          event.target.value
                      ) || null;

                    updateValue(
                      field.key,
                      selected
                    );
                  }}
                  className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none"
                >
                  <option value="">
                    {remoteLoading[
                      field.key
                    ]
                      ? "Loading..."
                      : `Select ${field.label}`}
                  </option>

                  {(
                    remoteOptions[
                      field.key
                    ] || []
                  ).map((option) => (
                    <option
                      key={
                        option[
                          field
                            .dataSource
                            ?.valueField ||
                            "id"
                        ]
                      }
                      value={
                        option[
                          field
                            .dataSource
                            ?.valueField ||
                            "id"
                        ]
                      }
                    >
                      {
                        option[
                          field
                            .dataSource
                            ?.labelField ||
                            "name"
                        ]
                      }
                    </option>
                  ))}
                </select>
              )}

              {[
  "tags_input",
  "tags_editor",
].includes(
  field.control
) && (
  <TagsEditor
    value={
      Array.isArray(value)
        ? value
        : []
    }
    disabled={disabled}
    onChange={(nextTags) =>
      updateValue(
        field.key,
        nextTags
      )
    }
  />
)}

{field.control ===
"select" && (
                <select
  value={
    field.valueType ===
      "boolean"
      ? value === true
        ? "true"
        : value === false
        ? "false"
        : ""
      : value || ""
  }
  disabled={disabled}
  onChange={(event) => {
    const rawValue =
      event.target.value;

    updateValue(
field.key,
field.valueType ===
"boolean"
? rawValue === "true"
? true
: rawValue === "false"
? false
: ""
: field.valueType ===
"number"
? rawValue === ""
? ""
: Number(rawValue)
: rawValue
);
  }}
  className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none"
>
                  <option value="">
                    Select an option
                  </option>

                  {(
                    field.options || []
                  ).map((option) => (
                    <option
                      key={
                        option.value
                      }
                      value={
  field.valueType ===
    "boolean"
    ? String(
        option.value
      )
    : option.value
}
>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}

              {field.control ===
"checkbox" && (
<label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-white/60">
<input
type="checkbox"
checked={Boolean(value)}
disabled={
disabled ||
(
isColorGradeAction &&
field.key ===
"appColorGradeUseBrandKit" &&
brandKitColorsLoading
)
}
onChange={(event) =>
updateValue(
field.key,
event.target.checked
)
}
className="h-4 w-4 rounded border-white/20 bg-transparent"
/>

<span>
{isColorGradeAction &&
field.key ===
"appColorGradeUseBrandKit" &&
brandKitColorsLoading
? "Loading Brand Kit colors..."
: Boolean(value)
? "Enabled"
: "Disabled"}
</span>
</label>
)}

{field.control ===
"textarea" && (
                <textarea
                  rows={4}
                  value={value || ""}
                  disabled={disabled}
                  onChange={(event) =>
                    updateValue(
                      field.key,
                      event.target.value
                    )
                  }
                  placeholder="Enter the post caption"
                  className="w-full resize-y rounded-lg border border-white/[0.08] bg-[#202228] px-3 py-2 text-xs leading-relaxed text-white outline-none placeholder:text-white/25"
                />
              )}

              {[
"text",
"document_title",
].includes(
field.control
) && (
<input
  type="text"
  value={value || ""}
  disabled={
    disabled ||
    isFieldBlockedByConflict(
      field
    )
  }
  onChange={(event) =>
    updateValue(
      field.key,
      event.target.value
    )
  }
  placeholder={
    field.key === "newName"
      ? "Enter the new asset name"
      : `Enter ${String(
          field.label || "value"
        ).toLowerCase()}`
  }
  className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25 disabled:cursor-not-allowed disabled:opacity-45"
/>
)}
            </div>
          );
        })}
      </div>

      {!fields.some(
  (field) =>
    field.control ===
      "delete_post_selection" ||
    (
      field.control ===
        "account_selection" &&
      (
        !Array.isArray(
          field.options
        ) ||
        field.options.length === 0
      )
    )
) && (
<div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-3.5 py-3">
<button
type="button"
disabled={disabled}
onClick={submitForm}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i
            className={`fa-solid ${
              loading
                ? "fa-spinner fa-spin"
                : submitted
                ? "fa-check"
                : "fa-arrow-right"
            } text-[10px]`}
          ></i>

          {loading
            ? "Saving..."
            : submitted
            ? "Submitted"
            : interaction.submitLabel ||
              "Continue"}
        </button>

        {interaction.allowCancel && (
          <button
            type="button"
            disabled={disabled}
            onClick={() =>
              onCancel?.({
                actionDraftId:
                  interaction.actionDraftId,
              })
            }
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.07] px-3.5 text-xs font-bold text-white/65 disabled:opacity-40"
          >
            <i className="fa-solid fa-xmark text-[10px]"></i>
            Cancel
          </button>
        )}

        {unresolvedFields.length >
  0 && (
  <span className="text-[10px] font-medium text-amber-100/60">
    {unresolvedFields.length} required field
    {unresolvedFields.length === 1
      ? ""
      : "s"}{" "}
    remaining. Click Continue to review.
  </span>
)}
      </div>
)}
</div>
);
};

const BATCH_MEDIA_TYPES = [
{
value: "text",
label: "Text",
},
{
value: "image",
label: "Image",
},
{
value: "video",
label: "Video",
},
{
value: "carousel",
label: "Carousel",
},
{
value: "document",
label: "Document",
},
{
value: "mixed",
label: "Mixed media",
},
];

const createBatchDraftId = () => {
return `draft-${Date.now()}-${Math.random()
.toString(36)
.slice(2, 10)}`;
};

const cloneBatchValue = (
value
) => {
try {
return JSON.parse(
JSON.stringify(value)
);
} catch (_) {
return value;
}
};

const normalizeBatchProfileOption = (
option = {}
) => {
const platform =
String(
option.platform ||
option.value?.platform ||
""
)
.trim()
.toUpperCase();
const profileId =
String(
option.profileId ||
option.value?.profileId ||
""
)
.trim();

if (
!platform ||
!profileId
) {
return null;
}

return {
platform,
profileId,
connectionId:
option.connectionId ||
option.value?.connectionId ||
null,
profileName:
option.profileName ||
option.label ||
option.value?.profileName ||
profileId,
};
};

const getBatchProfileKey = (
profile = {}
) => {
const platform =
String(
profile.platform || ""
)
.trim()
.toUpperCase();
const profileId =
String(
profile.profileId || ""
)
.trim();

return platform && profileId
? `${platform}:${profileId}`
: null;
};

const normalizeBatchDraft = (
draft = {},
index = 0
) => {
const post =
draft?.post &&
typeof draft.post ===
"object" &&
!Array.isArray(
draft.post
)
? draft.post
: {};

return {
draftId:
String(
draft.draftId ||
createBatchDraftId()
),
label:
String(
draft.label ||
`Draft ${index + 1}`
),
rationale:
String(
draft.rationale || ""
),
post: {
message:
String(
post.message || ""
),
title:
post.title || null,
firstComment:
post.firstComment || null,
profiles:
Array.isArray(
post.profiles
)
? cloneBatchValue(
post.profiles
)
: [],
mediaItems:
Array.isArray(
post.mediaItems
)
? cloneBatchValue(
post.mediaItems
)
: [],
mediaType:
String(
post.mediaType ||
"text"
).toLowerCase(),
linkUrl:
post.linkUrl || null,
boardId:
post.boardId || null,
playlistId:
post.playlistId || null,
collaborators:
Array.isArray(
post.collaborators
)
? cloneBatchValue(
post.collaborators
)
: [],
tags:
Array.isArray(
post.tags
)
? cloneBatchValue(
post.tags
)
: [],
platformOptions:
post.platformOptions &&
typeof post.platformOptions ===
"object" &&
!Array.isArray(
post.platformOptions
)
? cloneBatchValue(
post.platformOptions
)
: {},
profileOverrides:
post.profileOverrides &&
typeof post.profileOverrides ===
"object" &&
!Array.isArray(
post.profileOverrides
)
? cloneBatchValue(
post.profileOverrides
)
: {},
},
};
};

const normalizeBatchUploadedMedia = (
item = {}
) => {
const mimeType =
item.type ||
item.mimeType ||
null;
const normalizedMimeType =
String(
mimeType || ""
).toLowerCase();
let mediaType =
item.mediaType ||
item.metadata
?.libraryAsset?.type ||
null;

if (!mediaType) {
if (
normalizedMimeType.startsWith(
"image/"
)
) {
mediaType = "image";
} else if (
normalizedMimeType.startsWith(
"video/"
)
) {
mediaType = "video";
} else if (
normalizedMimeType.includes(
"pdf"
) ||
normalizedMimeType.includes(
"document"
) ||
normalizedMimeType.includes(
"presentation"
)
) {
mediaType = "document";
} else {
mediaType = "other";
}
}

return {
assetId:
item.assetId ||
item.libraryAssetId ||
item.metadata
?.libraryAssetId ||
null,
url:
item.url || null,
name:
item.name ||
item.metadata
?.originalName ||
null,
type:
mediaType,
mimeType,
thumbnailUrl:
item.thumbnailUrl ||
item.metadata
?.thumbnail?.url ||
null,
altText:
item.altText ||
null,
title:
item.title ||
null,
metadata:
item.metadata &&
typeof item.metadata ===
"object"
? item.metadata
: {},
};
};

const BatchAnalysisPanel = ({
analysis,
}) => {
if (
!analysis ||
typeof analysis !==
"object" ||
Array.isArray(analysis)
) {
return null;
}

const groups = [
{
key: "observations",
label: "Observed patterns",
items:
analysis.observations,
},
{
key: "dominantPostTypes",
label: "Dominant post types",
items:
analysis.dominantPostTypes,
},
{
key: "dominantMediaTypes",
label: "Dominant media types",
items:
analysis.dominantMediaTypes,
},
{
key: "contentGaps",
label: "Content gaps",
items:
analysis.contentGaps,
},
].filter(
(group) =>
Array.isArray(group.items) &&
group.items.length > 0
);

if (
!analysis.message &&
groups.length === 0
) {
return null;
}

return (
<div className="space-y-3 border-t border-white/[0.06] bg-blue-400/[0.035] px-3.5 py-3">
<div className="flex items-start gap-3">
<span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-400/12 text-blue-100/70">
<i className="fa-solid fa-chart-line text-[10px]"></i>
</span>
<div className="min-w-0">
<p className="mb-1 text-xs font-bold text-blue-100/80">
Analysis behind these drafts
</p>
{analysis.message && (
<p className="mb-0 whitespace-pre-wrap text-xs leading-relaxed text-white/50">
{analysis.message}
</p>
)}
</div>
</div>

{groups.length > 0 && (
<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
{groups.map(
(group) => (
<div
key={group.key}
className="rounded-xl border border-white/[0.06] bg-black/[0.12] p-3"
>
<p className="mb-2 text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
{group.label}
</p>
<ul className="mb-0 space-y-1.5 pl-4 text-[10px] leading-relaxed text-white/50">
{group.items.map(
(item, index) => (
<li
key={`${group.key}-${index}`}
className="list-disc"
>
{String(item)}
</li>
)
)}
</ul>
</div>
)
)}
</div>
)}
</div>
);
};

const DraftBatchEditor = ({
interaction,
enabled,
loading,
uploadMedia,
onSubmit,
onCancel,
}) => {
const fields =
Array.isArray(
interaction?.fields
)
? interaction.fields
: [];
const batchField =
fields.find(
(field) =>
field.control ===
"draft_batch"
) ||
null;
const strategyField =
fields.find(
(field) =>
field.patchPath ===
"strategySummary"
) ||
null;
const minimum =
Math.max(
Number(
batchField?.constraints
?.minimum ||
2
),
2
);
const maximum =
Math.min(
Math.max(
Number(
batchField?.constraints
?.maximum ||
20
),
minimum
),
20
);
const connectedProfiles =
Array.isArray(
batchField
?.connectedProfiles
)
? batchField.connectedProfiles
.map(
normalizeBatchProfileOption
)
.filter(Boolean)
: [];

const [
drafts,
setDrafts,
] = useState(() => {
return (
Array.isArray(
batchField?.value
)
? batchField.value
: []
).map(
normalizeBatchDraft
);
});
const [
strategySummary,
setStrategySummary,
] = useState(
String(
strategyField?.value ||
interaction?.analysis
?.strategySummary ||
""
)
);
const [
submitted,
setSubmitted,
] = useState(false);
const [
attemptedSubmit,
setAttemptedSubmit,
] = useState(false);
const [
expandedDraftId,
setExpandedDraftId,
] = useState(
drafts[0]?.draftId ||
null
);
const [
uploadState,
setUploadState,
] = useState({
draftId: null,
loading: false,
progress: 0,
error: null,
});
const [
platformOptionText,
setPlatformOptionText,
] = useState(() => {
return drafts.reduce(
(result, draft) => {
result[draft.draftId] =
Object.keys(
draft.post
.platformOptions ||
{}
).length > 0
? JSON.stringify(
draft.post
.platformOptions,
null,
2
)
: "";
return result;
},
{}
);
});
const [
platformOptionErrors,
setPlatformOptionErrors,
] = useState({});
const mediaInputRef =
useRef(null);
const activeUploadDraftIdRef =
useRef(null);

useEffect(() => {
const nextDrafts = (
Array.isArray(
batchField?.value
)
? batchField.value
: []
).map(
normalizeBatchDraft
);

setDrafts(
nextDrafts
);
setStrategySummary(
String(
strategyField?.value ||
interaction?.analysis
?.strategySummary ||
""
)
);
setSubmitted(false);
setAttemptedSubmit(false);
setExpandedDraftId(
nextDrafts[0]?.draftId ||
null
);
setPlatformOptionText(
nextDrafts.reduce(
(result, draft) => {
result[draft.draftId] =
Object.keys(
draft.post
.platformOptions ||
{}
).length > 0
? JSON.stringify(
draft.post
.platformOptions,
null,
2
)
: "";
return result;
},
{}
)
);
setPlatformOptionErrors({});
}, [
interaction?.actionDraftId,
interaction?.revision,
]);

const disabled =
!enabled ||
loading ||
submitted ||
uploadState.loading;

const updateDraft = (
draftId,
updater
) => {
if (disabled) {
return;
}

setDrafts(
(currentDrafts) =>
currentDrafts.map(
(draft) =>
draft.draftId ===
draftId
? updater(
cloneBatchValue(
draft
)
)
: draft
)
);
setAttemptedSubmit(false);
};

const updateDraftPost = (
draftId,
postPatch
) => {
updateDraft(
draftId,
(draft) => ({
...draft,
post: {
...draft.post,
...postPatch,
},
})
);
};

const addDraft = () => {
if (
disabled ||
drafts.length >=
maximum
) {
return;
}

const nextDraft =
normalizeBatchDraft(
{
draftId:
createBatchDraftId(),
label:
`Draft ${drafts.length + 1}`,
rationale: "",
post: {
message: "",
profiles: [],
mediaItems: [],
mediaType:
"text",
platformOptions: {},
profileOverrides: {},
},
},
drafts.length
);

setDrafts(
(current) => [
...current,
nextDraft,
]
);
setPlatformOptionText(
(current) => ({
...current,
[nextDraft.draftId]:
"",
})
);
setExpandedDraftId(
nextDraft.draftId
);
setAttemptedSubmit(false);
};

const removeDraft = (
draftId
) => {
if (disabled) {
return;
}

const nextDrafts =
drafts.filter(
(draft) =>
draft.draftId !==
draftId
);

setDrafts(
nextDrafts
);
setPlatformOptionText(
(current) => {
const next = {
...current,
};
delete next[draftId];
return next;
}
);
setPlatformOptionErrors(
(current) => {
const next = {
...current,
};
delete next[draftId];
return next;
}
);

if (
expandedDraftId ===
draftId
) {
setExpandedDraftId(
nextDrafts[0]
?.draftId ||
null
);
}

setAttemptedSubmit(false);
};

const moveDraft = (
draftId,
direction
) => {
if (disabled) {
return;
}

setDrafts(
(current) => {
const index =
current.findIndex(
(draft) =>
draft.draftId ===
draftId
);
const targetIndex =
index + direction;

if (
index < 0 ||
targetIndex < 0 ||
targetIndex >=
current.length
) {
return current;
}

const next = [
...current,
];
const [
moved,
] = next.splice(
index,
1
);
next.splice(
targetIndex,
0,
moved
);
return next;
}
);
};

const toggleProfile = (
draftId,
profile
) => {
const profileKey =
getBatchProfileKey(
profile
);

if (!profileKey) {
return;
}

updateDraft(
draftId,
(draft) => {
const currentProfiles =
Array.isArray(
draft.post.profiles
)
? draft.post.profiles
: [];
const selected =
currentProfiles.some(
(currentProfile) =>
getBatchProfileKey(
currentProfile
) ===
profileKey
);

return {
...draft,
post: {
...draft.post,
profiles:
selected
? currentProfiles.filter(
(currentProfile) =>
getBatchProfileKey(
currentProfile
) !==
profileKey
)
: [
...currentProfiles,
profile,
],
},
};
}
);
};

const removeMediaItem = (
draftId,
mediaIndex
) => {
updateDraft(
draftId,
(draft) => ({
...draft,
post: {
...draft.post,
mediaItems:
draft.post.mediaItems.filter(
(
_,
index
) =>
index !==
mediaIndex
),
},
})
);
};

const requestMediaUpload = (
draftId
) => {
if (
disabled ||
!mediaInputRef.current
) {
return;
}

activeUploadDraftIdRef.current =
draftId;
mediaInputRef.current.value =
"";
mediaInputRef.current.click();
};

const handleMediaUpload = async (
event
) => {
const files =
Array.from(
event.target.files ||
[]
);
event.target.value = "";

const draftId =
activeUploadDraftIdRef.current;

if (
disabled ||
!draftId ||
files.length === 0
) {
return;
}

setUploadState({
draftId,
loading: true,
progress: 0,
error: null,
});

try {
const uploaded =
await uploadMedia?.({
files,
onProgress: ({
progress,
}) => {
setUploadState(
(current) => ({
...current,
progress,
})
);
},
});

const normalized =
Array.isArray(uploaded)
? uploaded
.map(
normalizeBatchUploadedMedia
)
.filter(
(item) =>
item.assetId ||
item.url
)
: [];

if (
normalized.length === 0
) {
throw new Error(
"No valid media was uploaded."
);
}

updateDraft(
draftId,
(draft) => {
const merged = [];
const seen =
new Set();

[
...draft.post
.mediaItems,
...normalized,
].forEach((item) => {
const key =
item.assetId
? `asset:${item.assetId}`
: item.url
? `url:${item.url}`
: null;

if (
!key ||
seen.has(key)
) {
return;
}

seen.add(key);
merged.push(item);
});

return {
...draft,
post: {
...draft.post,
mediaItems:
merged,
},
};
}
);

setUploadState({
draftId,
loading: false,
progress: 100,
error: null,
});
} catch (error) {
setUploadState({
draftId,
loading: false,
progress: 0,
error:
error.message ||
"Media upload failed.",
});
} finally {
activeUploadDraftIdRef.current =
null;
}
};

const updatePlatformOptions = (
draftId,
text
) => {
setPlatformOptionText(
(current) => ({
...current,
[draftId]:
text,
})
);

if (!text.trim()) {
setPlatformOptionErrors(
(current) => ({
...current,
[draftId]:
null,
})
);
updateDraftPost(
draftId,
{
platformOptions: {},
}
);
return;
}

try {
const parsed =
JSON.parse(text);

if (
!parsed ||
typeof parsed !==
"object" ||
Array.isArray(parsed)
) {
throw new Error(
"Platform options must be a JSON object."
);
}

setPlatformOptionErrors(
(current) => ({
...current,
[draftId]:
null,
})
);
updateDraftPost(
draftId,
{
platformOptions:
parsed,
}
);
} catch (error) {
setPlatformOptionErrors(
(current) => ({
...current,
[draftId]:
error.message ||
"Enter valid JSON.",
})
);
}
};

const draftErrors =
drafts.reduce(
(result, draft, index) => {
const errors = [];
const caption =
String(
draft.post.message ||
""
).trim();
const mediaItems =
Array.isArray(
draft.post.mediaItems
)
? draft.post.mediaItems
: [];

if (
!String(
draft.label || ""
).trim()
) {
errors.push(
"Add a label."
);
}

if (
!caption &&
mediaItems.length ===
0
) {
errors.push(
"Add a caption or at least one media item."
);
}

if (
platformOptionErrors[
draft.draftId
]
) {
errors.push(
"Fix the platform-options JSON."
);
}

result[
draft.draftId
] = {
index,
errors,
};

return result;
},
{}
);

const batchErrors = [];

if (
drafts.length <
minimum
) {
batchErrors.push(
`Add at least ${minimum} drafts.`
);
}

if (
drafts.length >
maximum
) {
batchErrors.push(
`Keep the batch at ${maximum} drafts or fewer.`
);
}

const invalidDrafts =
Object.values(
draftErrors
).filter(
(item) =>
item.errors.length > 0
);

const submitBatch = async () => {
if (disabled) {
return;
}

if (
batchErrors.length > 0 ||
invalidDrafts.length > 0
) {
setAttemptedSubmit(true);

const firstInvalid =
drafts.find(
(draft) =>
draftErrors[
draft.draftId
]?.errors.length >
0
);

if (firstInvalid) {
setExpandedDraftId(
firstInvalid.draftId
);
}

return;
}

setSubmitted(true);

try {
await onSubmit?.({
actionDraftId:
interaction.actionDraftId,
revision:
interaction.revision,
patch: {
drafts:
cloneBatchValue(
drafts
),
strategySummary:
String(
strategySummary || ""
).trim(),
},
metadata: {
presentationMode:
"draft_batch_review",
draftCount:
drafts.length,
},
});
} catch (error) {
setSubmitted(false);
}
};

return (
<div className="mt-3 w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-violet-400/20 bg-violet-400/[0.06]">
<input
ref={mediaInputRef}
type="file"
multiple
className="hidden"
accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
onChange={
handleMediaUpload
}
/>

<div className="flex flex-wrap items-start justify-between gap-3 px-3.5 py-3">
<div className="min-w-0 flex-1">
<h4 className="mb-1 text-sm font-bold text-white/90">
{interaction.title ||
"Review generated drafts"}
</h4>
<p className="mb-0 text-xs leading-relaxed text-white/50">
{interaction.description ||
"Edit every draft before continuing to confirmation."}
</p>
</div>
<span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-violet-400/15 px-2.5 text-[10px] font-bold text-violet-100">
<i className="fa-solid fa-layer-group text-[9px]"></i>
{drafts.length} / {maximum} drafts
</span>
</div>

<BatchAnalysisPanel
analysis={
interaction.analysis
}
/>

<div className="space-y-3 border-t border-white/[0.06] px-3.5 py-3">
<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Strategy summary
</label>
<textarea
rows={3}
value={
strategySummary
}
disabled={disabled}
onChange={(event) =>
setStrategySummary(
event.target.value
)
}
placeholder="Describe the strategy shared by these drafts."
className="w-full resize-y rounded-lg border border-white/[0.08] bg-[#202228] px-3 py-2 text-xs leading-relaxed text-white outline-none placeholder:text-white/25 disabled:opacity-45"
/>
</div>

<div className="space-y-3">
{drafts.map(
(draft, index) => {
const expanded =
expandedDraftId ===
draft.draftId;
const errors =
draftErrors[
draft.draftId
]?.errors ||
[];
const selectedProfileKeys =
new Set(
draft.post.profiles
.map(
getBatchProfileKey
)
.filter(Boolean)
);

return (
<article
key={
draft.draftId
}
className={`overflow-hidden rounded-xl border bg-black/[0.13] ${
attemptedSubmit &&
errors.length > 0
? "border-red-300/30"
: "border-white/[0.07]"
}`}
>
<div className="flex items-center gap-2 px-3 py-3">
<button
type="button"
onClick={() =>
setExpandedDraftId(
expanded
? null
: draft.draftId
)
}
className="flex min-w-0 flex-1 items-center gap-3 text-left"
aria-expanded={
expanded
}
>
<span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-violet-400/12 text-[10px] font-black text-violet-100">
{index + 1}
</span>
<span className="min-w-0 flex-1">
<span className="block truncate text-xs font-bold text-white/80">
{draft.label ||
`Draft ${index + 1}`}
</span>
<span className="mt-0.5 block truncate text-[9px] text-white/32">
{draft.post.mediaType ||
"text"}
{" · "}
{draft.post.profiles
.length} destination
{draft.post.profiles
.length === 1
? ""
: "s"}
{" · "}
{draft.post.mediaItems
.length} media
</span>
</span>
{attemptedSubmit &&
errors.length > 0 && (
<span className="rounded-full bg-red-400/12 px-2 py-1 text-[9px] font-bold text-red-100/70">
Needs attention
</span>
)}
<i
className={`fa-solid fa-chevron-down flex-shrink-0 text-[9px] text-white/30 transition-transform ${
expanded
? "rotate-180"
: ""
}`}
></i>
</button>

<div className="flex flex-shrink-0 items-center gap-1">
<button
type="button"
disabled={
disabled ||
index === 0
}
onClick={() =>
moveDraft(
draft.draftId,
-1
)
}
title="Move draft earlier"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.055] text-white/45 disabled:opacity-25"
>
<i className="fa-solid fa-arrow-up text-[8px]"></i>
</button>
<button
type="button"
disabled={
disabled ||
index ===
drafts.length - 1
}
onClick={() =>
moveDraft(
draft.draftId,
1
)
}
title="Move draft later"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.055] text-white/45 disabled:opacity-25"
>
<i className="fa-solid fa-arrow-down text-[8px]"></i>
</button>
<button
type="button"
disabled={
disabled
}
onClick={() =>
removeDraft(
draft.draftId
)
}
title="Remove draft"
className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-400/10 text-red-100/55 transition hover:bg-red-400/20 hover:text-red-100 disabled:opacity-25"
>
<i className="fa-solid fa-trash text-[8px]"></i>
</button>
</div>
</div>

{expanded && (
<div className="space-y-4 border-t border-white/[0.06] p-3">
{attemptedSubmit &&
errors.length > 0 && (
<div className="rounded-lg border border-red-400/15 bg-red-400/[0.055] px-3 py-2">
{errors.map(
(error) => (
<p
key={error}
className="mb-1 last:mb-0 text-[10px] font-semibold text-red-100/75"
>
{error}
</p>
)
)}
</div>
)}

<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Label
</label>
<input
type="text"
value={
draft.label
}
disabled={disabled}
onChange={(event) =>
updateDraft(
draft.draftId,
(current) => ({
...current,
label:
event.target.value,
})
)
}
placeholder="Draft label"
className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none placeholder:text-white/25 disabled:opacity-45"
/>
</div>

<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Format
</label>
<select
value={
draft.post.mediaType
}
disabled={disabled}
onChange={(event) =>
updateDraftPost(
draft.draftId,
{
mediaType:
event.target.value,
}
)
}
className="h-10 w-full rounded-lg border border-white/[0.08] bg-[#202228] px-3 text-xs text-white outline-none disabled:opacity-45"
>
{BATCH_MEDIA_TYPES.map(
(option) => (
<option
key={option.value}
value={option.value}
>
{option.label}
</option>
)
)}
</select>
</div>
</div>

<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Rationale
</label>
<textarea
rows={3}
value={
draft.rationale
}
disabled={disabled}
onChange={(event) =>
updateDraft(
draft.draftId,
(current) => ({
...current,
rationale:
event.target.value,
})
)
}
placeholder="Explain why this draft supports the strategy."
className="w-full resize-y rounded-lg border border-white/[0.08] bg-[#202228] px-3 py-2 text-xs leading-relaxed text-white outline-none placeholder:text-white/25 disabled:opacity-45"
/>
</div>

<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Caption
</label>
<textarea
rows={6}
value={
draft.post.message
}
disabled={disabled}
onChange={(event) =>
updateDraftPost(
draft.draftId,
{
message:
event.target.value,
}
)
}
placeholder="Enter the complete editable caption."
className="w-full resize-y rounded-lg border border-white/[0.08] bg-[#202228] px-3 py-2 text-xs leading-relaxed text-white outline-none placeholder:text-white/25 disabled:opacity-45"
/>
</div>

<div>
<div className="mb-2 flex items-center justify-between gap-3">
<div>
<p className="mb-0 text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Destinations
</p>
<p className="mb-0 mt-0.5 text-[10px] text-white/30">
Leave empty to keep this draft destination-neutral.
</p>
</div>
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-bold text-white/40">
{draft.post.profiles
.length} selected
</span>
</div>

{connectedProfiles.length > 0 ? (
<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
{connectedProfiles.map(
(profile) => {
const key =
getBatchProfileKey(
profile
);
const selected =
selectedProfileKeys.has(
key
);
const platformDisplay =
getConfirmationPlatformDisplay(
profile.platform
);

return (
<button
key={key}
type="button"
disabled={disabled}
onClick={() =>
toggleProfile(
draft.draftId,
profile
)
}
className={`flex min-w-0 items-center gap-3 rounded-lg border p-2.5 text-left transition ${
selected
? "border-violet-300/35 bg-violet-400/15"
: "border-white/[0.07] bg-white/[0.025] hover:bg-white/[0.05]"
}`}
>
<span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055]">
<i
className={`${platformDisplay.icon} ${platformDisplay.color} text-[11px]`}
></i>
</span>
<span className="min-w-0 flex-1">
<span className="block truncate text-[11px] font-bold text-white/70">
{profile.profileName}
</span>
<span className="block truncate text-[9px] uppercase text-white/30">
{platformDisplay.label}
</span>
</span>
<i
className={`fa-solid ${
selected
? "fa-circle-check text-violet-200"
: "fa-circle text-white/20"
} text-[11px]`}
></i>
</button>
);
}
)}
</div>
) : (
<p className="mb-0 rounded-lg border border-white/[0.06] bg-white/[0.025] px-3 py-3 text-[10px] text-white/35">
No connected destination options were supplied.
</p>
)}
</div>

<div>
<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
<div>
<p className="mb-0 text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Media
</p>
<p className="mb-0 mt-0.5 text-[10px] text-white/30">
Upload media independently for this draft.
</p>
</div>
<button
type="button"
disabled={disabled}
onClick={() =>
requestMediaUpload(
draft.draftId
)
}
className="inline-flex h-8 items-center gap-2 rounded-lg bg-white px-3 text-[10px] font-bold text-black disabled:opacity-40"
>
<i
className={`fa-solid ${
uploadState.loading &&
uploadState.draftId ===
draft.draftId
? "fa-spinner fa-spin"
: "fa-paperclip"
} text-[9px]`}
></i>
{uploadState.loading &&
uploadState.draftId ===
draft.draftId
? `Uploading ${uploadState.progress}%`
: "Add media"}
</button>
</div>

{uploadState.error &&
uploadState.draftId ===
draft.draftId && (
<p className="mb-2 text-[10px] font-semibold text-red-100/70">
{uploadState.error}
</p>
)}

{draft.post.mediaItems
.length > 0 ? (
<div className="flex flex-wrap gap-2">
{draft.post.mediaItems.map(
(item, mediaIndex) => {
const previewUrl =
item.thumbnailUrl ||
item.url ||
null;

return (
<div
key={
item.assetId ||
item.url ||
mediaIndex
}
className="relative h-28 w-32 overflow-hidden rounded-xl border border-white/[0.08] bg-black/30"
>
{item.type ===
"image" &&
previewUrl ? (
<img
src={previewUrl}
alt={
item.name ||
"Draft media"
}
className="h-full w-full object-cover"
/>
) : item.type ===
"video" &&
previewUrl ? (
<video
src={previewUrl}
muted
playsInline
preload="metadata"
className="h-full w-full object-cover"
/>
) : (
<div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
<i className="fa-solid fa-file text-lg text-white/35"></i>
<span className="line-clamp-2 text-[8px] font-semibold text-white/45">
{item.name ||
"Media"}
</span>
</div>
)}

<button
type="button"
disabled={disabled}
onClick={() =>
removeMediaItem(
draft.draftId,
mediaIndex
)
}
title="Remove media"
className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white/70 transition hover:bg-red-500 hover:text-white"
>
<i className="fa-solid fa-xmark text-[9px]"></i>
</button>

<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent px-2 pb-2 pt-6">
<span className="block truncate text-[8px] font-semibold text-white/75">
{item.name ||
item.type ||
"Media"}
</span>
</div>
</div>
);
}
)}
</div>
) : (
<p className="mb-0 rounded-lg border border-dashed border-white/[0.08] px-3 py-3 text-[10px] text-white/30">
No media attached to this draft.
</p>
)}
</div>

<div>
<label className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Platform options
</label>
<p className="mb-2 text-[10px] leading-relaxed text-white/30">
Enter a JSON object only when this draft needs platform-specific settings.
</p>
<textarea
rows={5}
value={
platformOptionText[
draft.draftId
] || ""
}
disabled={disabled}
onChange={(event) =>
updatePlatformOptions(
draft.draftId,
event.target.value
)
}
placeholder='{"INSTAGRAM": {}, "YOUTUBE": {}}'
className={`w-full resize-y rounded-lg border bg-[#202228] px-3 py-2 font-mono text-[10px] leading-relaxed text-white outline-none placeholder:text-white/20 disabled:opacity-45 ${
platformOptionErrors[
draft.draftId
]
? "border-red-300/30"
: "border-white/[0.08]"
}`}
></textarea>
{platformOptionErrors[
draft.draftId
] && (
<p className="mb-0 mt-1.5 text-[10px] font-semibold text-red-100/70">
{platformOptionErrors[
draft.draftId
]}
</p>
)}
</div>
</div>
)}
</article>
);
}
)}
</div>

<button
type="button"
disabled={
disabled ||
drafts.length >=
maximum
}
onClick={
addDraft
}
className="inline-flex h-9 items-center gap-2 rounded-lg border border-dashed border-violet-300/25 bg-violet-400/[0.07] px-3.5 text-xs font-bold text-violet-100 transition hover:bg-violet-400/[0.14] disabled:cursor-not-allowed disabled:opacity-35"
>
<i className="fa-solid fa-plus text-[9px]"></i>
Add draft
</button>

{attemptedSubmit &&
batchErrors.length > 0 && (
<div className="rounded-lg border border-red-400/15 bg-red-400/[0.055] px-3 py-2">
{batchErrors.map(
(error) => (
<p
key={error}
className="mb-1 last:mb-0 text-[10px] font-semibold text-red-100/75"
>
{error}
</p>
)
)}
</div>
)}
</div>

<div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-3.5 py-3">
<button
type="button"
disabled={disabled}
onClick={
submitBatch
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black disabled:cursor-not-allowed disabled:opacity-40"
>
<i
className={`fa-solid ${
loading
? "fa-spinner fa-spin"
: submitted
? "fa-check"
: "fa-arrow-right"
} text-[10px]`}
></i>
{loading
? "Saving..."
: submitted
? "Submitted"
: interaction.submitLabel ||
"Continue to confirmation"}
</button>

{interaction.allowCancel && (
<button
type="button"
disabled={disabled}
onClick={() =>
onCancel?.({
actionDraftId:
interaction.actionDraftId,
})
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.07] px-3.5 text-xs font-bold text-white/65 disabled:opacity-40"
>
<i className="fa-solid fa-xmark text-[10px]"></i>
Cancel
</button>
)}

<span className="text-[10px] font-medium text-white/35">
{invalidDrafts.length > 0
? `${invalidDrafts.length} draft${
invalidDrafts.length === 1
? ""
: "s"
} need attention.`
: `${drafts.length} draft${
drafts.length === 1
? ""
: "s"
} ready for review.`}
</span>
</div>
</div>
);
};

const ActionDraftForm = (
props
) => {
const fields =
Array.isArray(
props.interaction?.fields
)
? props.interaction.fields
: [];
const hasDraftBatch =
fields.some(
(field) =>
field.control ===
"draft_batch"
);

if (hasDraftBatch) {
return (
<DraftBatchEditor
{...props}
/>
);
}

return (
<GenericActionDraftForm
{...props}
/>
);
};

const SourceChip = ({ source, className }) => {
  const [open, setOpen] = useState(false);

  const label = getReferenceLabel(source);
  const title = getReferenceTitle(source);
  const subtitle = getReferenceSubtitle(source);
  const description = getReferenceDescription(source);

  return (
    <span className="relative inline-flex min-w-0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`group flex h-8 max-w-[240px] items-center gap-2 rounded-lg bg-white/[0.035] px-2.5 text-left transition hover:bg-white/[0.07] ${className}`}
        title={title}
        aria-expanded={open}
      >
        <span className="flex h-5 min-w-[28px] flex-shrink-0 items-center justify-center rounded-md bg-white/[0.055] px-1.5 text-[9px] font-bold text-white/35">
          {label}
        </span>

        <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-white/55 group-hover:text-white/80">
          {title}
        </span>

        <i
          className={`fa-solid fa-chevron-down flex-shrink-0 text-[8px] text-white/20 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open && (
        <div className="absolute left-0 top-9 z-50 w-72 rounded-lg border border-white/[0.06] bg-[#181a1f] p-3 text-left shadow-[0_18px_50px_rgba(0,0,0,0.5)]">
          <div className="mb-2 flex items-start gap-2">
            <span className="flex h-5 min-w-[28px] flex-shrink-0 items-center justify-center rounded-md bg-white/[0.06] px-1.5 text-[9px] font-bold text-white/40">
              {label}
            </span>

            <div className="min-w-0">
              <p className="mb-0 break-words text-xs font-semibold text-white/85">
                {title}
              </p>
              <p className="mb-0 mt-0.5 text-[10px] font-medium text-white/35">
                {subtitle}
              </p>
            </div>
          </div>

          {source.invocationId && (
            <p className="mb-1 text-[10px] text-white/25">
              Invocation {String(source.invocationId).slice(-8)}
            </p>
          )}

          {description && (
            <p className="mb-0 text-[11px] leading-relaxed text-white/40">
              {description}
            </p>
          )}
        </div>
      )}
    </span>
  );
};

const AccountConnectCard = ({
  interaction,
  enabled,
  onConnect,
}) => {
  const platforms =
    Array.isArray(
      interaction?.platforms
    )
      ? interaction.platforms
      : [];

  if (
    interaction?.type !==
      "account_connect" ||
    platforms.length === 0
  ) {
    return null;
  }

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-blue-400/20 bg-blue-400/[0.06]">
      <div className="px-3.5 py-3">
        <div className="mb-1 flex items-center gap-2">
          <i className="fa-solid fa-link text-xs text-blue-200/70"></i>
          <h4 className="mb-0 text-sm font-bold text-white/90">
            {interaction.title ||
              "Connect a social profile"}
          </h4>
        </div>

        <p className="mb-0 text-xs leading-relaxed text-white/50">
          {interaction.description ||
            "Choose a platform to continue through the existing secure connection flow."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-white/[0.06] px-3.5 py-3">
        {platforms.map(
          (
            platformItem,
            index
          ) => {
            const platform =
              typeof platformItem ===
                "string"
                ? platformItem
                : platformItem.platform;

            const label =
              typeof platformItem ===
                "string"
                ? platformItem.replace(
                    /_/g,
                    " "
                  )
                : platformItem.label ||
                  platform;

            return (
              <button
                key={
                  platform ||
                  index
                }
                type="button"
                disabled={!enabled}
                onClick={() =>
  onConnect?.({
    selectedPlatform:
      platform,
    interaction: {
      ...interaction,
      requestedPlatforms:
        Array.isArray(
          interaction
            .requestedPlatforms
        ) &&
        interaction
          .requestedPlatforms
          .length > 0
          ? interaction
              .requestedPlatforms
          : platforms,
      platforms: [
        platformItem,
      ],
    },
  })
}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i
  className={`${
    getConfirmationPlatformDisplay(
      platform
    ).icon
  } text-[11px] ${
    getConfirmationPlatformDisplay(
      platform
    ).color
  }`}
></i>
Connect {label}
              </button>
            );
          }
        )}
      </div>
    </div>
  );
};

const AccountConnectResultCard = ({
  result,
}) => {
  const accounts =
    Array.isArray(
      result?.accounts
    )
      ? result.accounts
      : [];

  if (
    result?.operation !==
    "account_connect"
  ) {
    return null;
  }

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06]">
      <div className="flex items-center gap-2 px-3.5 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-200">
          <i className="fa-solid fa-check text-[10px]"></i>
        </span>

        <div>
          <p className="mb-0 text-sm font-bold text-emerald-100">
            Account connection successful
          </p>
          <p className="mb-0 text-[10px] text-white/40">
            {accounts.length} profile
            {accounts.length === 1
              ? ""
              : "s"}{" "}
            connected
          </p>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="grid grid-cols-1 gap-2 border-t border-white/[0.06] px-3.5 py-3 sm:grid-cols-2">
          {accounts.map(
            (account, index) => (
              <div
                key={`${account.platform}:${account.profileId}:${index}`}
                className="rounded-xl border border-white/[0.07] bg-black/[0.14] px-3 py-3"
              >
                <ConnectedAccountIdentity
                  account={account}
                />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};


const PublishedPostMediaPreview = ({
post,
}) => {
const media =
Array.isArray(
post?.media
)
? post.media
: [];

const [
activeIndex,
setActiveIndex,
] = useState(0);

useEffect(() => {
setActiveIndex(0);
}, [
post?.id,
]);

if (
media.length === 0
) {
return (
<div className="flex aspect-[16/10] items-center justify-center bg-black/25 text-white/25">
<i className="fa-regular fa-file-lines text-2xl"></i>
</div>
);
}

const normalizedIndex =
Math.min(
Math.max(
activeIndex,
0
),
media.length - 1
);

const activeMedia =
media[
normalizedIndex
];

const isVideo =
activeMedia?.type ===
"video";

return (
<div className="relative overflow-hidden bg-black/35">
{isVideo ? (
<video
src={
activeMedia.url
}
controls
playsInline
preload="metadata"
className="aspect-[16/10] w-full bg-black object-contain"
/>
) : (
<a
href={
activeMedia.url
}
target="_blank"
rel="noreferrer"
className="block"
>
<img
src={
activeMedia.url
}
alt={
activeMedia.name ||
"Published post media"
}
loading="lazy"
className="aspect-[16/10] w-full bg-black object-cover"
/>
</a>
)}

{media.length > 1 && (
<>
<button
type="button"
onClick={() =>
setActiveIndex(
(
normalizedIndex -
1 +
media.length
) %
media.length
)
}
aria-label="Show previous media"
className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/75 transition hover:bg-black/90 hover:text-white"
>
<i className="fa-solid fa-chevron-left text-[9px]"></i>
</button>

<button
type="button"
onClick={() =>
setActiveIndex(
(
normalizedIndex +
1
) %
media.length
)
}
aria-label="Show next media"
className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/75 transition hover:bg-black/90 hover:text-white"
>
<i className="fa-solid fa-chevron-right text-[9px]"></i>
</button>

<span className="absolute right-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
{normalizedIndex + 1} /{" "}
{media.length}
</span>
</>
)}

{isVideo && (
<span className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/75 px-2 py-1 text-[9px] font-bold text-white/80">
Video
</span>
)}
</div>
);
};

const getScheduledPostStatusDisplay = (
status = ""
) => {
const normalized =
String(
status || ""
)
.trim()
.toLowerCase();

const displays = {
draft: {
label:
"Draft",
badge:
"bg-white/[0.07] text-white/60",
icon:
"fa-regular fa-file-lines",
},
scheduled: {
label:
"Scheduled",
badge:
"bg-blue-400/12 text-blue-100/75",
icon:
"fa-regular fa-calendar",
},
queued: {
label:
"Queued",
badge:
"bg-cyan-400/12 text-cyan-100/75",
icon:
"fa-solid fa-list",
},
publishing: {
label:
"Publishing",
badge:
"bg-violet-400/12 text-violet-100/75",
icon:
"fa-solid fa-spinner fa-spin",
},
processing: {
label:
"Processing",
badge:
"bg-indigo-400/12 text-indigo-100/75",
icon:
"fa-solid fa-spinner fa-spin",
},
failed: {
label:
"Failed",
badge:
"bg-red-400/12 text-red-100/80",
icon:
"fa-solid fa-circle-exclamation",
},
retrying: {
label:
"Retrying",
badge:
"bg-amber-400/12 text-amber-100/80",
icon:
"fa-solid fa-rotate fa-spin",
},
reconnect_required: {
label:
"Reconnect required",
badge:
"bg-orange-400/12 text-orange-100/80",
icon:
"fa-solid fa-link-slash",
},
cancelled: {
label:
"Cancelled",
badge:
"bg-white/[0.055] text-white/40",
icon:
"fa-solid fa-ban",
},
};

return (
displays[
normalized
] || {
label:
normalized
.replace(
/_/g,
" "
)
.replace(
/\b\w/g,
(character) =>
character.toUpperCase()
) ||
"Unknown",
badge:
"bg-white/[0.06] text-white/50",
icon:
"fa-solid fa-circle-info",
}
);
};

const ScheduledPostDestinations = ({
post,
}) => {
const destinations =
Array.isArray(
post?.destinations
)
? post.destinations
: [];

if (
destinations.length === 0
) {
return (
<span className="text-[10px] font-semibold text-white/28">
No destinations selected
</span>
);
}

return (
<div className="flex flex-wrap gap-1.5">
{destinations.map(
(destination, index) => {
const platformDisplay =
getConfirmationPlatformDisplay(
destination.platform
);

return (
<span
key={`${destination.platform}:${destination.profileId}:${index}`}
title={
destination.profileId
}
className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.055] px-2 py-1 text-[9px] font-semibold text-white/45"
>
<i
className={`${platformDisplay.icon} ${platformDisplay.color}`}
></i>

<span className="truncate">
{platformDisplay.label}
</span>
</span>
);
}
)}
</div>
);
};

const formatAssetStorageSize = (
  value
) => {
  const bytes = Math.max(
    Number(value || 0),
    0
  );

  if (bytes === 0) {
    return "0 B";
  }

  const units = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
  ];

  const unitIndex = Math.min(
    Math.floor(
      Math.log(bytes) /
        Math.log(1024)
    ),
    units.length - 1
  );

  const amount =
    bytes /
    1024 ** unitIndex;

  const maximumFractionDigits =
    unitIndex === 0 ||
    amount >= 100
      ? 0
      : amount >= 10
      ? 1
      : 2;

  return `${amount.toLocaleString(
    undefined,
    {
      maximumFractionDigits,
    }
  )} ${units[unitIndex]}`;
};

const ASSET_TYPE_DISPLAY = {
  image: {
    label: "Images",
    icon: "fa-regular fa-image",
    color: "text-fuchsia-200",
    background:
      "bg-fuchsia-400/10",
  },
  video: {
    label: "Videos",
    icon: "fa-solid fa-video",
    color: "text-blue-200",
    background:
      "bg-blue-400/10",
  },
  audio: {
    label: "Audio",
    icon: "fa-solid fa-music",
    color: "text-emerald-200",
    background:
      "bg-emerald-400/10",
  },
  document: {
    label: "Documents",
    icon: "fa-regular fa-file-lines",
    color: "text-amber-200",
    background:
      "bg-amber-400/10",
  },
  text: {
    label: "Text",
    icon: "fa-solid fa-align-left",
    color: "text-cyan-200",
    background:
      "bg-cyan-400/10",
  },
  other: {
    label: "Other",
    icon: "fa-regular fa-file",
    color: "text-white/55",
    background:
      "bg-white/[0.06]",
  },
};


const POST_REPORT_METRIC_DISPLAY = {
  likes: {
    label: "Likes",
    icon: "fa-solid fa-thumbs-up",
    format: "number",
  },
  reactions: {
    label: "Reactions",
    icon: "fa-solid fa-face-smile",
    format: "number",
  },
  comments: {
    label: "Comments",
    icon: "fa-solid fa-comment",
    format: "number",
  },
  shares: {
    label: "Shares",
    icon: "fa-solid fa-share",
    format: "number",
  },
  impressions: {
    label: "Impressions",
    icon: "fa-solid fa-eye",
    format: "number",
  },
  views: {
    label: "Views",
    icon: "fa-solid fa-chart-column",
    format: "number",
  },
  reach: {
    label: "Reach",
    icon: "fa-solid fa-bullseye",
    format: "number",
  },
  engagedUsers: {
    label: "Engaged users",
    icon: "fa-solid fa-user-check",
    format: "number",
  },
  clicks: {
    label: "Clicks",
    icon: "fa-solid fa-arrow-pointer",
    format: "number",
  },
  engagements: {
    label: "Engagements",
    icon: "fa-solid fa-heart",
    format: "number",
  },
  saves: {
    label: "Saves",
    icon: "fa-solid fa-bookmark",
    format: "number",
  },
  engagementRate: {
    label: "Engagement rate",
    icon: "fa-solid fa-percent",
    format: "percentage",
  },
  videoViews: {
    label: "Video views",
    icon: "fa-solid fa-video",
    format: "number",
  },
  videoViewTime: {
    label: "Video view time",
    icon: "fa-solid fa-clock",
    format: "number",
  },
};

const formatPostReportLabel = (
  value = ""
) => {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const formatPostReportValue = (
  value,
  metricKey = null
) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    return "Not available";
  }

  const number = Number(value);

  if (
    POST_REPORT_METRIC_DISPLAY[
      metricKey
    ]?.format === "percentage"
  ) {
    return `${number.toLocaleString(
      undefined,
      {
        maximumFractionDigits: 2,
      }
    )}%`;
  }

  return number.toLocaleString(
    undefined,
    {
      maximumFractionDigits: 2,
    }
  );
};

const getPostReportStatusDisplay = (
  status = ""
) => {
  const displays = {
    available: {
      label: "Report available",
      icon:
        "fa-solid fa-circle-check",
      badge:
        "bg-emerald-400/10 text-emerald-100/70",
    },
    no_data: {
      label: "No report data",
      icon:
        "fa-regular fa-folder-open",
      badge:
        "bg-amber-400/10 text-amber-100/70",
    },
    not_entitled: {
      label: "Reports unavailable",
      icon:
        "fa-solid fa-lock",
      badge:
        "bg-amber-400/10 text-amber-100/70",
    },
    no_accounts: {
      label: "No accounts",
      icon:
        "fa-solid fa-link-slash",
      badge:
        "bg-white/[0.06] text-white/45",
    },
    not_connected: {
      label: "Not connected",
      icon:
        "fa-solid fa-link-slash",
      badge:
        "bg-red-400/10 text-red-100/70",
    },
    selection_required: {
      label: "Selection required",
      icon:
        "fa-solid fa-users",
      badge:
        "bg-violet-400/10 text-violet-100/70",
    },
  };

  return (
    displays[status] ||
    displays.no_data
  );
};

const getPostReportAnalysisDisplay = (
  analysisType = ""
) => {
  const displays = {
    aggregate: {
      label: "Aggregate analysis",
      icon:
        "fa-solid fa-calculator",
    },
    rank: {
      label: "Ranked posts",
      icon:
        "fa-solid fa-ranking-star",
    },
    group: {
      label: "Grouped analysis",
      icon:
        "fa-solid fa-layer-group",
    },
    trend: {
      label: "Trend analysis",
      icon:
        "fa-solid fa-chart-line",
    },
    correlation: {
      label: "Correlation",
      icon:
        "fa-solid fa-link",
    },
    outliers: {
      label: "Outliers",
      icon:
        "fa-solid fa-triangle-exclamation",
    },
    select_representatives: {
      label: "Representative posts",
      icon:
        "fa-solid fa-star",
    },
    list: {
      label: "Post reports",
      icon:
        "fa-solid fa-list",
    },
  };

  return (
    displays[analysisType] ||
    displays.list
  );
};

const getPriorityPostReportMetrics = ({
  metrics = [],
  rankedMetric = null,
  maximum = 6,
}) => {
  const availableMetrics = (
    Array.isArray(metrics)
      ? metrics
      : []
  ).filter(
    (metric) =>
      metric?.available === true
  );

  const priorityKeys = [
    rankedMetric,
    "engagementRate",
    "engagements",
    "reach",
    "impressions",
    "views",
    "likes",
    "reactions",
    "comments",
    "shares",
    "saves",
    "clicks",
    "videoViews",
    "videoViewTime",
    "engagedUsers",
  ].filter(Boolean);

  const metricsByKey =
    new Map(
      availableMetrics.map(
        (metric) => [
          metric.key,
          metric,
        ]
      )
    );

  const selected = [];
  const selectedKeys =
    new Set();

  priorityKeys.forEach((key) => {
    const metric =
      metricsByKey.get(key);

    if (
      !metric ||
      selectedKeys.has(key) ||
      selected.length >= maximum
    ) {
      return;
    }

    selectedKeys.add(key);
    selected.push(metric);
  });

  availableMetrics.forEach(
    (metric) => {
      if (
        selected.length >= maximum ||
        selectedKeys.has(
          metric.key
        )
      ) {
        return;
      }

      selectedKeys.add(
        metric.key
      );
      selected.push(metric);
    }
  );

  return selected;
};

const PostReportMetricGrid = ({
  metrics = [],
  rankedMetric = null,
}) => {
  const availableMetrics =
    getPriorityPostReportMetrics({
      metrics,
      rankedMetric,
      maximum: 6,
    });

  if (
    availableMetrics.length === 0
  ) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
      {availableMetrics.map(
        (metric) => (
          <div
            key={metric.key}
            className="min-w-0"
          >
            <p className="mb-0 truncate text-[9px] font-semibold text-white/30">
              {metric.label}
            </p>

            <p className="mb-0 mt-0.5 truncate text-sm font-black text-white/78">
              {formatPostReportValue(
                metric.value,
                metric.key
              )}
            </p>
          </div>
        )
      )}
    </div>
  );
};

const PostReportMediaPreview = ({
  item,
}) => {
  const media = Array.isArray(
    item?.media
  )
    ? item.media
    : [];

  if (media.length === 0) {
    return null;
  }

  return (
    <PublishedPostMediaPreview
      post={{
        id:
          item.id ||
          item.postId,
        media,
      }}
    />
  );
};

const PostReportItemCard = ({
  item,
  showRank = false,
}) => {
  const platformDisplay =
    getConfirmationPlatformDisplay(
      item?.platform
    );

  const representativeLabel =
    item?.representativePosition ===
    "high"
      ? "High performer"
      : item?.representativePosition ===
        "median"
      ? "Typical performer"
      : item?.representativePosition ===
        "low"
      ? "Low performer"
      : item?.representativePosition
      ? formatPostReportLabel(
          item.representativePosition
        )
      : null;

  return (
    <article className="min-w-0 overflow-hidden rounded-xl border border-white/[0.075] bg-black/[0.14]">
      <PostReportMediaPreview
        item={item}
      />

      <div className="px-3.5 py-3">
        <div className="flex items-start gap-3">
          {showRank &&
            item?.rank && (
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-400/12 text-xs font-black text-blue-100">
              {item.rank}
            </span>
          )}

          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055]">
            <i
              className={`${platformDisplay.icon} ${platformDisplay.color} text-xs`}
            ></i>
          </span>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="text-[9px] font-bold uppercase tracking-wide text-white/35">
                {platformDisplay.label}
              </span>

              {item?.postType && (
                <span className="rounded-full bg-white/[0.055] px-2 py-0.5 text-[8px] font-bold uppercase text-white/35">
                  {formatPostReportLabel(
                    item.postType
                  )}
                </span>
              )}

              {representativeLabel && (
                <span className="rounded-full bg-violet-400/10 px-2 py-0.5 text-[8px] font-bold text-violet-100/65">
                  {representativeLabel}
                </span>
              )}
            </div>

            <p
              className="mb-0 line-clamp-3 whitespace-pre-wrap break-words text-xs leading-relaxed text-white/65"
              title={
                item?.message ||
                "No caption"
              }
            >
              {item?.message ||
                "No caption"}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {item?.postDate && (
                <span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/32">
                  <i className="fa-regular fa-clock mr-1"></i>
                  {new Date(
                    item.postDate
                  ).toLocaleString()}
                </span>
              )}

              {item?.rankedMetric &&
                item?.rankedValue !==
                  null && (
                  <span className="rounded-full bg-blue-400/10 px-2 py-1 text-[9px] font-bold text-blue-100/65">
                    {formatPostReportLabel(
                      item.rankedMetric
                    )}
                    :{" "}
                    {formatPostReportValue(
                      item.rankedValue,
                      item.rankedMetric
                    )}
                  </span>
                )}

              {item?.zScore !==
                null && (
                <span className="rounded-full bg-amber-400/10 px-2 py-1 text-[9px] font-bold text-amber-100/65">
                  Z-score{" "}
                  {Number(
                    item.zScore
                  ).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {item?.platformUrl && (
            <a
              href={item.platformUrl}
              target="_blank"
              rel="noreferrer"
              title="Open post"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055] text-white/45 transition hover:bg-white/[0.1] hover:text-white"
            >
              <i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
            </a>
          )}
        </div>
      </div>

      {getPriorityPostReportMetrics({
        metrics: item?.metrics,
        rankedMetric:
          item?.rankedMetric,
        maximum: 6,
      }).length > 0 && (
        <div className="border-t border-white/[0.05] px-3.5 py-3">
          <PostReportMetricGrid
            metrics={item?.metrics}
            rankedMetric={
              item?.rankedMetric
            }
          />
        </div>
      )}
    </article>
  );
};



const PostReportAggregateGrid = ({
  aggregates = {},
}) => {
  const entries = Object.entries(
    aggregates &&
      typeof aggregates ===
        "object" &&
      !Array.isArray(aggregates)
      ? aggregates
      : {}
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {entries.map(
        ([
          metricKey,
          aggregate,
        ]) => {
          const display =
            POST_REPORT_METRIC_DISPLAY[
              metricKey
            ] || {
              label:
                formatPostReportLabel(
                  metricKey
                ),
              icon:
                "fa-solid fa-chart-simple",
              format: "number",
            };

          const values =
            aggregate?.values &&
            typeof aggregate.values ===
              "object" &&
            !Array.isArray(
              aggregate.values
            )
              ? aggregate.values
              : {};

          const availableRowCount =
            Math.max(
              Number(
                aggregate?.availableRowCount ||
                  0
              ),
              0
            );

          const averageValue =
            values.average ??
            null;

          const totalValue =
            values.sum ??
            null;

          const medianValue =
            values.median ??
            null;

          const minimumValue =
            values.minimum ??
            null;

          const maximumValue =
            values.maximum ??
            null;

          const countValue =
            values.count ??
            availableRowCount;

          return (
            <details
              key={metricKey}
              className="group overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.13]"
            >
              <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-400/10 text-blue-100/65">
                  <i
                    className={`${display.icon} text-[9px]`}
                  ></i>
                </span>

                <div className="min-w-0 flex-1">
                  <p className="mb-0 truncate text-xs font-bold text-white/75">
                    {display.label}
                  </p>

                  <p className="mb-0 mt-0.5 text-[9px] text-white/30">
                    {availableRowCount.toLocaleString()}{" "}
                    available row
                    {availableRowCount ===
                    1
                      ? ""
                      : "s"}
                  </p>
                </div>

                <div className="min-w-0 flex-shrink-0 text-right">
                  <p className="mb-0 text-sm font-black text-white/80">
                    {formatPostReportValue(
                      averageValue,
                      metricKey
                    )}
                  </p>

                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Average
                  </p>
                </div>

                <i className="fa-solid fa-chevron-down ml-1 text-[8px] text-white/25 transition-transform group-open:rotate-180"></i>
              </summary>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/[0.05] px-3 py-3 sm:grid-cols-5">
                <div className="min-w-0">
                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Count
                  </p>

                  <p className="mb-0 mt-1 truncate text-xs font-black text-white/68">
                    {formatPostReportValue(
                      countValue,
                      null
                    )}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Total
                  </p>

                  <p className="mb-0 mt-1 truncate text-xs font-black text-white/68">
                    {formatPostReportValue(
                      totalValue,
                      metricKey
                    )}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Median
                  </p>

                  <p className="mb-0 mt-1 truncate text-xs font-black text-white/68">
                    {formatPostReportValue(
                      medianValue,
                      metricKey
                    )}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Minimum
                  </p>

                  <p className="mb-0 mt-1 truncate text-xs font-black text-white/68">
                    {formatPostReportValue(
                      minimumValue,
                      metricKey
                    )}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                    Maximum
                  </p>

                  <p className="mb-0 mt-1 truncate text-xs font-black text-white/68">
                    {formatPostReportValue(
                      maximumValue,
                      metricKey
                    )}
                  </p>
                </div>
              </div>
            </details>
          );
        }
      )}
    </div>
  );
};



const PostReportGroupedRows = ({
  rows = [],
  labelKey,
}) => {
  if (
    !Array.isArray(rows) ||
    rows.length === 0
  ) {
    return null;
  }

  return (
    <div className="space-y-2">
      {rows.map(
        (row, index) => (
          <div
            key={`${
              row?.[labelKey] ||
              row?.value ||
              index
            }-${index}`}
            className="overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.13]"
          >
            <div className="flex items-center justify-between gap-3 px-3 py-2.5">
              <p className="mb-0 truncate text-xs font-bold text-white/72">
                {formatPostReportLabel(
                  row?.[labelKey] ||
                    row?.value ||
                    `Group ${index + 1}`
                )}
              </p>

              <span className="rounded-full bg-white/[0.055] px-2 py-1 text-[9px] font-semibold text-white/38">
                {Number(
                  row?.rowCount || 0
                ).toLocaleString()}{" "}
                post
                {Number(
                  row?.rowCount || 0
                ) === 1
                  ? ""
                  : "s"}
              </span>
            </div>

            <div className="border-t border-white/[0.05] p-2.5">
              <PostReportAggregateGrid
                aggregates={
                  row?.aggregates
                }
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};

const PostReportsReadCard = ({
  presentation,
}) => {
  const reports = Array.isArray(
    presentation?.reports
  )
    ? presentation.reports
    : [];

  return (
    <div className="overflow-hidden rounded-2xl border border-blue-300/[0.13] bg-gradient-to-b from-blue-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-400/15 text-blue-100">
              <i className="fa-solid fa-chart-column text-sm"></i>
            </span>

            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-blue-100/55">
                Post Reports
              </span>

              <h3 className="mb-0 text-base font-black text-white">
                {presentation?.title ||
                  "Post Analytics"}
              </h3>
            </div>
          </div>

          <p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
            {presentation?.description ||
              "Authoritative post-level analytics for the selected scope."}
          </p>
        </div>

        <span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-bold text-white/45">
          <i className="fa-solid fa-chart-simple text-[9px]"></i>

          {reports.length} result
          {reports.length === 1
            ? ""
            : "s"}
        </span>
      </div>

      {reports.length > 0 ? (
        <div className="space-y-3 border-t border-white/[0.07] p-3">
          {reports.map(
            (report) => {
              const platformDisplay =
                getConfirmationPlatformDisplay(
                  report.platform
                );

              const statusDisplay =
                getPostReportStatusDisplay(
                  report.status
                );

              const analysisDisplay =
                getPostReportAnalysisDisplay(
                  report.operation ===
                    "analysis"
                    ? report.analysisType
                    : "list"
                );

              const items =
                Array.isArray(
                  report.items
                )
                  ? report.items
                  : [];

              const outliers =
                Array.isArray(
                  report.outliers
                )
                  ? report.outliers
                  : [];

              const dateFilters = [
                report.filters?.since
                  ? {
                      key: "since",
                      label: "From",
                      value:
                        new Date(
                          report.filters.since
                        ).toLocaleDateString(),
                    }
                  : null,
                report.filters?.until
                  ? {
                      key: "until",
                      label: "To",
                      value:
                        new Date(
                          report.filters.until
                        ).toLocaleDateString(),
                    }
                  : null,
              ].filter(Boolean);

              return (
                <section
                  key={report.key}
                  className="overflow-hidden rounded-xl border border-white/[0.075] bg-black/[0.14]"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 px-3.5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/[0.055]">
                        <i
                          className={`${platformDisplay.icon} ${platformDisplay.color} text-sm`}
                        ></i>
                      </span>

                      <div className="min-w-0">
                        <h4 className="mb-0 truncate text-sm font-black text-white/85">
                          {report.profileName ||
                            "Connected profile"}
                        </h4>

                        <p className="mb-0 mt-0.5 truncate text-[9px] font-semibold uppercase tracking-wide text-white/32">
                          {platformDisplay.label}
                          {report.accountType
                            ? ` · ${report.accountType}`
                            : ""}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-400/10 px-2.5 py-1 text-[9px] font-bold text-blue-100/65">
                        <i
                          className={`${analysisDisplay.icon} text-[8px]`}
                        ></i>

                        {analysisDisplay.label}
                      </span>

                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold ${statusDisplay.badge}`}
                      >
                        <i
                          className={`${statusDisplay.icon} text-[8px]`}
                        ></i>

                        {statusDisplay.label}
                      </span>
                    </div>
                  </div>

                  {dateFilters.length >
                    0 && (
                    <div className="flex flex-wrap gap-1.5 border-t border-white/[0.05] px-3.5 py-2.5">
                      {dateFilters.map(
                        (filter) => (
                          <span
                            key={
                              filter.key
                            }
                            className="rounded-full bg-white/[0.055] px-2.5 py-1 text-[9px] font-semibold text-white/40"
                          >
                            {filter.label}:{" "}
                            {filter.value}
                          </span>
                        )
                      )}
                    </div>
                  )}

                  {report.hasRows ? (
                    <div className="space-y-3 border-t border-white/[0.06] p-3">
                      {report.coverage && (
<div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2.5">
<span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-white/42">
<i className="fa-solid fa-layer-group text-[9px] text-blue-100/55"></i>

{Number(
report.coverage
.matchingRows ||
report.total ||
0
).toLocaleString()}{" "}
matching posts
</span>

<span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-white/42">
<i className="fa-solid fa-chart-simple text-[9px] text-blue-100/55"></i>

{Number(
report.coverage
.processedRows ||
0
).toLocaleString()}{" "}
analyzed
</span>

{Number(
report.coverage
.excludedRows ||
0
) > 0 && (
<span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-amber-100/55">
<i className="fa-solid fa-circle-minus text-[9px]"></i>

{Number(
report.coverage
.excludedRows ||
0
).toLocaleString()}{" "}
excluded
</span>
)}

<span
className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[9px] font-bold ${
report.coverage
.complete
? "bg-emerald-400/10 text-emerald-100/65"
: "bg-amber-400/10 text-amber-100/65"
}`}
>
<i
className={`fa-solid ${
report.coverage
.complete
? "fa-circle-check"
: "fa-circle-exclamation"
} text-[8px]`}
></i>

{report.coverage
.complete
? "Complete coverage"
: "Partial coverage"}
</span>
</div>
)}

                      {report.operation ===
                        "list" &&
                        items.length >
                          0 && (
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {items.map(
                              (item) => (
                                <PostReportItemCard
                                  key={
                                    item.key
                                  }
                                  item={item}
                                />
                              )
                            )}
                          </div>
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "aggregate" && (
                          <PostReportAggregateGrid
                            aggregates={
                              report.aggregates
                            }
                          />
                        )}

                      {report.operation ===
                        "analysis" &&
                        [
                          "rank",
                          "select_representatives",
                        ].includes(
                          report.analysisType
                        ) &&
                        items.length >
                          0 && (
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {items.map(
                              (item) => (
                                <PostReportItemCard
                                  key={
                                    item.key
                                  }
                                  item={item}
                                  showRank
                                />
                              )
                            )}
                          </div>
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "group" && (
                          <PostReportGroupedRows
                            rows={
                              report.groups
                            }
                            labelKey="value"
                          />
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "trend" && (
                          <PostReportGroupedRows
                            rows={
                              report.trend
                            }
                            labelKey="period"
                          />
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "correlation" && (
                          <div className="rounded-xl border border-white/[0.07] bg-black/[0.13] px-4 py-4">
                            <div className="flex items-center gap-3">
                              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-400/12 text-blue-100">
                                <i className="fa-solid fa-link text-xs"></i>
                              </span>

                              <div className="min-w-0 flex-1">
                                <p className="mb-0 text-xs font-bold text-white/72">
                                  {formatPostReportLabel(
                                    report
                                      .correlation
                                      ?.leftMetric ||
                                      "First metric"
                                  )}
                                  {" ↔ "}
                                  {formatPostReportLabel(
                                    report
                                      .correlation
                                      ?.rightMetric ||
                                      "Second metric"
                                  )}
                                </p>

                                <p className="mb-0 mt-0.5 text-[9px] text-white/30">
                                  {Number(
                                    report
                                      .correlation
                                      ?.rowCount ||
                                      0
                                  ).toLocaleString()}{" "}
                                  comparable rows
                                </p>
                              </div>

                              <p className="mb-0 text-xl font-black text-blue-100">
                                {report
                                  .correlation
                                  ?.coefficient ===
                                  null ||
                                report
                                  .correlation
                                  ?.coefficient ===
                                  undefined
                                  ? "Not available"
                                  : Number(
                                      report
                                        .correlation
                                        .coefficient
                                    ).toFixed(
                                      3
                                    )}
                              </p>
                            </div>
                          </div>
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "outliers" &&
                        outliers.length >
                          0 && (
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {outliers.map(
                              (item) => (
                                <PostReportItemCard
                                  key={
                                    item.key
                                  }
                                  item={item}
                                />
                              )
                            )}
                          </div>
                        )}

                      {report.operation ===
                        "analysis" &&
                        report.analysisType ===
                          "outliers" &&
                        outliers.length ===
                          0 && (
                          <div className="rounded-xl border border-white/[0.07] bg-black/[0.13] px-4 py-6 text-center">
                            <p className="mb-0 text-xs font-semibold text-white/40">
                              No statistical outliers were found for the requested metric.
                            </p>
                          </div>
                        )}

                      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.05] pt-2.5 text-[9px] font-semibold text-white/28">
                        {report.operation ===
                          "list" && (
                          <span>
                            {report.returnedCount.toLocaleString()}{" "}
                            of{" "}
                            {report.total.toLocaleString()}{" "}
                            report rows shown
                          </span>
                        )}

                        {report.latestAvailableReportDate && (
                          <span>
                            Latest report{" "}
                            {new Date(
                              report.latestAvailableReportDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        {report.lastSyncedAt && (
                          <span className="ml-auto">
                            Synced{" "}
                            {new Date(
                              report.lastSyncedAt
                            ).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-white/[0.06] px-4 py-7 text-center">
                      <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
                        <i
                          className={`${statusDisplay.icon} text-sm`}
                        ></i>
                      </span>

                      <p className="mb-1 mt-3 text-sm font-bold text-white/70">
                        Post analytics unavailable
                      </p>

                      <p className="mx-auto mb-0 max-w-lg text-xs leading-relaxed text-white/35">
                        {report.summary ||
                          "No post report data is available for the requested scope."}
                      </p>
                    </div>
                  )}
                </section>
              );
            }
          )}
        </div>
      ) : (
        <div className="border-t border-white/[0.07] px-4 py-8 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
            <i className="fa-solid fa-chart-column text-sm"></i>
          </span>

          <p className="mb-1 mt-3 text-sm font-bold text-white/70">
            No post reports returned
          </p>

          <p className="mb-0 text-xs text-white/35">
            No post-level analytics result was available for this request.
          </p>
        </div>
      )}
    </div>
  );
};

const getAppHistoryStatusDisplay = (
  status = ""
) => {
  const normalizedStatus = String(
    status || ""
  )
    .trim()
    .toLowerCase();

  const displays = {
    processing: {
      label: "Processing",
      icon:
        "fa-solid fa-spinner fa-spin",
      badge:
        "bg-blue-400/10 text-blue-100/70",
      border:
        "border-blue-300/[0.12]",
    },
    completed: {
      label: "Completed",
      icon:
        "fa-solid fa-circle-check",
      badge:
        "bg-emerald-400/10 text-emerald-100/70",
      border:
        "border-emerald-300/[0.1]",
    },
    failed: {
      label: "Failed",
      icon:
        "fa-solid fa-circle-exclamation",
      badge:
        "bg-red-400/10 text-red-100/70",
      border:
        "border-red-300/[0.13]",
    },
    refunded: {
      label: "Refunded",
      icon:
        "fa-solid fa-rotate-left",
      badge:
        "bg-amber-400/10 text-amber-100/70",
      border:
        "border-amber-300/[0.12]",
    },
  };

  return (
    displays[normalizedStatus] || {
      label:
        normalizedStatus
          .replace(/_/g, " ")
          .replace(
            /\b\w/g,
            (character) =>
              character.toUpperCase()
          ) || "Unknown",
      icon:
        "fa-solid fa-circle-info",
      badge:
        "bg-white/[0.06] text-white/45",
      border:
        "border-white/[0.075]",
    }
  );
};

const getAppHistoryMediaType = (
  media = {}
) => {
  const type = String(
    media?.type || ""
  )
    .trim()
    .toLowerCase();

  if (
    type === "video" ||
    String(
      media?.mimeType || ""
    )
      .toLowerCase()
      .startsWith("video/") ||
    /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i.test(
      String(media?.url || "")
    )
  ) {
    return "video";
  }

  if (
    type === "image" ||
    String(
      media?.mimeType || ""
    )
      .toLowerCase()
      .startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(
      String(media?.url || "")
    )
  ) {
    return "image";
  }

  return "other";
};

const AppHistoryMediaPreview = ({
  media = [],
  label = "Media",
  carousel = false,
}) => {
  const items = (
Array.isArray(media)
? media
: []
).filter((item) => item?.url);

const [
activeCarouselIndex,
setActiveCarouselIndex,
] = useState(0);

const carouselTrackRef =
useRef(null);

const scrollFrameRef =
useRef(null);

useEffect(() => {
setActiveCarouselIndex(0);

const track =
carouselTrackRef.current;

if (track) {
track.scrollTo({
left: 0,
behavior: "auto",
});
}

return () => {
if (scrollFrameRef.current) {
window.cancelAnimationFrame(
scrollFrameRef.current
);
}
};
}, [
carousel,
items
.map((item) => item.url)
.join("|"),
]);

const scrollToHistoryCarouselCard = (
requestedIndex
) => {
if (items.length === 0) {
return;
}

const nextIndex = Math.min(
Math.max(
requestedIndex,
0
),
items.length - 1
);

setActiveCarouselIndex(
nextIndex
);

const track =
carouselTrackRef.current;

const target =
track?.children?.[
nextIndex
];

if (
!track ||
!target
) {
return;
}

const targetLeft =
target.offsetLeft -
(
track.clientWidth -
target.clientWidth
) /
2;

track.scrollTo({
left: Math.max(
targetLeft,
0
),
behavior: "smooth",
});
};

const updateHistoryCarouselIndex = (
track
) => {
if (
!track ||
track.children.length === 0
) {
return;
}

const viewportCenter =
track.scrollLeft +
track.clientWidth / 2;

let closestIndex = 0;
let closestDistance =
Number.POSITIVE_INFINITY;

Array.from(
track.children
).forEach(
(child, index) => {
const childCenter =
child.offsetLeft +
child.clientWidth / 2;

const distance =
Math.abs(
childCenter -
viewportCenter
);

if (
distance <
closestDistance
) {
closestDistance =
distance;
closestIndex =
index;
}
}
);

setActiveCarouselIndex(
closestIndex
);
};

if (items.length === 0) {
return null;
}

  if (carousel) {
const activeItem =
items[
activeCarouselIndex
] ||
items[0];

return (
<div className="min-w-0">
<div className="mb-2 flex items-center justify-between gap-3">
<div className="min-w-0">
<p className="mb-0 text-[9px] font-bold uppercase tracking-[0.08em] text-white/28">
{label}
</p>

<p className="mb-0 mt-0.5 text-[10px] font-semibold text-white/45">
Swipe through the generated carousel
</p>
</div>

<span className="flex-shrink-0 rounded-full bg-white/[0.055] px-2.5 py-1 text-[9px] font-bold text-white/45">
{activeCarouselIndex + 1}
{" / "}
{items.length}
</span>
</div>

<div
className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-black/25 outline-none focus-visible:ring-2 focus-visible:ring-violet-300/40"
tabIndex={0}
role="region"
aria-roledescription="carousel"
aria-label={`Generated carousel. Showing card ${activeCarouselIndex + 1} of ${items.length}.`}
onKeyDown={(event) => {
if (
event.key ===
"ArrowLeft"
) {
event.preventDefault();

scrollToHistoryCarouselCard(
activeCarouselIndex - 1
);
}

if (
event.key ===
"ArrowRight"
) {
event.preventDefault();

scrollToHistoryCarouselCard(
activeCarouselIndex + 1
);
}

if (
event.key === "Home"
) {
event.preventDefault();

scrollToHistoryCarouselCard(
0
);
}

if (
event.key === "End"
) {
event.preventDefault();

scrollToHistoryCarouselCard(
items.length - 1
);
}
}}
>
<div
ref={carouselTrackRef}
className="copilot-no-scrollbar flex touch-pan-x snap-x snap-mandatory overflow-x-auto scroll-smooth overscroll-x-contain"
style={{
WebkitOverflowScrolling:
"touch",
}}
onScroll={(event) => {
const track =
event.currentTarget;

if (scrollFrameRef.current) {
window.cancelAnimationFrame(
scrollFrameRef.current
);
}

scrollFrameRef.current =
window.requestAnimationFrame(
() => {
updateHistoryCarouselIndex(
track
);
}
);
}}
>
{items.map(
(item, index) => {
const mediaType =
getAppHistoryMediaType(
item
);

const active =
index ===
activeCarouselIndex;

return (
<article
key={`${item.url}-${index}`}
aria-label={`Carousel card ${index + 1} of ${items.length}`}
aria-current={
active
? "true"
: undefined
}
className={`w-full flex-none snap-center snap-always overflow-hidden rounded-xl border bg-[#15161a] shadow-[0_12px_30px_rgba(0,0,0,0.24)] transition duration-200 ${
active
? "border-violet-300/30 opacity-100"
: "border-white/[0.07] opacity-80"
}`}
style={{
scrollSnapStop:
"always",
}}
>
<div className="relative aspect-[4/5] overflow-hidden bg-black/35">
{mediaType ===
"video" ? (
<>
<video
src={item.url}
muted
playsInline
preload="metadata"
className="h-full w-full object-contain"
/>

<span className="pointer-events-none absolute inset-0 flex items-center justify-center">
<span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white shadow-lg backdrop-blur">
<i className="fa-solid fa-play text-[10px]"></i>
</span>
</span>
</>
) : mediaType ===
"image" ? (
<img
src={item.url}
alt={
item.name ||
`Carousel card ${index + 1}`
}
loading={
index <= 2
? "eager"
: "lazy"
}
draggable={false}
className="h-full w-full select-none object-contain"
onDoubleClick={() => {
window.open(
item.url,
"_blank",
"noopener,noreferrer"
);
}}
/>
) : (
<div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-white/35">
<i className="fa-solid fa-file text-2xl"></i>

<span className="line-clamp-2 text-[10px] font-semibold">
{item.name ||
`Carousel card ${index + 1}`}
</span>
</div>
)}

<div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between bg-gradient-to-b from-black/75 via-black/15 to-transparent p-2 pb-8">
<span className="rounded-full bg-black/65 px-2 py-1 text-[9px] font-black text-white/90 backdrop-blur">
Card {index + 1}
</span>

<span className="rounded-full bg-black/65 px-2 py-1 text-[8px] font-bold text-white/70 backdrop-blur">
{index + 1} /{" "}
{items.length}
</span>
</div>

{active && (
<div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent px-3 pb-3 pt-10">
<p
className="mb-0 truncate text-[10px] font-bold text-white/85"
title={
item.name ||
item.url
}
>
{item.name ||
`Carousel card ${index + 1}`}
</p>
</div>
)}
</div>
</article>
);
}
)}
</div>

{items.length > 1 && (
<>
<button
type="button"
disabled={
activeCarouselIndex === 0
}
onClick={() =>
scrollToHistoryCarouselCard(
activeCarouselIndex - 1
)
}
aria-label="Show previous carousel card"
className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/75 text-white/80 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-black/95 hover:text-white disabled:pointer-events-none disabled:opacity-20"
>
<i className="fa-solid fa-chevron-left text-[10px]"></i>
</button>

<button
type="button"
disabled={
activeCarouselIndex ===
items.length - 1
}
onClick={() =>
scrollToHistoryCarouselCard(
activeCarouselIndex + 1
)
}
aria-label="Show next carousel card"
className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/75 text-white/80 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-black/95 hover:text-white disabled:pointer-events-none disabled:opacity-20"
>
<i className="fa-solid fa-chevron-right text-[10px]"></i>
</button>
</>
)}
</div>

{items.length > 1 && (
<div className="copilot-no-scrollbar mt-2 flex max-w-full items-center justify-center gap-1.5 overflow-x-auto px-2 py-1">
{items.map(
(item, index) => (
<button
key={`app-history-carousel-dot-${item.url}-${index}`}
type="button"
onClick={() =>
scrollToHistoryCarouselCard(
index
)
}
aria-label={`Show carousel card ${index + 1}`}
aria-current={
index ===
activeCarouselIndex
? "true"
: undefined
}
className={`h-2 flex-shrink-0 rounded-full transition-all duration-200 ${
index ===
activeCarouselIndex
? "w-5 bg-violet-300"
: "w-2 bg-white/20 hover:bg-white/40"
}`}
></button>
)
)}
</div>
)}

<div className="mt-2 flex min-w-0 items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.025] px-2.5 py-2">
<div className="min-w-0 flex-1">
<p
className="mb-0 truncate text-[10px] font-bold text-white/65"
title={
activeItem?.name ||
activeItem?.url
}
>
{activeItem?.name ||
`Carousel card ${activeCarouselIndex + 1}`}
</p>

<p className="mb-0 mt-0.5 text-[9px] font-semibold text-white/28">
Card {activeCarouselIndex + 1} of{" "}
{items.length}
</p>
</div>

<a
href={activeItem?.url}
target="_blank"
rel="noreferrer"
title={`Open carousel card ${activeCarouselIndex + 1}`}
className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.07] text-white/50 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
</a>

<a
href={activeItem?.url}
download={
activeItem?.name ||
`carousel-card-${activeCarouselIndex + 1}.png`
}
title={`Download carousel card ${activeCarouselIndex + 1}`}
className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.07] text-white/50 transition hover:bg-white/[0.12] hover:text-white"
>
<i className="fa-solid fa-download text-[9px]"></i>
</a>
</div>
</div>
);
}

return (
<div>
      <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.08em] text-white/28">
        {label}
      </p>

      <div
        className={`grid gap-2 ${
          items.length === 1
            ? "grid-cols-1"
            : "grid-cols-2"
        }`}
      >
        {items
          .slice(0, 6)
          .map(
            (item, index) => {
              const mediaType =
                getAppHistoryMediaType(
                  item
                );

              return (
                <a
                  key={`${item.url}-${index}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative block min-w-0 overflow-hidden rounded-xl border border-white/[0.07] bg-black/25"
                >
                  {mediaType ===
                  "video" ? (
                    <div className="relative">
                      <video
                        src={item.url}
                        muted
                        playsInline
                        preload="metadata"
                        className="aspect-video w-full bg-black object-cover"
                      />

                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white">
                          <i className="fa-solid fa-play text-[9px]"></i>
                        </span>
                      </span>
                    </div>
                  ) : mediaType ===
                    "image" ? (
                    <img
                      src={item.url}
                      alt={
                        item.name ||
                        `${label} ${index + 1}`
                      }
                      loading="lazy"
                      className="aspect-video w-full bg-black object-cover"
                    />
                  ) : (
                    <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 px-3 text-white/35">
                      <i className="fa-solid fa-file text-xl"></i>

                      <span className="line-clamp-2 text-center text-[9px] font-semibold">
                        {item.name ||
                          label}
                      </span>
                    </div>
                  )}

                  <div className="flex min-w-0 items-center justify-between gap-2 px-2.5 py-2">
                    <span
                      className="min-w-0 flex-1 truncate text-[9px] font-semibold text-white/50"
                      title={
                        item.name ||
                        item.url
                      }
                    >
                      {item.name ||
                        `${label} ${
                          index + 1
                        }`}
                    </span>

                    <i className="fa-solid fa-arrow-up-right-from-square flex-shrink-0 text-[8px] text-white/30 transition group-hover:text-white/65"></i>
                  </div>
                </a>
              );
            }
          )}
      </div>

      {items.length > 6 && (
        <p className="mb-0 mt-2 text-[9px] font-semibold text-white/28">
          +{items.length - 6} more
          media item
          {items.length - 6 === 1
            ? ""
            : "s"}
        </p>
      )}
    </div>
  );
};

const getBackgroundJobStatusDisplay = (
status = ""
) => {
const normalized =
String(
status || "unknown"
)
.trim()
.toLowerCase();

const displays = {
waiting: {
label: "Waiting",
icon:
"fa-regular fa-clock",
badge:
"bg-cyan-400/10 text-cyan-100/70",
border:
"border-cyan-300/[0.11]",
},
delayed: {
label: "Delayed",
icon:
"fa-solid fa-hourglass-half",
badge:
"bg-amber-400/10 text-amber-100/70",
border:
"border-amber-300/[0.11]",
},
active: {
label: "Processing",
icon:
"fa-solid fa-spinner fa-spin",
badge:
"bg-blue-400/10 text-blue-100/70",
border:
"border-blue-300/[0.12]",
},
completed: {
label: "Completed",
icon:
"fa-solid fa-circle-check",
badge:
"bg-emerald-400/10 text-emerald-100/70",
border:
"border-emerald-300/[0.1]",
},
failed: {
label: "Failed",
icon:
"fa-solid fa-circle-exclamation",
badge:
"bg-red-400/10 text-red-100/75",
border:
"border-red-300/[0.13]",
},
stalled: {
label: "Stalled",
icon:
"fa-solid fa-triangle-exclamation",
badge:
"bg-orange-400/10 text-orange-100/75",
border:
"border-orange-300/[0.12]",
},
paused: {
label: "Paused",
icon:
"fa-solid fa-pause",
badge:
"bg-violet-400/10 text-violet-100/70",
border:
"border-violet-300/[0.11]",
},
cancelled: {
label: "Cancelled",
icon:
"fa-solid fa-ban",
badge:
"bg-white/[0.06] text-white/45",
border:
"border-white/[0.07]",
},
unknown: {
label: "Unknown",
icon:
"fa-solid fa-circle-info",
badge:
"bg-white/[0.06] text-white/45",
border:
"border-white/[0.07]",
},
};

return (
displays[normalized] ||
displays.unknown
);
};

const BackgroundJobsReadCard = ({
presentation,
}) => {
const jobs =
Array.isArray(
presentation?.jobs
)
? presentation.jobs
: [];

const filters =
presentation?.filters &&
typeof presentation.filters ===
"object" &&
!Array.isArray(
presentation.filters
)
? presentation.filters
: {};

const appliedFilters = [
filters.queueName
? {
key: "queueName",
label: "Queue",
value: filters.queueName,
}
: null,
filters.jobName
? {
key: "jobName",
label: "Job",
value: filters.jobName,
}
: null,
filters.status
? {
key: "status",
label: "Status",
value: filters.status,
}
: null,
filters.platform
? {
key: "platform",
label: "Platform",
value: filters.platform,
}
: null,
filters.profileId
? {
key: "profileId",
label: "Profile",
value: filters.profileId,
}
: null,
].filter(Boolean);

const activeCount =
jobs.filter(
(job) =>
job.status === "active"
).length;

const failedCount =
jobs.filter(
(job) =>
[
"failed",
"stalled",
].includes(
job.status
)
).length;

return (
<div className="overflow-hidden rounded-2xl border border-cyan-300/[0.13] bg-gradient-to-b from-cyan-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="min-w-0 flex-1">
<div className="mb-2 flex items-center gap-2">
<span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-400/15 text-cyan-100">
<i className="fa-solid fa-gears text-sm"></i>
</span>

<div>
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-cyan-100/55">
Background Processing
</span>

<h3 className="mb-0 text-base font-black text-white">
{presentation?.title ||
"Background Jobs"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{presentation?.description ||
"Current background processing activity for this brand."}
</p>
</div>

<div className="flex flex-wrap items-center justify-end gap-1.5">
{activeCount > 0 && (
<span className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-400/10 px-2.5 text-[10px] font-bold text-blue-100/70">
<i className="fa-solid fa-spinner fa-spin text-[9px]"></i>

{activeCount} active
</span>
)}

{failedCount > 0 && (
<span className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-red-400/10 px-2.5 text-[10px] font-bold text-red-100/70">
<i className="fa-solid fa-circle-exclamation text-[9px]"></i>

{failedCount} issue
{failedCount === 1
? ""
: "s"}
</span>
)}

<span className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-bold text-white/45">
<i className="fa-solid fa-list-check text-[9px]"></i>

{jobs.length} job
{jobs.length === 1
? ""
: "s"}
</span>
</div>
</div>

{appliedFilters.length > 0 && (
<div className="flex flex-wrap gap-1.5 border-t border-white/[0.06] px-3 py-2.5">
{appliedFilters.map(
(filter) => (
<span
key={filter.key}
title={String(
filter.value
)}
className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-bold text-white/45"
>
<span className="flex-shrink-0 text-white/28">
{filter.label}:
</span>

<span className="max-w-[260px] truncate capitalize">
{String(
filter.value
).replace(
/_/g,
" "
)}
</span>
</span>
)
)}
</div>
)}

{jobs.length > 0 ? (
<div className="space-y-3 border-t border-white/[0.07] p-3">
{jobs.map(
(job) => {
const statusDisplay =
getBackgroundJobStatusDisplay(
job.status
);

const progressPercentage =
job.progressPercentage ===
null ||
job.progressPercentage ===
undefined
? null
: Math.min(
Math.max(
Number(
job.progressPercentage
),
0
),
100
);

const finishedAt =
job.completedAt ||
job.failedAt ||
null;

return (
<article
key={
job.key ||
job.id
}
className={`overflow-hidden rounded-xl border bg-black/[0.14] ${statusDisplay.border}`}
>
<div className="flex flex-wrap items-start justify-between gap-3 px-3.5 py-3">
<div className="flex min-w-0 flex-1 items-center gap-3">
<span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-100/70">
<i className="fa-solid fa-gear text-xs"></i>
</span>

<div className="min-w-0">
<h4
className="mb-0 truncate text-sm font-black text-white/85"
title={
job.jobName ||
"Background job"
}
>
{job.jobName ||
"Background job"}
</h4>

<p className="mb-0 mt-0.5 truncate text-[9px] font-semibold uppercase tracking-wide text-white/30">
{job.queueName ||
"Unassigned queue"}
{job.platform
? ` · ${job.platform}`
: ""}
</p>
</div>
</div>

<span
className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold ${statusDisplay.badge}`}
>
<i
className={`${statusDisplay.icon} text-[8px]`}
></i>

{statusDisplay.label}
</span>
</div>

{progressPercentage !==
null && (
<div className="border-t border-white/[0.05] px-3.5 py-3">
<div className="mb-1.5 flex items-center justify-between gap-3">
<span className="text-[9px] font-bold uppercase tracking-wide text-white/28">
Progress
</span>

<span className="text-[10px] font-black text-cyan-100/75">
{progressPercentage.toLocaleString(
undefined,
{
maximumFractionDigits:
1,
}
)}
%
</span>
</div>

<div className="h-2 overflow-hidden rounded-full bg-white/[0.07]">
<div
className={`h-full rounded-full transition-all duration-500 ${
job.status ===
"failed" ||
job.status ===
"stalled"
? "bg-red-400"
: job.status ===
"completed"
? "bg-emerald-300"
: "bg-cyan-300"
}`}
style={{
width:
`${progressPercentage}%`,
}}
></div>
</div>
</div>
)}

<div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/[0.05] px-3.5 py-3 sm:grid-cols-4">
<div className="min-w-0">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Attempts
</p>

<p className="mb-0 mt-1 text-sm font-black text-white/75">
{Number(
job.attemptsMade ||
0
).toLocaleString()}
{" / "}
{Number(
job.attempts ||
0
).toLocaleString()}
</p>
</div>

<div className="min-w-0">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Priority
</p>

<p className="mb-0 mt-1 text-sm font-black text-white/75">
{job.priority ===
null ||
job.priority ===
undefined
? "Not set"
: Number(
job.priority
).toLocaleString()}
</p>
</div>

<div className="min-w-0">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Created
</p>

<p className="mb-0 mt-1 text-[10px] font-semibold text-white/55">
{job.createdAt
? new Date(
job.createdAt
).toLocaleString()
: "Not available"}
</p>
</div>

<div className="min-w-0">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Finished
</p>

<p className="mb-0 mt-1 text-[10px] font-semibold text-white/55">
{finishedAt
? new Date(
finishedAt
).toLocaleString()
: "Not finished"}
</p>
</div>
</div>

{job.lastError && (
<div className="border-t border-red-300/[0.08] bg-red-400/[0.045] px-3.5 py-3">
<div className="flex items-start gap-2.5">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-[10px] text-red-200/70"></i>

<div className="min-w-0">
<p className="mb-1 text-[10px] font-bold text-red-100/75">
{job.failureType
? String(
job.failureType
).replace(
/_/g,
" "
)
: "Job error"}
</p>

<p className="mb-0 whitespace-pre-wrap break-words text-[10px] leading-relaxed text-red-100/50">
{job.lastError}
</p>
</div>
</div>
</div>
)}

<details className="group border-t border-white/[0.05]">
<summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3.5 py-2.5">
<span className="text-[9px] font-semibold text-white/30">
Job details
</span>

<i className="fa-solid fa-chevron-down text-[8px] text-white/25 transition-transform group-open:rotate-180"></i>
</summary>

<div className="grid grid-cols-[100px_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-white/[0.04] px-3.5 py-3 text-[10px]">
<span className="font-semibold text-white/28">
Record ID
</span>

<span className="break-all text-white/52">
{job.id}
</span>

<span className="font-semibold text-white/28">
Job ID
</span>

<span className="break-all text-white/52">
{job.jobId ||
"Not available"}
</span>

<span className="font-semibold text-white/28">
Bull job ID
</span>

<span className="break-all text-white/52">
{job.bullJobId ||
"Not available"}
</span>

<span className="font-semibold text-white/28">
Domain status
</span>

<span className="break-words capitalize text-white/52">
{job.domainStatus
? String(
job.domainStatus
).replace(
/_/g,
" "
)
: "Not available"}
</span>

<span className="font-semibold text-white/28">
Profile ID
</span>

<span className="break-all text-white/52">
{job.profileId ||
"Not available"}
</span>

<span className="font-semibold text-white/28">
Started
</span>

<span className="text-white/52">
{job.startedAt
? new Date(
job.startedAt
).toLocaleString()
: "Not available"}
</span>

<span className="font-semibold text-white/28">
Updated
</span>

<span className="text-white/52">
{job.updatedAt
? new Date(
job.updatedAt
).toLocaleString()
: "Not available"}
</span>
</div>
</details>
</article>
);
}
)}

<div className="flex flex-wrap items-center gap-2 border-t border-white/[0.05] pt-2.5 text-[9px] font-semibold text-white/28">
<span>
{jobs.length.toLocaleString()}{" "}
of{" "}
{Number(
presentation?.total ||
jobs.length
).toLocaleString()}{" "}
jobs shown
</span>

<span className="ml-auto">
Page{" "}
{Number(
presentation?.page || 1
).toLocaleString()}
</span>
</div>
</div>
) : (
<div className="border-t border-white/[0.07] px-4 py-8 text-center">
<span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
<i className="fa-solid fa-gears text-sm"></i>
</span>

<p className="mb-1 mt-3 text-sm font-bold text-white/70">
No background jobs found
</p>

<p className="mb-0 text-xs text-white/35">
No background processing records matched the requested filters.
</p>
</div>
)}
</div>
);
};

const CreditBalanceReadCard = ({
presentation,
}) => {
const totalCredits =
Math.max(
Number(
presentation?.totalCredits ||
0
),
0
);

const consumedCredits =
Math.max(
Number(
presentation?.consumedCredits ||
0
),
0
);

const remainingCredits =
Math.max(
Number(
presentation?.remainingCredits ||
0
),
0
);

const remainingPercentage =
Math.min(
Math.max(
Number(
presentation?.remainingPercentage ||
0
),
0
),
100
);

const status =
String(
presentation?.status ||
"available"
)
.trim()
.toLowerCase();

const statusDisplay =
status === "depleted"
? {
label:
"No credits remaining",
icon:
"fa-solid fa-circle-exclamation",
badge:
"bg-red-400/12 text-red-100/75",
bar:
"bg-red-400",
}
: status === "low"
? {
label:
"Low balance",
icon:
"fa-solid fa-triangle-exclamation",
badge:
"bg-amber-400/12 text-amber-100/75",
bar:
"bg-amber-300",
}
: status ===
"unavailable"
? {
label:
"Balance unavailable",
icon:
"fa-solid fa-circle-info",
badge:
"bg-white/[0.06] text-white/45",
bar:
"bg-white/20",
}
: {
label:
"Credits available",
icon:
"fa-solid fa-circle-check",
badge:
"bg-emerald-400/10 text-emerald-100/70",
bar:
"bg-emerald-300",
};

return (
<div className="overflow-hidden rounded-2xl border border-amber-300/[0.14] bg-gradient-to-b from-amber-400/[0.06] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="min-w-0 flex-1">
<div className="mb-2 flex items-center gap-2">
<span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400/15 text-amber-100">
<i className="fa-solid fa-coins text-sm"></i>
</span>

<div>
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-amber-100/55">
AI Credits
</span>

<h3 className="mb-0 text-base font-black text-white">
{presentation?.title ||
"AI Credit Balance"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{presentation?.description ||
`${remainingCredits} of ${totalCredits} AI credits remaining.`}
</p>
</div>

<span
className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${statusDisplay.badge}`}
>
<i
className={`${statusDisplay.icon} text-[9px]`}
></i>

{statusDisplay.label}
</span>
</div>

<div className="border-t border-white/[0.07] p-3">
<div className="overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.14]">
<div className="px-4 py-4">
<div className="flex items-end justify-between gap-4">
<div className="min-w-0">
<p className="mb-1 text-[9px] font-bold uppercase tracking-[0.08em] text-white/30">
Remaining balance
</p>

<p className="mb-0 text-3xl font-black tracking-tight text-white">
{remainingCredits.toLocaleString()}
</p>

<p className="mb-0 mt-1 text-[10px] font-semibold text-white/35">
AI credit
{remainingCredits ===
1
? ""
: "s"}{" "}
available
</p>
</div>

<div className="flex-shrink-0 text-right">
<p className="mb-0 text-lg font-black text-amber-100">
{remainingPercentage.toLocaleString(
undefined,
{
maximumFractionDigits:
1,
}
)}
%
</p>

<p className="mb-0 text-[9px] font-semibold uppercase tracking-wide text-white/28">
remaining
</p>
</div>
</div>

<div className="mt-4 h-2 overflow-hidden rounded-full bg-white/[0.07]">
<div
className={`h-full rounded-full transition-all duration-500 ${statusDisplay.bar}`}
style={{
width:
`${remainingPercentage}%`,
}}
></div>
</div>
</div>

<div className="grid grid-cols-2 divide-x divide-white/[0.06] border-t border-white/[0.06]">
<div className="min-w-0 px-4 py-3">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Total credits
</p>

<p className="mb-0 mt-1 text-lg font-black text-white/75">
{totalCredits.toLocaleString()}
</p>
</div>

<div className="min-w-0 px-4 py-3">
<p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
Credits used
</p>

<p className="mb-0 mt-1 text-lg font-black text-white/75">
{consumedCredits.toLocaleString()}
</p>
</div>
</div>
</div>
</div>
</div>
);
};

const AppsHistoryReadCard = ({
  presentation,
}) => {
  const transactions =
    Array.isArray(
      presentation?.transactions
    )
      ? presentation.transactions
      : [];

  const filters =
    presentation?.filters &&
    typeof presentation.filters ===
      "object" &&
    !Array.isArray(
      presentation.filters
    )
      ? presentation.filters
      : {};

  const appliedFilters = [
    filters.appId
      ? {
          key: "appId",
          label: "App",
          value: filters.appId,
        }
      : null,
    filters.status
      ? {
          key: "status",
          label: "Status",
          value: String(
            filters.status
          ).replace(/_/g, " "),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="overflow-hidden rounded-2xl border border-violet-300/[0.13] bg-gradient-to-b from-violet-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-400/15 text-violet-100">
              <i className="fa-solid fa-wand-magic-sparkles text-sm"></i>
            </span>

            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-violet-100/55">
                AI Apps
              </span>

              <h3 className="mb-0 text-base font-black text-white">
                {presentation?.title ||
                  "AI Apps History"}
              </h3>
            </div>
          </div>

          <p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
            {presentation?.description ||
              "AI app transaction history for this brand."}
          </p>
        </div>

        <span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-bold text-white/45">
          <i className="fa-solid fa-clock-rotate-left text-[9px]"></i>

          {transactions.length} transaction
          {transactions.length === 1
            ? ""
            : "s"}
        </span>
      </div>

      {appliedFilters.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-white/[0.06] px-3 py-2.5">
          {appliedFilters.map(
            (filter) => (
              <span
                key={filter.key}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-bold text-white/45"
              >
                <span className="text-white/28">
                  {filter.label}:
                </span>

                <span className="truncate capitalize">
                  {filter.value}
                </span>
              </span>
            )
          )}
        </div>
      )}

      {transactions.length > 0 ? (
        <div className="space-y-3 border-t border-white/[0.07] p-3">
          {transactions.map(
            (transaction) => {
              const statusDisplay =
                getAppHistoryStatusDisplay(
                  transaction.status
                );

              const inputMedia =
                Array.isArray(
                  transaction.inputMedia
                )
                  ? transaction.inputMedia
                  : [];

              const outputMedia =
                Array.isArray(
                  transaction.outputMedia
                )
                  ? transaction.outputMedia
                  : [];

              return (
                <details
                  key={
                    transaction.key ||
                    transaction.id
                  }
                  className={`overflow-hidden rounded-xl border bg-black/[0.14] ${statusDisplay.border}`}
                >
                  <summary className="flex cursor-pointer list-none flex-wrap items-start justify-between gap-3 px-3.5 py-3 transition hover:bg-white/[0.025]">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-violet-400/10 text-violet-100/70">
                        <i className="fa-solid fa-wand-magic-sparkles text-xs"></i>
                      </span>

                      <div className="min-w-0">
                        <h4
                          className="mb-0 truncate text-sm font-black text-white/85"
                          title={
                            transaction.appName
                          }
                        >
                          {transaction.appName ||
                            "AI App"}
                        </h4>


                      </div>
                    </div>

                    <span className="flex flex-shrink-0 items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold ${statusDisplay.badge}`}
                      >
                        <i
                          className={`${statusDisplay.icon} text-[8px]`}
                        ></i>

                        {statusDisplay.label}
                      </span>

                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.05] text-white/35">
                        <i className="fa-solid fa-chevron-down text-[8px]"></i>
                      </span>
                    </span>
                  </summary>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/[0.05] px-3.5 py-3 sm:grid-cols-4">
                    <div className="min-w-0">
                      <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                        Charged
                      </p>

                      <p className="mb-0 mt-1 text-sm font-black text-white/75">
                        {Number(
                          transaction.creditsCharged ||
                            0
                        ).toLocaleString()}{" "}
                        credit
                        {Number(
                          transaction.creditsCharged ||
                            0
                        ) === 1
                          ? ""
                          : "s"}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                        Refunded
                      </p>

                      <p
                        className={`mb-0 mt-1 text-sm font-black ${
                          Number(
                            transaction.creditsRefunded ||
                              0
                          ) > 0
                            ? "text-amber-200"
                            : "text-white/45"
                        }`}
                      >
                        {Number(
                          transaction.creditsRefunded ||
                            0
                        ).toLocaleString()}{" "}
                        credit
                        {Number(
                          transaction.creditsRefunded ||
                            0
                        ) === 1
                          ? ""
                          : "s"}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                        Created
                      </p>

                      <p className="mb-0 mt-1 text-[10px] font-semibold text-white/55">
                        {transaction.createdAt
                          ? new Date(
                              transaction.createdAt
                            ).toLocaleString()
                          : "Not available"}
                      </p>
                    </div>

                    <div className="min-w-0">
                      <p className="mb-0 text-[8px] font-bold uppercase tracking-wide text-white/25">
                        Finished
                      </p>

                      <p className="mb-0 mt-1 text-[10px] font-semibold text-white/55">
                        {transaction.completedAt ||
                        transaction.failedAt ||
                        transaction.refundedAt
                          ? new Date(
                              transaction.completedAt ||
                                transaction.failedAt ||
                                transaction.refundedAt
                            ).toLocaleString()
                          : "Not available"}
                      </p>
                    </div>
                  </div>

                  {(inputMedia.length > 0 ||
                    outputMedia.length >
                      0) && (
                    <div className="grid grid-cols-1 gap-3 border-t border-white/[0.05] px-3.5 py-3 sm:grid-cols-2">
                      <AppHistoryMediaPreview
                        media={inputMedia}
                        label="Input"
                      />

                      <AppHistoryMediaPreview
                        media={outputMedia}
                        label="Output"
                        carousel={
                          transaction.appSlug ===
                            "carousel-maker" ||
                          transaction.appId ===
                            "carousel.make"
                        }
                      />
                    </div>
                  )}

                  {(transaction.status ===
                    "failed" ||
                    transaction.status ===
                      "refunded") && (
                    <div className="border-t border-red-300/[0.08] bg-red-400/[0.045] px-3.5 py-3">
                      <div className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-exclamation mt-0.5 text-[10px] text-red-200/70"></i>

                        <div className="min-w-0">
                          <p className="mb-1 text-[10px] font-bold text-red-100/75">
                            {transaction.status ===
                            "refunded"
                              ? "Transaction failed · Credits refunded"
                              : "Transaction failed"}
                          </p>

                          <p className="mb-0 whitespace-pre-wrap break-words text-[10px] leading-relaxed text-red-100/50">
                            {transaction.errorMessage ||
                              "No error message was recorded."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <details className="group border-t border-white/[0.05]">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3.5 py-2.5">
                      <span className="text-[9px] font-semibold text-white/30">
                        Transaction details
                      </span>

                      <i className="fa-solid fa-chevron-down text-[8px] text-white/25 transition-transform group-open:rotate-180"></i>
                    </summary>

                    <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-white/[0.04] px-3.5 py-3 text-[10px]">
                      <span className="font-semibold text-white/28">
                        Transaction ID
                      </span>

                      <span className="break-all text-white/52">
                        {transaction.id}
                      </span>

                      <span className="font-semibold text-white/28">
                        App ID
                      </span>

                      <span className="break-all text-white/52">
                        {transaction.appId ||
                          "Not available"}
                      </span>

                      <span className="font-semibold text-white/28">
                        Provider task
                      </span>

                      <span className="break-all text-white/52">
                        {transaction.providerTaskId ||
                          "Not available"}
                      </span>

                      <span className="font-semibold text-white/28">
                        Queued
                      </span>

                      <span className="text-white/52">
                        {transaction.queuedAt
                          ? new Date(
                              transaction.queuedAt
                            ).toLocaleString()
                          : "Not available"}
                      </span>

                      <span className="font-semibold text-white/28">
                        Started
                      </span>

                      <span className="text-white/52">
                        {transaction.startedAt
                          ? new Date(
                              transaction.startedAt
                            ).toLocaleString()
                          : "Not available"}
                      </span>
                    </div>
                  </details>
                </details>
              );
            }
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.05] pt-2.5 text-[9px] font-semibold text-white/28">
            <span>
              {transactions.length.toLocaleString()}{" "}
              of{" "}
              {Number(
                presentation?.total ||
                  transactions.length
              ).toLocaleString()}{" "}
              transactions shown
            </span>

            <span className="ml-auto">
              Page{" "}
              {Number(
                presentation?.page || 1
              ).toLocaleString()}
            </span>
          </div>
        </div>
      ) : (
        <div className="border-t border-white/[0.07] px-4 py-8 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
            <i className="fa-solid fa-wand-magic-sparkles text-sm"></i>
          </span>

          <p className="mb-1 mt-3 text-sm font-bold text-white/70">
            No AI app transactions found
          </p>

          <p className="mb-0 text-xs text-white/35">
            No AI app activity matched the requested filters.
          </p>
        </div>
      )}
    </div>
  );
};

const formatProfileMetricValue = ({
  value,
  format,
  available,
}) => {
  if (
    !available ||
    value === null ||
    value === undefined ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    return "Not available";
  }

  const number = Number(value);

  if (format === "percentage") {
    return `${number.toLocaleString(
      undefined,
      {
        maximumFractionDigits: 2,
      }
    )}%`;
  }

  if (format === "signed_number") {
    const formatted =
      Math.abs(number).toLocaleString(
        undefined,
        {
          maximumFractionDigits: 2,
        }
      );

    if (number > 0) {
      return `+${formatted}`;
    }

    if (number < 0) {
      return `−${formatted}`;
    }

    return "0";
  }

  return number.toLocaleString(
    undefined,
    {
      maximumFractionDigits: 2,
    }
  );
};

const getProfileSummaryStatusDisplay = (
  status = ""
) => {
  const displays = {
    available: {
      label: "Report available",
      icon:
        "fa-solid fa-circle-check",
      badge:
        "bg-emerald-400/10 text-emerald-100/70",
    },
    zero_metrics: {
      label: "Recorded zero",
      icon:
        "fa-solid fa-circle-check",
      badge:
        "bg-blue-400/10 text-blue-100/70",
    },
    no_data: {
      label: "No report data",
      icon:
        "fa-regular fa-folder-open",
      badge:
        "bg-amber-400/10 text-amber-100/70",
    },
    not_entitled: {
      label: "Reports unavailable",
      icon:
        "fa-solid fa-lock",
      badge:
        "bg-amber-400/10 text-amber-100/70",
    },
    no_accounts: {
      label: "No accounts",
      icon:
        "fa-solid fa-link-slash",
      badge:
        "bg-white/[0.06] text-white/45",
    },
    not_connected: {
      label: "Not connected",
      icon:
        "fa-solid fa-link-slash",
      badge:
        "bg-red-400/10 text-red-100/70",
    },
    selection_required: {
      label: "Selection required",
      icon:
        "fa-solid fa-users",
      badge:
        "bg-violet-400/10 text-violet-100/70",
    },
  };

  return (
    displays[status] ||
    displays.no_data
  );
};

const getProfileSummaryEmptyMessage = (
  profile
) => {
  if (
    profile?.status ===
    "not_entitled"
  ) {
    return (
      profile.summary ||
      "Reports are not included in the current plan."
    );
  }

  if (
    profile?.status ===
    "no_accounts"
  ) {
    return "No social accounts are connected to this brand.";
  }

  if (
    profile?.status ===
    "not_connected"
  ) {
    return (
      profile.summary ||
      "The requested social profile is not connected."
    );
  }

  if (
    profile?.status ===
    "selection_required"
  ) {
    return (
      profile.summary ||
      "Select an exact connected profile before requesting analytics."
    );
  }

  return (
    profile?.summary ||
    "The profile is connected, but no report data is available for the requested period."
  );
};

const ProfileSummaryReadCard = ({
  presentation,
}) => {
  const profiles = Array.isArray(
    presentation?.profiles
  )
    ? presentation.profiles
    : [];

  return (
    <div className="overflow-hidden rounded-2xl border border-blue-300/[0.13] bg-gradient-to-b from-blue-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-400/15 text-blue-100">
              <i className="fa-solid fa-chart-line text-sm"></i>
            </span>

            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-blue-100/55">
                Profile Reports
              </span>

              <h3 className="mb-0 text-base font-black text-white">
                {presentation?.title ||
                  "Profile Analytics"}
              </h3>
            </div>
          </div>

          <p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
            {presentation?.description ||
              "Authoritative profile analytics for the selected scope."}
          </p>
        </div>

        <span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-bold text-white/45">
          <i className="fa-solid fa-user-chart text-[9px]"></i>

          {profiles.length} profile
          {profiles.length === 1
            ? ""
            : "s"}
        </span>
      </div>

      {profiles.length > 0 ? (
        <div className="space-y-3 border-t border-white/[0.07] p-3">
          {profiles.map(
            (profile) => {
              const platformDisplay =
                getConfirmationPlatformDisplay(
                  profile.platform
                );

              const statusDisplay =
                getProfileSummaryStatusDisplay(
                  profile.status
                );

              const metrics =
                Array.isArray(
                  profile.metrics
                )
                  ? profile.metrics
                  : [];

              const dateFilters = [
                profile.filters?.since
                  ? {
                      key: "since",
                      label: "From",
                      value:
                        new Date(
                          profile.filters.since
                        ).toLocaleDateString(),
                    }
                  : null,
                profile.filters?.until
                  ? {
                      key: "until",
                      label: "To",
                      value:
                        new Date(
                          profile.filters.until
                        ).toLocaleDateString(),
                    }
                  : null,
              ].filter(Boolean);

              return (
                <section
                  key={
                    profile.key ||
                    `${profile.platform}:${profile.profileId}`
                  }
                  className="overflow-hidden rounded-xl border border-white/[0.075] bg-black/[0.14]"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 px-3.5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/[0.055]">
                          <i
                            className={`${platformDisplay.icon} ${platformDisplay.color} text-sm`}
                          ></i>
                        </span>

                        <div className="min-w-0">
                          <h4
                            className="mb-0 truncate text-sm font-black text-white/85"
                            title={
                              profile.profileName
                            }
                          >
                            {profile.profileName ||
                              "Connected profile"}
                          </h4>

                          <p className="mb-0 mt-0.5 truncate text-[9px] font-semibold uppercase tracking-wide text-white/32">
                            {platformDisplay.label}
                            {profile.accountType
                              ? ` · ${profile.accountType}`
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>

                    <span
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold ${statusDisplay.badge}`}
                    >
                      <i
                        className={`${statusDisplay.icon} text-[8px]`}
                      ></i>

                      {statusDisplay.label}
                    </span>
                  </div>

                  {dateFilters.length >
                    0 && (
                    <div className="flex flex-wrap gap-1.5 border-t border-white/[0.05] px-3.5 py-2.5">
                      {dateFilters.map(
                        (filter) => (
                          <span
                            key={
                              filter.key
                            }
                            className="rounded-full bg-white/[0.055] px-2.5 py-1 text-[9px] font-semibold text-white/40"
                          >
                            {filter.label}:{" "}
                            {filter.value}
                          </span>
                        )
                      )}
                    </div>
                  )}

                  {profile.hasRows ? (
                    <>
                      <div className="grid grid-cols-2 gap-2 border-t border-white/[0.06] p-3 sm:grid-cols-3">
                        {metrics.map(
                          (metric) => {
                            const unavailable =
                              !metric.available;

                            return (
                              <div
                                key={
                                  metric.key
                                }
                                className={`min-w-0 rounded-xl border px-3 py-3 ${
                                  unavailable
                                    ? "border-white/[0.045] bg-white/[0.018]"
                                    : "border-white/[0.065] bg-white/[0.03]"
                                }`}
                              >
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <span
                                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
                                      unavailable
                                        ? "bg-white/[0.04] text-white/22"
                                        : "bg-blue-400/10 text-blue-100/65"
                                    }`}
                                  >
                                    <i
                                      className={`${metric.icon} text-[9px]`}
                                    ></i>
                                  </span>

                                  {!metric.complete &&
                                    metric.available && (
                                      <span
                                        title="Available from part of the requested report period"
                                        className="rounded-full bg-amber-400/10 px-1.5 py-0.5 text-[8px] font-bold text-amber-100/55"
                                      >
                                        Partial
                                      </span>
                                    )}
                                </div>

                                <p
                                  className={`mb-0 truncate text-base font-black ${
                                    unavailable
                                      ? "text-white/28"
                                      : metric.format ===
                                          "signed_number" &&
                                        Number(
                                          metric.value
                                        ) > 0
                                      ? "text-emerald-200"
                                      : metric.format ===
                                          "signed_number" &&
                                        Number(
                                          metric.value
                                        ) < 0
                                      ? "text-red-200"
                                      : "text-white/85"
                                  }`}
                                  title={formatProfileMetricValue(
                                    metric
                                  )}
                                >
                                  {formatProfileMetricValue(
                                    metric
                                  )}
                                </p>

                                <p className="mb-0 mt-0.5 truncate text-[9px] font-semibold text-white/32">
                                  {metric.label}
                                </p>
                              </div>
                            );
                          }
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.05] px-3.5 py-2.5 text-[9px] font-semibold text-white/28">
                        <span>
                          {profile.rowCount.toLocaleString()} report row
                          {profile.rowCount ===
                          1
                            ? ""
                            : "s"}
                        </span>

                        {profile.reportDate && (
                          <span>
                            Latest report{" "}
                            {new Date(
                              profile.reportDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        {profile.lastSyncedAt && (
                          <span className="ml-auto">
                            Synced{" "}
                            {new Date(
                              profile.lastSyncedAt
                            ).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="border-t border-white/[0.06] px-4 py-7 text-center">
                      <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
                        <i
                          className={`${statusDisplay.icon} text-sm`}
                        ></i>
                      </span>

                      <p className="mb-1 mt-3 text-sm font-bold text-white/70">
                        Profile analytics unavailable
                      </p>

                      <p className="mx-auto mb-0 max-w-lg text-xs leading-relaxed text-white/35">
                        {getProfileSummaryEmptyMessage(
                          profile
                        )}
                      </p>
                    </div>
                  )}
                </section>
              );
            }
          )}
        </div>
      ) : (
        <div className="border-t border-white/[0.07] px-4 py-8 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
            <i className="fa-solid fa-chart-line text-sm"></i>
          </span>

          <p className="mb-1 mt-3 text-sm font-bold text-white/70">
            No profile summaries returned
          </p>

          <p className="mb-0 text-xs text-white/35">
            No profile analytics result was available for this request.
          </p>
        </div>
      )}
    </div>
  );
};

const getAssetSearchModeDisplay = (
  retrievalMode = ""
) => {
  const mode = String(
    retrievalMode || ""
  )
    .trim()
    .toLowerCase();

  if (
    mode ===
    "strict_visual_presence"
  ) {
    return {
      label: "Visual match",
      icon: "fa-solid fa-eye",
      badge:
        "bg-cyan-400/10 text-cyan-100/65",
    };
  }

  if (
    mode ===
    "semantic_suitability"
  ) {
    return {
      label: "Semantic match",
      icon:
        "fa-solid fa-wand-magic-sparkles",
      badge:
        "bg-violet-400/10 text-violet-100/65",
    };
  }

  if (
    mode === "factual_lookup"
  ) {
    return {
      label: "Relevant match",
      icon:
        "fa-solid fa-magnifying-glass",
      badge:
        "bg-blue-400/10 text-blue-100/65",
    };
  }

  return {
    label: "Library results",
    icon: "fa-solid fa-list",
    badge:
      "bg-white/[0.06] text-white/45",
  };
};

const AssetSearchReadCard = ({
  presentation,
  message,
  onAction,
  loading = false,
  inlineInteraction = null,
  inlineEditingEnabled = false,
  loadRemoteOptions,
  uploadActionDraftMedia,
  onSubmitActionDraft,
  onCancelActionDraft,
}) => {
  const [
    activeActionId,
    setActiveActionId,
  ] = useState(null);

  const assets = Array.isArray(
    presentation?.assets
  )
    ? presentation.assets
    : [];

  const filters =
    presentation?.filters &&
    typeof presentation.filters ===
      "object" &&
    !Array.isArray(
      presentation.filters
    )
      ? presentation.filters
      : {};

  const modeDisplay =
    getAssetSearchModeDisplay(
      presentation?.retrievalMode
    );

  const editing =
    inlineInteraction?.type ===
      "action_form" &&
    [
      "assets.rename",
      "assets.delete",
    ].includes(
      inlineInteraction?.toolName
    );

  const editingDelete =
    inlineInteraction?.toolName ===
    "assets.delete";

  const appliedFilters = [
    filters.query
      ? {
          key: "query",
          label: "Search",
          value: filters.query,
        }
      : null,
    filters.type
      ? {
          key: "type",
          label: "Type",
          value: filters.type,
        }
      : null,
    filters.status
      ? {
          key: "status",
          label: "Status",
          value: filters.status,
        }
      : null,
  ].filter(Boolean);

  const runAction =
    async (action) => {
      if (
        !action ||
        loading ||
        activeActionId ||
        editing
      ) {
        return;
      }

      setActiveActionId(
        action.id
      );

      try {
        await onAction?.({
          message,
          action,
          fieldKeys: [],
        });
      } finally {
        setActiveActionId(
          null
        );
      }
    };

  return (
    <div className="overflow-hidden rounded-2xl border border-fuchsia-300/[0.13] bg-gradient-to-b from-fuchsia-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fuchsia-400/15 text-fuchsia-100">
              <i className="fa-solid fa-photo-film text-sm"></i>
            </span>

            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-fuchsia-100/55">
                Media Library
              </span>

              <h3 className="mb-0 text-base font-black text-white">
                {presentation?.title ||
                  "Media Library"}
              </h3>
            </div>
          </div>

          <p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
            {editing
              ? editingDelete
                ? "Review the selected asset. Nothing will be deleted until you confirm."
                : "Enter the new asset name. The asset will not change until you review and confirm."
              : presentation?.description ||
                "Media Library search results."}
          </p>
        </div>

        {editing ? (
          <span
            className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${
              editingDelete
                ? "bg-red-400/12 text-red-100"
                : "bg-violet-400/15 text-violet-100"
            }`}
          >
            <i
              className={`fa-solid ${
                editingDelete
                  ? "fa-trash"
                  : "fa-pen"
              } text-[9px]`}
            ></i>

            {editingDelete
              ? "Preparing deletion"
              : "Renaming"}
          </span>
        ) : (
          <span
            className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${modeDisplay.badge}`}
          >
            <i
              className={`${modeDisplay.icon} text-[9px]`}
            ></i>

            {modeDisplay.label}
          </span>
        )}
      </div>

      {editing ? (
        <div className="border-t border-white/[0.07] bg-black/[0.12] p-3">
          <ActionDraftForm
            interaction={
              inlineInteraction
            }
            enabled={
              inlineEditingEnabled
            }
            loading={loading}
            loadRemoteOptions={
              loadRemoteOptions
            }
            uploadMedia={
              uploadActionDraftMedia
            }
            onSubmit={
              onSubmitActionDraft
            }
            onCancel={
              onCancelActionDraft
            }
          />
        </div>
      ) : (
        <>
          {appliedFilters.length >
            0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-white/[0.06] px-3 py-2.5">
              {appliedFilters.map(
                (filter) => (
                  <span
                    key={filter.key}
                    title={String(
                      filter.value
                    )}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 text-[9px] font-bold text-white/45"
                  >
                    <span className="flex-shrink-0 text-white/28">
                      {filter.label}:
                    </span>

                    <span className="max-w-[260px] truncate capitalize">
                      {String(
                        filter.value
                      ).replace(
                        /_/g,
                        " "
                      )}
                    </span>
                  </span>
                )
              )}
            </div>
          )}

          {assets.length > 0 ? (
            <div className="grid grid-cols-1 items-start gap-3 border-t border-white/[0.07] p-3 sm:grid-cols-2">
              {assets.map(
                (asset) => {
                  const actions =
                    Array.isArray(
                      asset.actions
                    )
                      ? asset.actions
                      : [];

                  const renameAction =
                    actions.find(
                      (action) =>
                        action?.operation ===
                        "rename"
                    ) || null;

                  const deleteAction =
                    actions.find(
                      (action) =>
                        action?.operation ===
                        "delete"
                    ) || null;

                  const semanticMatch =
                    asset.matchSource &&
                    ![
                      "current_library_browsing",
                      "legacy_library_browsing",
                    ].includes(
                      asset.matchSource
                    );

                  return (
                    <article
                      key={
                        asset.key ||
                        asset.id ||
                        asset.url
                      }
                      className="min-w-0 overflow-hidden rounded-xl border border-white/[0.075] bg-black/[0.14]"
                    >
                      <AssetPreviewTile
                        asset={asset}
                      />

                      <div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.055] px-2.5 py-2.5">
                        {semanticMatch && (
                          <span className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-400/10 px-2.5 text-[9px] font-bold text-violet-100/65">
                            <i className="fa-solid fa-wand-magic-sparkles text-[8px]"></i>
                            Relevant
                          </span>
                        )}

                        {asset.url && (
                          <a
                            href={
                              asset.url
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.055] px-2.5 text-[10px] font-bold text-white/50 transition hover:bg-white/[0.1] hover:text-white"
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
                            Open
                          </a>
                        )}

                        {renameAction && (
                          <button
                            type="button"
                            disabled={
                              loading ||
                              Boolean(
                                activeActionId
                              )
                            }
                            onClick={() =>
                              runAction(
                                renameAction
                              )
                            }
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-400/10 px-2.5 text-[10px] font-bold text-violet-100/70 transition hover:bg-violet-400/20 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-45"
                          >
                            <i
                              className={`fa-solid ${
                                activeActionId ===
                                renameAction.id
                                  ? "fa-spinner fa-spin"
                                  : "fa-pen"
                              } text-[9px]`}
                            ></i>
                            Rename
                          </button>
                        )}

                        {deleteAction && (
                          <button
                            type="button"
                            disabled={
                              loading ||
                              Boolean(
                                activeActionId
                              )
                            }
                            onClick={() =>
                              runAction(
                                deleteAction
                              )
                            }
                            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg bg-red-400/10 px-2.5 text-[10px] font-bold text-red-100/65 transition hover:bg-red-400/20 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-45"
                          >
                            <i
                              className={`fa-solid ${
                                activeActionId ===
                                deleteAction.id
                                  ? "fa-spinner fa-spin"
                                  : "fa-trash"
                              } text-[9px]`}
                            ></i>
                            Delete
                          </button>
                        )}
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          ) : (
            <div className="border-t border-white/[0.07] px-4 py-8 text-center">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
                <i className="fa-regular fa-folder-open text-sm"></i>
              </span>

              <p className="mb-1 mt-3 text-sm font-bold text-white/70">
                No assets found
              </p>

              <p className="mb-0 text-xs text-white/35">
                {presentation?.semantic
                  ? "No Media Library assets met the requested relevance threshold."
                  : "No Media Library assets matched the requested filters."}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AssetCountReadCard = ({
  presentation,
}) => {
  const total = Math.max(
    Number(
      presentation?.total ??
        presentation?.count ??
        0
    ),
    0
  );

  const totalSizeBytes = Math.max(
    Number(
      presentation?.totalSizeBytes ||
        0
    ),
    0
  );

  const byType =
    presentation?.byType &&
    typeof presentation.byType ===
      "object" &&
    !Array.isArray(
      presentation.byType
    )
      ? presentation.byType
      : {};


  const filters =
    presentation?.filters &&
    typeof presentation.filters ===
      "object" &&
    !Array.isArray(
      presentation.filters
    )
      ? presentation.filters
      : {};

  const typeEntries = Object.entries(
    ASSET_TYPE_DISPLAY
  ).map(
    ([
      type,
      display,
    ]) => ({
      type,
      ...display,
      count: Math.max(
        Number(
          byType[type] || 0
        ),
        0
      ),
    })
  );


  const appliedFilters = [
    filters.type
      ? {
          key: "type",
          label: "Type",
          value: String(
            filters.type
          ).replace(
            /_/g,
            " "
          ),
        }
      : null,
    filters.status
      ? {
          key: "status",
          label: "Status",
          value: String(
            filters.status
          ).replace(
            /_/g,
            " "
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="overflow-hidden rounded-2xl border border-fuchsia-300/[0.13] bg-gradient-to-b from-fuchsia-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fuchsia-400/15 text-fuchsia-100">
              <i className="fa-solid fa-photo-film text-sm"></i>
            </span>

            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-fuchsia-100/55">
                Media Library
              </span>

              <h3 className="mb-0 text-base font-black text-white">
                {presentation?.title ||
                  "Media Library"}
              </h3>
            </div>
          </div>

          <p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
            {presentation?.description ||
              "Media Library asset summary."}
          </p>
        </div>

        {appliedFilters.length >
          0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {appliedFilters.map(
              (filter) => (
                <span
                  key={filter.key}
                  className="rounded-full bg-white/[0.07] px-2.5 py-1 text-[9px] font-bold capitalize text-white/55"
                >
                  {filter.label}:{" "}
                  {filter.value}
                </span>
              )
            )}
          </div>
        )}
      </div>

      <div className="space-y-2 border-t border-white/[0.07] p-3">
        <div className="overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.13]">
          <div className="grid grid-cols-2 divide-x divide-white/[0.06]">
            <div className="flex min-w-0 items-center gap-3 px-3.5 py-3">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-fuchsia-400/12 text-fuchsia-100/75">
                <i className="fa-solid fa-layer-group text-[10px]"></i>
              </span>

              <div className="min-w-0">
                <p className="mb-0 truncate text-lg font-black text-white">
                  {total.toLocaleString()}
                </p>

                <p className="mb-0 text-[9px] font-semibold text-white/32">
                  Total asset
                  {total === 1
                    ? ""
                    : "s"}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-center gap-3 px-3.5 py-3">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cyan-400/12 text-cyan-100/75">
                <i className="fa-solid fa-hard-drive text-[10px]"></i>
              </span>

              <div className="min-w-0">
                <p className="mb-0 truncate text-lg font-black text-white">
                  {formatAssetStorageSize(
                    totalSizeBytes
                  )}
                </p>

                <p className="mb-0 text-[9px] font-semibold text-white/32">
                  Total storage
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.13]">
          <div className="flex items-center gap-3 px-3.5 py-3">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055] text-white/45">
              <i className="fa-solid fa-shapes text-[10px]"></i>
            </span>

            <div className="min-w-0 flex-1">
              <p className="mb-0 text-sm font-bold text-white/80">
                Assets by type
              </p>

              <p className="mb-0 mt-0.5 text-[10px] text-white/32">
                Current asset composition for this scope
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 border-t border-white/[0.05] p-3 sm:grid-cols-3">
            {typeEntries.map(
              (item) => (
                <div
                  key={item.type}
                  className="flex min-w-0 items-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2.5"
                >
                  <span
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${item.background} ${item.color}`}
                  >
                    <i
                      className={`${item.icon} text-[10px]`}
                    ></i>
                  </span>

                  <div className="min-w-0">
                    <p className="mb-0 text-sm font-black text-white/80">
                      {item.count.toLocaleString()}
                    </p>

                    <p className="mb-0 truncate text-[9px] font-semibold text-white/32">
                      {item.label}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>


        {total === 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-black/[0.13] px-4 py-6 text-center">
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
              <i className="fa-regular fa-folder-open text-sm"></i>
            </span>

            <p className="mb-1 mt-3 text-sm font-bold text-white/70">
              No assets found
            </p>

            <p className="mb-0 text-xs text-white/35">
              {appliedFilters.length >
              0
                ? "No Media Library assets matched the applied filters."
                : "No assets are currently stored in this Media Library."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};



const ScheduledPostsReadCard = ({
presentation,
message,
onAction,
loading = false,
inlineInteraction = null,
inlineEditingEnabled = false,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
const [
activeActionId,
setActiveActionId,
] = useState(null);

const posts =
Array.isArray(
presentation?.posts
)
? presentation.posts
: [];

const actions =
Array.isArray(
presentation?.actions
)
? presentation.actions
: [];

const manageDuplicateAction =
actions.find(
(action) =>
action?.id ===
"schedule.manageDuplicate"
) ||
null;

const manageDeleteAction =
actions.find(
(action) =>
action?.id ===
"schedule.manageDelete"
) ||
null;

const editing =
inlineInteraction?.type ===
"action_form" &&
[
"posts.duplicate",
"posts.delete",
].includes(
inlineInteraction?.toolName
);

const editingDuplicate =
inlineInteraction?.toolName ===
"posts.duplicate";

const runAction =
async (action) => {
if (
!action ||
loading ||
activeActionId ||
editing
) {
return;
}

setActiveActionId(
action.id
);

try {
await onAction?.({
message,
action,
fieldKeys: [],
});
} finally {
setActiveActionId(
null
);
}
};

return (
<div className="overflow-hidden rounded-2xl border border-cyan-300/[0.13] bg-gradient-to-b from-cyan-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="min-w-0 flex-1">
<div className="mb-2 flex items-center gap-2">
<span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-400/15 text-cyan-100">
<i className="fa-solid fa-calendar-days text-sm"></i>
</span>

<div>
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-cyan-100/55">
Publishing Queue
</span>

<h3 className="mb-0 text-base font-black text-white">
{presentation.title ||
"Publishing Queue"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{editing
? editingDuplicate
? "Select the exact publishing records to duplicate. Every selected record will become a separate new draft after confirmation."
: "Select the exact publishing records to delete. Nothing will be removed until you review and confirm."
: presentation.description}
</p>
</div>

{editing ? (
<span
className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${
editingDuplicate
? "bg-violet-400/15 text-violet-100"
: "bg-red-400/12 text-red-100"
}`}
>
<i
className={`fa-solid ${
editingDuplicate
? "fa-copy"
: "fa-trash"
} text-[9px]`}
></i>

{editingDuplicate
? "Selecting duplicates"
: "Selecting deletions"}
</span>
) : (
<div className="flex flex-wrap items-center gap-2">
{manageDuplicateAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
manageDuplicateAction
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.07] px-3 text-[10px] font-bold text-white/65 transition hover:bg-violet-400/15 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
manageDuplicateAction.id
? "fa-spinner fa-spin"
: "fa-copy"
} text-[9px]`}
></i>
Duplicate posts
</button>
)}

{manageDeleteAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
manageDeleteAction
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-red-400/10 px-3 text-[10px] font-bold text-red-100/75 transition hover:bg-red-400/20 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
manageDeleteAction.id
? "fa-spinner fa-spin"
: "fa-trash"
} text-[9px]`}
></i>
Delete posts
</button>
)}
</div>
)}
</div>

{editing ? (
<div className="border-t border-white/[0.07] bg-black/[0.12] p-3">
<ActionDraftForm
interaction={
inlineInteraction
}
enabled={
inlineEditingEnabled
}
loading={
loading
}
loadRemoteOptions={
loadRemoteOptions
}
uploadMedia={
uploadActionDraftMedia
}
onSubmit={
onSubmitActionDraft
}
onCancel={
onCancelActionDraft
}
/>
</div>
) : posts.length > 0 ? (
<div className="grid grid-cols-1 gap-3 border-t border-white/[0.07] p-3 sm:grid-cols-2">
{posts.map(
(post) => {
const postActions =
Array.isArray(
post.actions
)
? post.actions
: [];

const duplicateAction =
postActions.find(
(action) =>
action?.operation ===
"duplicate"
) ||
null;

const deleteAction =
postActions.find(
(action) =>
action?.operation ===
"delete"
) ||
null;

const statusDisplay =
getScheduledPostStatusDisplay(
post.status
);

const hasFailure =
Boolean(
post.failure?.reason ||
post.failure?.type ||
post.failure?.code
);

return (
<article
key={
post.key ||
post.id
}
className={`min-w-0 overflow-hidden rounded-xl border bg-black/[0.14] ${
hasFailure
? "border-red-300/[0.13]"
: "border-white/[0.075]"
}`}
>
<PublishedPostMediaPreview
post={
post
}
/>

<div className="p-3">
<div className="mb-2 flex items-start justify-between gap-3">
<div className="min-w-0">
<p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-white/30">
{String(
post.mediaType ||
"text"
).replace(
/_/g,
" "
)}
</p>

<h4
className="mb-0 line-clamp-1 text-xs font-bold text-white/75"
title={
post.title ||
post.message ||
"No caption"
}
>
{post.title ||
post.message ||
"No caption"}
</h4>
</div>

<span
className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[9px] font-bold ${statusDisplay.badge}`}
>
<i
className={`${statusDisplay.icon} text-[8px]`}
></i>
{statusDisplay.label}
</span>
</div>

{post.title &&
post.message &&
post.message !==
post.title && (
<p
className="mb-0 line-clamp-4 whitespace-pre-wrap break-words text-xs leading-relaxed text-white/58"
title={
post.message
}
>
{post.message}
</p>
)}

<div className="mt-3">
<p className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-white/25">
Destinations
</p>

<ScheduledPostDestinations
post={
post
}
/>
</div>

<div className="mt-3 flex flex-wrap items-center gap-1.5">
{post.scheduledTime ? (
<span className="rounded-full bg-blue-400/10 px-2 py-1 text-[9px] font-semibold text-blue-100/60">
<i className="fa-regular fa-clock mr-1"></i>
{new Date(
post.scheduledTime
).toLocaleString()}
</span>
) : (
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/30">
Not scheduled
</span>
)}

{post.media?.length > 1 && (
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/35">
{post.media.length} media
</span>
)}

{Number(
post.failure?.attemptCount ||
0
) > 0 && (
<span className="rounded-full bg-amber-400/10 px-2 py-1 text-[9px] font-semibold text-amber-100/60">
{post.failure.attemptCount} attempt
{post.failure.attemptCount ===
1
? ""
: "s"}
</span>
)}

{post.matchSource ===
"knowledge_embedding" && (
<span className="rounded-full bg-cyan-400/10 px-2 py-1 text-[9px] font-semibold text-cyan-100/60">
Semantic match
</span>
)}
</div>
</div>

{hasFailure && (
<div className="border-t border-red-300/[0.08] bg-red-400/[0.045] px-3 py-2.5">
<div className="flex items-start gap-2">
<i className="fa-solid fa-circle-exclamation mt-0.5 text-[10px] text-red-200/70"></i>

<div className="min-w-0">
<p className="mb-0 text-[10px] font-bold text-red-100/75">
{post.failure.type
? String(
post.failure.type
).replace(
/_/g,
" "
)
: "Publishing failure"}
</p>

{post.failure.reason && (
<p className="mb-0 mt-1 whitespace-pre-wrap break-words text-[10px] leading-relaxed text-red-100/50">
{post.failure.reason}
</p>
)}

{post.failure.code && (
<p className="mb-0 mt-1 text-[9px] font-semibold text-red-100/35">
Code:{" "}
{post.failure.code}
{post.failure.subcode
? ` · ${post.failure.subcode}`
: ""}
</p>
)}
</div>
</div>
</div>
)}

<div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.055] px-3 py-2.5">
{post.platformPostUrl && (
<a
href={
post.platformPostUrl
}
target="_blank"
rel="noreferrer"
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.055] px-2.5 text-[10px] font-bold text-white/50 transition hover:bg-white/[0.1] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
Open
</a>
)}

{duplicateAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
duplicateAction
)
}
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-400/10 px-2.5 text-[10px] font-bold text-violet-100/70 transition hover:bg-violet-400/20 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
duplicateAction.id
? "fa-spinner fa-spin"
: "fa-copy"
} text-[9px]`}
></i>
Duplicate
</button>
)}

{deleteAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
deleteAction
)
}
className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg bg-red-400/10 px-2.5 text-[10px] font-bold text-red-100/65 transition hover:bg-red-400/20 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
deleteAction.id
? "fa-spinner fa-spin"
: "fa-trash"
} text-[9px]`}
></i>
Delete
</button>
)}
</div>
</article>
);
}
)}
</div>
) : (
<div className="border-t border-white/[0.07] px-4 py-8 text-center">
<span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
<i className="fa-regular fa-calendar-xmark text-sm"></i>
</span>

<p className="mb-1 mt-3 text-sm font-bold text-white/70">
No publishing records found
</p>

<p className="mb-0 text-xs text-white/35">
No drafts, schedules, queue records, or failures matched the requested filters.
</p>
</div>
)}
</div>
);
};

const PublishedPostsReadCard = ({
presentation,
message,
onAction,
loading = false,
inlineInteraction = null,
inlineEditingEnabled = false,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
const [
activeActionId,
setActiveActionId,
] = useState(null);

const posts =
Array.isArray(
presentation?.posts
)
? presentation.posts
: [];

const actions =
Array.isArray(
presentation?.actions
)
? presentation.actions
: [];

const manageDuplicateAction =
actions.find(
(action) =>
action?.id ===
"posts.manageDuplicate"
) ||
null;

const manageDeleteAction =
actions.find(
(action) =>
action?.id ===
"posts.manageDelete"
) ||
null;

const editing =
inlineInteraction?.type ===
"action_form" &&
[
"posts.duplicate",
"posts.delete",
].includes(
inlineInteraction?.toolName
);

const editingDuplicate =
inlineInteraction?.toolName ===
"posts.duplicate";

const runAction =
async (action) => {
if (
!action ||
loading ||
activeActionId ||
editing
) {
return;
}

setActiveActionId(
action.id
);

try {
await onAction?.({
message,
action,
fieldKeys: [],
});
} finally {
setActiveActionId(
null
);
}
};

return (
<div className="overflow-hidden rounded-2xl border border-fuchsia-300/[0.13] bg-gradient-to-b from-fuchsia-400/[0.055] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="min-w-0 flex-1">
<div className="mb-2 flex items-center gap-2">
<span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fuchsia-400/15 text-fuchsia-100">
<i className="fa-solid fa-photo-film text-sm"></i>
</span>

<div>
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-fuchsia-100/55">
Published Content
</span>

<h3 className="mb-0 text-base font-black text-white">
{presentation.title ||
"Published Posts"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{editing
? editingDuplicate
? "Select the exact published posts to duplicate. Each selected post will become a separate new draft after confirmation."
: "Select the exact published posts to delete. Published deletion also removes supported posts from their social platforms after confirmation."
: presentation.description}
</p>
</div>

{editing ? (
<span
className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold ${
editingDuplicate
? "bg-violet-400/15 text-violet-100"
: "bg-red-400/12 text-red-100"
}`}
>
<i
className={`fa-solid ${
editingDuplicate
? "fa-copy"
: "fa-trash"
} text-[9px]`}
></i>
{editingDuplicate
? "Selecting duplicates"
: "Selecting deletions"}
</span>
) : (
<div className="flex flex-wrap items-center gap-2">
{manageDuplicateAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
manageDuplicateAction
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.07] px-3 text-[10px] font-bold text-white/65 transition hover:bg-violet-400/15 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
manageDuplicateAction.id
? "fa-spinner fa-spin"
: "fa-copy"
} text-[9px]`}
></i>
Duplicate posts
</button>
)}

{manageDeleteAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
manageDeleteAction
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-red-400/10 px-3 text-[10px] font-bold text-red-100/75 transition hover:bg-red-400/20 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
manageDeleteAction.id
? "fa-spinner fa-spin"
: "fa-trash"
} text-[9px]`}
></i>
Delete posts
</button>
)}
</div>
)}
</div>

{editing ? (
<div className="border-t border-white/[0.07] bg-black/[0.12] p-3">
<ActionDraftForm
interaction={
inlineInteraction
}
enabled={
inlineEditingEnabled
}
loading={
loading
}
loadRemoteOptions={
loadRemoteOptions
}
uploadMedia={
uploadActionDraftMedia
}
onSubmit={
onSubmitActionDraft
}
onCancel={
onCancelActionDraft
}
/>
</div>
) : posts.length > 0 ? (
<div className="grid grid-cols-1 gap-3 border-t border-white/[0.07] p-3 sm:grid-cols-2">
{posts.map(
(post) => {
const postActions =
Array.isArray(
post.actions
)
? post.actions
: [];

const duplicateAction =
postActions.find(
(action) =>
action?.operation ===
"duplicate"
) ||
null;

const deleteAction =
postActions.find(
(action) =>
action?.operation ===
"delete"
) ||
null;

const platformDisplay =
getConfirmationPlatformDisplay(
post.platform
);

return (
<article
key={
post.key ||
post.id
}
className="min-w-0 overflow-hidden rounded-xl border border-white/[0.075] bg-black/[0.14]"
>
<PublishedPostMediaPreview
post={
post
}
/>

<div className="p-3">
<div className="mb-2 flex items-start justify-between gap-3">
<div className="min-w-0">
<div className="mb-1 flex min-w-0 items-center gap-1.5">
<i
className={`${platformDisplay.icon} text-[11px] ${platformDisplay.color}`}
></i>

<span className="truncate text-xs font-bold text-white/75">
{post.profile?.name ||
platformDisplay.label}
</span>
</div>

<p className="mb-0 text-[9px] font-semibold uppercase tracking-wide text-white/30">
{platformDisplay.label}
{" · "}
{String(
post.postType ||
"text"
).replace(
/_/g,
" "
)}
</p>
</div>

<span className="rounded-full bg-emerald-400/10 px-2 py-1 text-[9px] font-bold text-emerald-100/65">
{String(
post.publishStatus ||
"published"
).replace(
/_/g,
" "
)}
</span>
</div>

<p
className="mb-0 line-clamp-4 whitespace-pre-wrap break-words text-xs leading-relaxed text-white/62"
title={
post.message ||
"No caption"
}
>
{post.message ||
"No caption"}
</p>

<div className="mt-3 flex flex-wrap items-center gap-1.5">
{post.publishedAt && (
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/35">
<i className="fa-regular fa-clock mr-1"></i>
{new Date(
post.publishedAt
).toLocaleString()}
</span>
)}

{post.media?.length > 1 && (
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/35">
{post.media.length} media
</span>
)}

{post.matchSource ===
"knowledge_embedding" && (
<span className="rounded-full bg-fuchsia-400/10 px-2 py-1 text-[9px] font-semibold text-fuchsia-100/60">
Semantic match
</span>
)}
</div>
</div>

<div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.055] px-3 py-2.5">
{post.officialUrl && (
<a
href={
post.officialUrl
}
target="_blank"
rel="noreferrer"
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/[0.055] px-2.5 text-[10px] font-bold text-white/50 transition hover:bg-white/[0.1] hover:text-white"
>
<i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
Open
</a>
)}

{duplicateAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
duplicateAction
)
}
className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-400/10 px-2.5 text-[10px] font-bold text-violet-100/70 transition hover:bg-violet-400/20 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
duplicateAction.id
? "fa-spinner fa-spin"
: "fa-copy"
} text-[9px]`}
></i>
Duplicate
</button>
)}

{deleteAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
deleteAction
)
}
className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg bg-red-400/10 px-2.5 text-[10px] font-bold text-red-100/65 transition hover:bg-red-400/20 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
deleteAction.id
? "fa-spinner fa-spin"
: "fa-trash"
} text-[9px]`}
></i>
Delete
</button>
)}
</div>
</article>
);
}
)}
</div>
) : (
<div className="border-t border-white/[0.07] px-4 py-8 text-center">
<span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
<i className="fa-regular fa-folder-open text-sm"></i>
</span>

<p className="mb-1 mt-3 text-sm font-bold text-white/70">
No published posts found
</p>

<p className="mb-0 text-xs text-white/35">
No current published posts matched the requested filters.
</p>
</div>
)}
</div>
);
};

const ConnectedAccountsReadCard = ({
presentation,
message,
onAction,
onConnect,
loading = false,
inlineInteraction = null,
inlineEditingEnabled = false,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
const [
activeActionId,
setActiveActionId,
] = useState(null);

const accounts =
Array.isArray(
presentation?.accounts
)
? presentation.accounts
: [];

const actions =
Array.isArray(
presentation?.actions
)
? presentation.actions
: [];

const connectAction =
actions.find(
(action) =>
action?.id ===
"accounts.connect"
) ||
null;

const manageAction =
actions.find(
(action) =>
action?.id ===
"accounts.manageDisconnect"
) ||
null;

const editing =
inlineInteraction?.type ===
"action_form" &&
inlineInteraction?.toolName ===
"accounts.disconnect";

const runAction =
async (action) => {
if (
!action ||
loading ||
activeActionId ||
editing
) {
return;
}

setActiveActionId(
action.id
);

try {
await onAction?.({
message,
action,
fieldKeys: [],
});
} finally {
setActiveActionId(
null
);
}
};

const startConnect = () => {
if (
loading ||
editing ||
!connectAction
) {
return;
}

onConnect?.({
interaction: {
type:
"account_connect",
version: 1,
status:
"open",
title:
"Connect a social profile",
description:
"Choose a platform to continue through the existing secure connection flow.",
groupName:
presentation.title ||
null,
requestedPlatforms:
presentation.connectPlatforms ||
[],
platforms:
presentation.connectPlatforms ||
[],
},
});
};

return (
<div className="overflow-hidden rounded-2xl border border-blue-300/[0.14] bg-gradient-to-b from-blue-400/[0.06] to-black/[0.08] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="min-w-0 flex-1">
<div className="mb-2 flex items-center gap-2">
<span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-400/15 text-blue-100">
<i className="fa-solid fa-link text-sm"></i>
</span>

<div>
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-blue-100/55">
Social Accounts
</span>

<h3 className="mb-0 text-base font-black text-white">
{presentation.title ||
"Connected Profiles"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{editing
? "Select the exact profiles to disconnect. Nothing will be removed until you review and confirm."
: presentation.description}
</p>
</div>

{editing ? (
<span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-red-400/12 px-2.5 text-[10px] font-bold text-red-100">
<i className="fa-solid fa-user-minus text-[9px]"></i>
Managing
</span>
) : (
<div className="flex flex-wrap items-center gap-2">
{manageAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
manageAction
)
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.07] px-3 text-[10px] font-bold text-white/65 transition hover:bg-white/[0.12] hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
manageAction.id
? "fa-spinner fa-spin"
: "fa-sliders"
} text-[9px]`}
></i>
Manage
</button>
)}

{connectAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={
startConnect
}
className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-[10px] font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-45"
>
<i className="fa-solid fa-plus text-[9px]"></i>
{connectAction.label ||
"Connect profile"}
</button>
)}
</div>
)}
</div>

{editing ? (
<div className="border-t border-white/[0.07] bg-black/[0.12] p-3">
<ActionDraftForm
interaction={
inlineInteraction
}
enabled={
inlineEditingEnabled
}
loading={
loading
}
loadRemoteOptions={
loadRemoteOptions
}
uploadMedia={
uploadActionDraftMedia
}
onSubmit={
onSubmitActionDraft
}
onCancel={
onCancelActionDraft
}
/>
</div>
) : accounts.length > 0 ? (
<div className="grid grid-cols-1 gap-2 border-t border-white/[0.07] p-3 sm:grid-cols-2">
{accounts.map(
(account) => {
const disconnectAction =
account.disconnectAction ||
null;

return (
<div
key={
account.key ||
`${account.platform}:${account.profileId}`
}
className="min-w-0 overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.13] p-3"
>
<div className="flex items-start gap-3">
<div className="min-w-0 flex-1">
<ConnectedAccountIdentity
account={
account
}
/>
</div>

{disconnectAction && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction(
disconnectAction
)
}
title={`Disconnect ${account.profileName}`}
className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.05] text-white/35 transition hover:bg-red-400/12 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
disconnectAction.id
? "fa-spinner fa-spin"
: "fa-link-slash"
} text-[9px]`}
></i>
</button>
)}
</div>

<div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-white/[0.05] pt-2.5">
<span className="rounded-full bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white/40">
{account.accountType ||
"Connected profile"}
</span>

<span className="rounded-full bg-emerald-400/10 px-2 py-1 text-[9px] font-bold text-emerald-100/65">
Connected
</span>
</div>

{account.connectedAt && (
<p className="mb-0 mt-2 text-[9px] font-medium text-white/25">
Connected{" "}
{new Date(
account.connectedAt
).toLocaleDateString()}
</p>
)}
</div>
);
}
)}
</div>
) : (
<div className="border-t border-white/[0.07] px-4 py-8 text-center">
<span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-white/30">
<i className="fa-solid fa-link-slash text-sm"></i>
</span>

<p className="mb-1 mt-3 text-sm font-bold text-white/70">
No profiles connected
</p>

<p className="mb-0 text-xs text-white/35">
Connect a social profile to publish content and view account data.
</p>
</div>
)}
</div>
);
};

const isHexColor = (
value
) => {
return /^#[0-9a-f]{3,8}$/i.test(
String(value || "").trim()
);
};

const normalizePresentationList = (
value,
{
splitStrings = false,
} = {}
) => {
if (Array.isArray(value)) {
return value;
}

if (
value === undefined ||
value === null ||
value === ""
) {
return [];
}

if (
splitStrings &&
typeof value ===
"string"
) {
return value
.split(/[,;\n]+/)
.map(
(item) =>
item.trim()
)
.filter(Boolean);
}

return [value];
};

const BrandKitFieldValue = ({
field,
}) => {
const value =
field?.value;

if (
value === undefined ||
value === null ||
value === "" ||
(
Array.isArray(value) &&
value.length === 0
) ||
(
typeof value === "object" &&
!Array.isArray(value) &&
Object.keys(value).length === 0
)
) {
return (
<span className="text-xs font-semibold text-white/28">
Not configured
</span>
);
}

if (
field.type ===
"images"
) {
const images =
normalizePresentationList(
value
)
.map((item) => {
if (
typeof item ===
"string"
) {
return {
url: item,
name:
"Brand image",
};
}

if (
item &&
typeof item ===
"object"
) {
return {
url:
item.url ||
item.imageUrl ||
item.src ||
null,
name:
item.name ||
item.title ||
"Brand image",
};
}

return null;
})
.filter(
(item) =>
item?.url
);

return (
<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
{images.map(
(image, index) => (
<a
key={`${image.url}-${index}`}
href={image.url}
target="_blank"
rel="noreferrer"
className="group overflow-hidden rounded-xl border border-white/[0.08] bg-black/20"
>
<img
src={image.url}
alt={
image.name
}
loading="lazy"
className="aspect-square w-full object-cover transition duration-200 group-hover:scale-[1.02]"
/>
<div className="truncate px-2 py-1.5 text-[10px] font-semibold text-white/45">
{image.name}
</div>
</a>
)
)}
</div>
);
}

if (
field.type ===
"colors"
) {
const colors =
normalizePresentationList(
value,
{
splitStrings: true,
}
)
.map(
(color) =>
typeof color ===
"string"
? color
: color?.hex ||
color?.value ||
color?.color ||
""
)
.filter(Boolean);

return (
<div className="flex flex-wrap gap-2">
{colors.map(
(color, index) => (
<span
key={`${color}-${index}`}
className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-black/20 px-2.5 py-2 text-[10px] font-bold text-white/65"
>
<span
className="h-5 w-5 rounded-md border border-white/15"
style={{
backgroundColor:
isHexColor(color)
? color
: "transparent",
}}
></span>
{color}
</span>
)
)}
</div>
);
}

if (
field.type ===
"styles" &&
typeof value ===
"object" &&
!Array.isArray(value)
) {
return (
<div className="space-y-2">
{Object.entries(value).map(
([
name,
description,
]) => (
<div
key={name}
className="rounded-lg border border-white/[0.07] bg-black/[0.14] px-3 py-2.5"
>
<p className="mb-1 text-xs font-bold text-white/75">
{String(name).replace(
/_/g,
" "
)}
</p>
<p className="mb-0 whitespace-pre-wrap text-xs leading-relaxed text-white/45">
{String(
description ||
"Not configured"
)}
</p>
</div>
)
)}
</div>
);
}

if (
Array.isArray(value)
) {
return (
<div className="flex flex-wrap gap-1.5">
{value.map(
(item, index) => {
const label =
typeof item ===
"object"
? item.text ||
item.label ||
item.name ||
JSON.stringify(item)
: String(item);

return (
<span
key={`${label}-${index}`}
className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[10px] font-semibold text-white/55"
>
{label}
</span>
);
}
)}
</div>
);
}

if (
typeof value ===
"object"
) {
return (
<pre className="mb-0 overflow-x-auto whitespace-pre-wrap rounded-lg bg-black/20 p-2.5 text-[10px] leading-relaxed text-white/55">
{JSON.stringify(
value,
null,
2
)}
</pre>
);
}

return (
<p
className="mb-0 whitespace-pre-wrap break-words text-xs leading-relaxed text-white/62"
style={
field.type ===
"font"
? {
fontFamily:
String(value),
}
: undefined
}
>
{String(value)}
</p>
);
};

const BrandKitReadCard = ({
presentation,
message,
onAction,
loading = false,
inlineInteraction = null,
inlineEditingEnabled = false,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
const [
activeActionId,
setActiveActionId,
] = useState(null);

const [
expandedSection,
setExpandedSection,
] = useState(null);

const sections =
Array.isArray(
presentation?.sections
)
? presentation.sections
: [];

const primaryAction =
Array.isArray(
presentation?.actions
)
? presentation.actions.find(
(action) =>
action?.id ===
"brandKit.edit"
) ||
presentation.actions[0] ||
null
: null;

const editing =
inlineInteraction?.type ===
"action_form";

const runAction = async ({
action,
fieldKeys = [],
}) => {
if (
loading ||
activeActionId ||
editing ||
!action
) {
return;
}

setActiveActionId(
action.id
);

try {
await onAction?.({
message,
action,
fieldKeys,
});
} finally {
setActiveActionId(
null
);
}
};

return (
<div
className={`copilot-brand-kit-read-card w-full min-w-0 max-w-full overflow-hidden rounded-2xl bg-[#181a1f] ${
editing ? "is-editing" : "is-read-only"
}`}
>
<style>
{`
.copilot-brand-kit-read-card,
.copilot-brand-kit-read-card * {
box-sizing: border-box;
}

.copilot-brand-kit-read-card.is-read-only,
.copilot-brand-kit-read-card.is-read-only [class*="border"] {
border: 0 !important;
outline: 0 !important;
}

.copilot-brand-kit-read-card.is-read-only [class*="ring-"] {
box-shadow: none !important;
}

.copilot-brand-kit-read-card p,
.copilot-brand-kit-read-card h3,
.copilot-brand-kit-read-card span {
overflow-wrap: anywhere;
word-break: break-word;
}

.copilot-brand-kit-read-card button,
.copilot-brand-kit-read-card a {
max-width: 100%;
}

@media (max-width: 639px) {
.copilot-brand-kit-header {
align-items: stretch !important;
}

.copilot-brand-kit-primary-action {
width: 100%;
justify-content: center;
}

.copilot-brand-kit-section-header {
align-items: flex-start !important;
flex-wrap: wrap;
}

.copilot-brand-kit-section-action {
margin-left: 40px;
}
}
`}
</style>
<div className="copilot-brand-kit-header flex flex-col items-stretch justify-between gap-4 px-4 py-4 sm:flex-row sm:items-start">
<div className="min-w-0 flex-1">
<div className="mb-2 flex min-w-0 items-center gap-2">
<span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-violet-400/15 text-violet-100">
<i className="fa-solid fa-palette text-sm"></i>
</span>  
<div className="min-w-0">
<span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-violet-100/55">
Brand Kit
</span>  
<h3 className="mb-0 break-words text-base font-black text-white">
{presentation.title ||
"Brand Kit"}
</h3>
</div>
</div>

<p className="mb-0 max-w-xl text-xs leading-relaxed text-white/42">
{editing
? "Edit the selected values below. Your Brand Kit will not change until you review and confirm the update."
: presentation.description}
</p>
</div>

{editing ? (
<span className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-violet-400/15 px-2.5 text-[10px] font-bold text-violet-100">
<i className="fa-solid fa-pen text-[9px]"></i>
Editing
</span>
) : primaryAction ? (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction({
action:
primaryAction,
fieldKeys:
primaryAction.fieldKeys ||
[],
})
}
className="copilot-brand-kit-primary-action inline-flex h-9 flex-shrink-0 items-center gap-2 rounded-lg bg-white px-3.5 text-xs font-bold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
primaryAction.id
? "fa-spinner fa-spin"
: "fa-pen"
} text-[10px]`}
></i>
Edit Brand Kit
</button>
) : null}
</div>

{editing ? (
<div className="border-t border-white/[0.07] bg-black/[0.12] p-3">
<ActionDraftForm
interaction={
inlineInteraction
}
enabled={
inlineEditingEnabled
}
loading={
loading
}
loadRemoteOptions={
loadRemoteOptions
}
uploadMedia={
uploadActionDraftMedia
}
onSubmit={
onSubmitActionDraft
}
onCancel={
onCancelActionDraft
}
/>
</div>
) : (
<div className="space-y-2 bg-black/[0.08] p-3 sm:p-4">
{sections.map(
(section) => {
const expanded =
expandedSection ===
section.key;  

return (
<div
key={section.key}
className="overflow-hidden rounded-xl bg-black/[0.16]"
>
<div className="copilot-brand-kit-section-header flex min-w-0 items-center gap-2 px-3.5 py-3">
<button
type="button"
onClick={() =>
setExpandedSection(
expanded
? null
: section.key
)
}
className="flex min-w-0 flex-1 items-center gap-3 text-left"
aria-expanded={
expanded
}
>
<span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055] text-white/45">
<i
className={`fa-solid ${
section.key ===
"business_profile"
? "fa-building"
: section.key ===
"writing_style"
? "fa-pen-nib"
: section.key ===
"positioning"
? "fa-bullseye"
: section.key ===
"visual_identity"
? "fa-image"
: "fa-lightbulb"
} text-[10px]`}
></i>
</span>

<span className="min-w-0 flex-1">
<span className="block truncate text-sm font-bold text-white/80">
{section.title}
</span>

<span className="mt-0.5 block truncate text-[10px] text-white/32">
{section.description}
</span>
</span>

<i
className={`fa-solid fa-chevron-down flex-shrink-0 text-[9px] text-white/30 transition-transform ${
expanded
? "rotate-180"
: ""
}`}
></i>
</button>

{section.action && (
<button
type="button"
disabled={
loading ||
Boolean(
activeActionId
)
}
onClick={() =>
runAction({
action:
section.action,
fieldKeys:
section.fieldKeys ||
[],
})
}
className="copilot-brand-kit-section-action inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/[0.07] px-2.5 text-[10px] font-bold text-white/55 transition hover:bg-white/[0.12] hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
>
<i
className={`fa-solid ${
activeActionId ===
section.action.id
? "fa-spinner fa-spin"
: "fa-pen"
} text-[9px]`}
></i>
Edit
</button>
)}
</div>

{expanded && (
<div className="grid min-w-0 grid-cols-1 gap-2 bg-black/[0.08] p-3 sm:grid-cols-2">
{(
Array.isArray(
section.fields
)
? section.fields
: []
).map(
(field) => (
<div
key={field.key}
className={`min-w-0 max-w-full overflow-hidden rounded-xl bg-white/[0.035] px-3 py-3 ${
[
"images",
"styles",
"long_text",
"topics",
].includes(
field.type
)
? "sm:col-span-2"
: ""
}`}
>
<p className="mb-2 text-[9px] font-bold uppercase tracking-[0.08em] text-white/28">
{field.label}
</p>

<BrandKitFieldValue
field={field}
/>
</div>
)
)}
</div>
)}
</div>
);
}
)}
</div>
)}
</div>
);
};

const normalizeCopilotDisplayText = (
value = ""
) => {
return String(value || "")
.replace(
/^\s{0,3}#{1,6}\s+/gm,
""
)
.replace(/\*\*/g, "")
.replace(/__/g, "")
.trim();
};

const appendStructuredCitations = (
text = "",
citations = []
) => {
const normalizedText =
normalizeCopilotDisplayText(
text
);
const normalizedCitations = [
...new Set(
(
Array.isArray(citations)
? citations
: []
)
.map((citation) =>
String(citation || "")
.trim()
.toUpperCase()
)
.filter((citation) =>
/^(S|T|A)\d+$/.test(
citation
)
)
),
];

if (
!normalizedText ||
normalizedCitations.length === 0
) {
return normalizedText;
}

const existingCitations =
new Set(
(
normalizedText.match(
/\[(S|T|A)\d+\]/gi
) || []
)
.map((citation) =>
citation
.slice(1, -1)
.toUpperCase()
)
);

const missingCitations =
normalizedCitations.filter(
(citation) =>
!existingCitations.has(
citation
)
);

if (
missingCitations.length === 0
) {
return normalizedText;
}

return `${normalizedText} ${missingCitations
.map((citation) =>
`[${citation}]`
)
.join(" ")}`;
};

const StructuredAssistantContent = ({
presentation,
}) => {
if (
!presentation ||
typeof presentation !==
"object" ||
Array.isArray(presentation)
) {
return null;
}

const title =
normalizeCopilotDisplayText(
presentation.title
);
const summary =
appendStructuredCitations(
presentation.summary,
presentation.citations
);
const directAnswer =
appendStructuredCitations(
presentation.directAnswer,
presentation.citations
);
const sections =
Array.isArray(
presentation.sections
)
? presentation.sections
: [];
const tables =
Array.isArray(
presentation.tables
)
? presentation.tables
: [];
const missingContext =
Array.isArray(
presentation.missingContext
)
? presentation.missingContext
.map(
normalizeCopilotDisplayText
)
.filter(Boolean)
: [];
const nextActions =
Array.isArray(
presentation.nextActions
)
? presentation.nextActions
: [];

return (
<div className="space-y-3 text-sm leading-relaxed text-white/75">
{title && (
<h3 className="mb-0 text-base font-medium leading-snug text-white/90">
{title}
</h3>
)}

{summary && (
<p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-white/65">
{summary}
</p>
)}

{directAnswer && (
<p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-white/75">
{directAnswer}
</p>
)}

{sections.map(
(section, sectionIndex) => {
const heading =
normalizeCopilotDisplayText(
section?.heading
);
const items =
Array.isArray(
section?.items
)
? section.items
: [];

if (
!heading &&
items.length === 0
) {
return null;
}

return (
<section
key={`structured-section-${sectionIndex}`}
className="space-y-2"
>
{heading && (
<h4 className="mb-0 text-[13px] font-medium leading-snug text-white/85">
{heading}
</h4>
)}

{items.length > 0 && (
<ul className="mb-0 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-white/72 marker:text-white/35">
{items.map(
(item, itemIndex) => {
const itemText =
typeof item ===
"string"
? normalizeCopilotDisplayText(
item
)
: appendStructuredCitations(
item?.text ||
item?.label ||
"",
item?.citations
);

if (!itemText) {
return null;
}

return (
<li
key={`structured-section-${sectionIndex}-item-${itemIndex}`}
>
{itemText}
</li>
);
}
)}
</ul>
)}
</section>
);
}
)}

{tables.map(
(table, tableIndex) => {
const tableTitle =
normalizeCopilotDisplayText(
table?.title
);
const columns =
Array.isArray(
table?.columns
)
? table.columns.map(
normalizeCopilotDisplayText
)
: [];
const rows =
Array.isArray(
table?.rows
)
? table.rows
: [];

if (
columns.length === 0 ||
rows.length === 0
) {
return null;
}

return (
<section
key={`structured-table-${tableIndex}`}
className="space-y-2"
>
{tableTitle && (
<h4 className="mb-0 text-[13px] font-medium leading-snug text-white/85">
{tableTitle}
</h4>
)}

<div className="overflow-x-auto rounded-lg border border-white/[0.1] bg-black/[0.16]">
<table className="w-full border-collapse text-left text-xs text-white/70">
<thead className="bg-white/[0.07] text-white/85">
<tr>
{columns.map(
(column, columnIndex) => (
<th
key={`structured-table-${tableIndex}-column-${columnIndex}`}
className="border-b border-white/[0.1] px-3 py-2.5 font-medium text-white/85"
>
{column}
</th>
)
)}
</tr>
</thead>

<tbody>
{rows.map(
(row, rowIndex) => (
<tr
key={`structured-table-${tableIndex}-row-${rowIndex}`}
className="border-b border-white/[0.06] text-white/70 last:border-b-0 odd:bg-white/[0.015]"
>
{columns.map(
(
_,
columnIndex
) => (
<td
key={`structured-table-${tableIndex}-row-${rowIndex}-cell-${columnIndex}`}
className="px-3 py-2.5 align-top leading-relaxed text-white/70"
>
{normalizeCopilotDisplayText(
Array.isArray(row)
? row[columnIndex]
: ""
)}
</td>
)
)}
</tr>
)
)}
</tbody>
</table>
</div>
</section>
);
}
)}

{missingContext.length > 0 && (
<section className="space-y-2">
<h4 className="mb-0 text-[13px] font-medium leading-snug text-white/85">
Missing context
</h4>

<ul className="mb-0 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-white/72 marker:text-white/35">
{missingContext.map(
(item, index) => (
<li
key={`structured-missing-${index}`}
>
{item}
</li>
)
)}
</ul>
</section>
)}

{nextActions.length > 0 && (
<section className="space-y-2">
<h4 className="mb-0 text-[13px] font-medium leading-snug text-white/85">
Next
</h4>

<ul className="mb-0 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-white/72 marker:text-white/35">
{nextActions.map(
(action, index) => {
const text =
normalizeCopilotDisplayText(
typeof action ===
"string"
? action
: action?.text ||
action?.label ||
""
);

if (!text) {
return null;
}

return (
<li
key={`structured-next-${index}`}
>
{text}
</li>
);
}
)}
</ul>
</section>
)}
</div>
);
};

const ReadPresentationCard = ({
presentation,
message,
onReadResultAction,
onConnectAccount,
profileSelectionEnabled,
actionDraftStatus,
clarificationInteraction,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
if (
!presentation ||
typeof presentation !==
"object"
) {
return null;
}

if (
presentation.type ===
"brand_kit"
) {
return (
<BrandKitReadCard
presentation={
presentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
);
}

if (
presentation.type ===
"connected_accounts"
) {
return (
<ConnectedAccountsReadCard
presentation={
presentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
onConnect={
onConnectAccount
}
/>
);
}

if (
presentation.type ===
"published_posts"
) {
return (
<PublishedPostsReadCard
presentation={
presentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
);
}

if (
presentation.type ===
"scheduled_posts"
) {
return (
<ScheduledPostsReadCard
presentation={
presentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
);
}

if (
presentation.type ===
"asset_search"
) {
return (
<AssetSearchReadCard
presentation={
presentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
);
}

if (
presentation.type ===
"asset_count"
) {
return (
<AssetCountReadCard
presentation={
presentation
}
/>
);
}

if (
presentation.type ===
"profile_summary"
) {
return (
<ProfileSummaryReadCard
presentation={
presentation
}
/>
);
}

if (
presentation.type ===
"post_reports"
) {
return (
<PostReportsReadCard
presentation={
presentation
}
/>
);
}

if (
presentation.type ===
"apps_history"
) {
return (
<AppsHistoryReadCard
presentation={
presentation
}
/>
);
}

if (
presentation.type ===
"background_jobs"
) {
return (
<BackgroundJobsReadCard
presentation={
presentation
}
/>
);
}

if (
presentation.type ===
"credit_balance"
) {
return (
<CreditBalanceReadCard
presentation={
presentation
}
/>
);
}

return null;
};

const getWorkspaceHealthSection = (
presentation
) => {
const type =
presentation?.type ||
"unknown";

if (
type ===
"connected_accounts"
) {
const count =
Number(
presentation.count ??
presentation.totalConnectedAccounts ??
presentation.accounts?.length ??
0
);
return {
title:
"Connected profiles",
summary:
`${count.toLocaleString()} connected profile${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-link",
iconColor:
"text-blue-200",
statusColor:
count > 0
? "text-emerald-200"
: "text-amber-200",
};
}

if (
type ===
"brand_kit"
) {
const sections =
Array.isArray(
presentation.sections
)
? presentation.sections
: [];
const configuredFields =
sections.reduce(
(total, section) => {
const fields =
Array.isArray(
section?.fields
)
? section.fields
: [];

return total +
fields.filter((field) => {
const value =
field?.value;

if (
value === null ||
value === undefined ||
value === ""
) {
return false;
}

if (
Array.isArray(value)
) {
return value.length > 0;
}

if (
typeof value ===
"object"
) {
return Object.keys(
value
).length > 0;
}

return true;
}).length;
},
0
);

return {
title:
"Brand Kit",
summary:
`${configuredFields.toLocaleString()} configured field${
configuredFields === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-palette",
iconColor:
"text-violet-200",
statusColor:
configuredFields > 0
? "text-emerald-200"
: "text-amber-200",
};
}

if (
type ===
"asset_count"
) {
const count =
Number(
presentation.total ??
presentation.count ??
0
);
return {
title:
"Media Library",
summary:
`${count.toLocaleString()} asset${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-photo-film",
iconColor:
"text-fuchsia-200",
statusColor:
"text-white/70",
};
}

if (
type ===
"asset_search"
) {
const count =
Number(
presentation.returnedCount ??
presentation.assets?.length ??
0
);
return {
title:
"Media Library",
summary:
`${count.toLocaleString()} matching asset${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-photo-film",
iconColor:
"text-fuchsia-200",
statusColor:
"text-white/70",
};
}

if (
type ===
"scheduled_posts"
) {
const count =
Number(
presentation.returnedCount ??
presentation.posts?.length ??
0
);
return {
title:
"Upcoming scheduled posts",
summary:
count > 0
? `${count.toLocaleString()} upcoming post${
count === 1
? ""
: "s"
}`
: "No upcoming posts",
icon:
"fa-solid fa-calendar-days",
iconColor:
"text-cyan-200",
statusColor:
count > 0
? "text-white/70"
: "text-amber-200",
};
}

if (
type ===
"background_jobs"
) {
const jobs =
Array.isArray(
presentation.jobs
)
? presentation.jobs
: [];
const failedCount =
jobs.filter((job) =>
[
"failed",
"stalled",
].includes(
String(
job?.status ||
""
).toLowerCase()
)
).length;

return {
title:
"Failed background jobs",
summary:
failedCount > 0
? `${failedCount.toLocaleString()} failed job${
failedCount === 1
? ""
: "s"
}`
: "No failed jobs",
icon:
"fa-solid fa-gears",
iconColor:
failedCount > 0
? "text-red-200"
: "text-cyan-200",
statusColor:
failedCount > 0
? "text-red-200"
: "text-emerald-200",
};
}

if (
type ===
"apps_history"
) {
const count =
Number(
presentation.transactions?.length ??
presentation.returnedCount ??
0
);
return {
title:
"Recent AI app transactions",
summary:
`${count.toLocaleString()} recent transaction${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-wand-magic-sparkles",
iconColor:
"text-violet-200",
statusColor:
"text-white/70",
};
}

if (
type ===
"credit_balance"
) {
const remaining =
Number(
presentation.remainingCredits ??
0
);
const status =
String(
presentation.status ||
"available"
).toLowerCase();

return {
title:
"Remaining AI credits",
summary:
`${remaining.toLocaleString()} credit${
remaining === 1
? ""
: "s"
} remaining`,
icon:
"fa-solid fa-coins",
iconColor:
"text-amber-200",
statusColor:
status ===
"depleted"
? "text-red-200"
: status ===
"low"
? "text-amber-200"
: "text-emerald-200",
};
}

if (
type ===
"published_posts"
) {
const count =
Number(
presentation.returnedCount ??
presentation.posts?.length ??
0
);
return {
title:
"Published posts",
summary:
`${count.toLocaleString()} post${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-photo-film",
iconColor:
"text-fuchsia-200",
statusColor:
"text-white/70",
};
}

if (
type ===
"profile_summary"
) {
const count =
Number(
presentation.profileCount ??
presentation.profiles?.length ??
0
);
return {
title:
"Profile analytics",
summary:
`${count.toLocaleString()} profile result${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-chart-line",
iconColor:
"text-blue-200",
statusColor:
"text-white/70",
};
}

if (
type ===
"post_reports"
) {
const count =
Number(
presentation.reportCount ??
presentation.reports?.length ??
0
);
return {
title:
"Post analytics",
summary:
`${count.toLocaleString()} report result${
count === 1
? ""
: "s"
}`,
icon:
"fa-solid fa-chart-column",
iconColor:
"text-blue-200",
statusColor:
"text-white/70",
};
}

return {
title:
presentation?.title ||
"Workspace result",
summary:
presentation?.description ||
"Workspace data available",
icon:
"fa-solid fa-circle-info",
iconColor:
"text-white/55",
statusColor:
"text-white/60",
};
};

const WorkspaceHealthReport = ({
presentations,
message,
onReadResultAction,
onConnectAccount,
profileSelectionEnabled,
actionDraftStatus,
clarificationInteraction,
loadRemoteOptions,
uploadActionDraftMedia,
onSubmitActionDraft,
onCancelActionDraft,
}) => {
const items =
Array.isArray(
presentations
)
? presentations.filter(Boolean)
: [];

if (
items.length <= 1
) {
return null;
}

return (
<div className="overflow-hidden rounded-2xl border border-white/[0.09] bg-gradient-to-b from-white/[0.045] to-black/[0.1] shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
<div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
<div className="flex min-w-0 flex-1 items-center gap-3">
<span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-200">
<i className="fa-solid fa-heart-pulse text-sm"></i>
</span>

<div className="min-w-0">
<span className="block text-[9px] font-medium uppercase tracking-[0.1em] text-white/40">
Workspace health
</span>

<h3 className="mb-0 text-base font-medium text-white/90">
Current workspace report
</h3>

<p className="mb-0 mt-1 text-xs leading-relaxed text-white/40">
Expand a section to review its complete details.
</p>
</div>
</div>

<span className="inline-flex h-8 flex-shrink-0 items-center rounded-lg bg-white/[0.06] px-2.5 text-[10px] font-medium text-white/50">
{items.length} sections
</span>
</div>

<div className="space-y-2 border-t border-white/[0.07] p-3">
{items.map(
(
presentation,
index
) => {
const section =
getWorkspaceHealthSection(
presentation
);

return (
<details
key={`${presentation.type}-${presentation.invocationId || index}`}
className="group overflow-hidden rounded-xl border border-white/[0.07] bg-black/[0.14]"
>
<summary className="flex cursor-pointer list-none items-center gap-3 px-3.5 py-3 transition hover:bg-white/[0.025]">
<span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055]">
<i
className={`${section.icon} ${section.iconColor} text-[11px]`}
></i>
</span>

<span className="min-w-0 flex-1">
<span className="block truncate text-xs font-medium text-white/80">
{section.title}
</span>

<span
className={`mt-0.5 block truncate text-[10px] font-medium ${section.statusColor}`}
>
{section.summary}
</span>
</span>

<span className="text-[9px] font-medium text-white/30 group-open:hidden">
Review
</span>

<span className="hidden text-[9px] font-medium text-white/30 group-open:inline">
Hide
</span>

<i className="fa-solid fa-chevron-down text-[8px] text-white/30 transition-transform group-open:rotate-180"></i>
</summary>

<div className="border-t border-white/[0.06] p-2">
<ReadPresentationCard
presentation={
presentation
}
message={
message
}
onReadResultAction={
onReadResultAction
}
onConnectAccount={
onConnectAccount
}
profileSelectionEnabled={
profileSelectionEnabled
}
actionDraftStatus={
actionDraftStatus
}
clarificationInteraction={
clarificationInteraction
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
/>
</div>
</details>
);
}
)}
</div>
</div>
);
};

const PlainAssistantContent = ({
content = "",
}) => {
const normalizedContent =
normalizeCopilotDisplayText(
content
);

if (!normalizedContent) {
return null;
}

return (
<p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-inherit">
{normalizedContent}
</p>
);
};

export default function CopilotMessageBubble({
message,
promptGoal = null,
onCancelGoal,
goalLoading = false,
onReadResultAction,
onConfirmAction,
onCancelAction,
onEditAction,
onSubmitProfileSelection,
onConnectAccount,
  onSubmitPublishingRequirements,
  onSubmitActionDraft,
  onCancelActionDraft,
  loadRemoteOptions,
  uploadActionDraftMedia,
  loadPinterestBoards,
  uploadPublishingMedia,
  profileSelectionEnabled = false,
  activeActionId = null,
  actionStatus = "idle",
  actionDraftStatus = "idle",
  chatStatus = "idle",
}) {
  const [toolDetailsOpen, setToolDetailsOpen] = useState(false);
  const styles = getBubbleStyles(message.role);
  const isToolMessage = message.role === "tool";

  const toolDetailMessages =
    isToolMessage &&
    Array.isArray(message.toolMessages) &&
    message.toolMessages.length > 0
      ? message.toolMessages
      : [message];

  const lastToolMessage =
    toolDetailMessages[toolDetailMessages.length - 1] || message;

  const timestamp = formatTimestamp(
    lastToolMessage.createdAt || message.createdAt
  );

  const toolActivityLabel = toolDetailMessages.some(
    (toolMessage) =>
      getToolActivityLabel(toolMessage) === "Exploring workspace"
  )
    ? "Exploring workspace"
    : "Reviewing workspace";

  const toolCount = toolDetailMessages.length;

  const sourceReferences = useMemo(() => {
    const detailMessages =
      message.role === "tool" &&
      Array.isArray(message.toolMessages) &&
      message.toolMessages.length > 0
        ? message.toolMessages
        : [message];

    return mergeReferenceLists(
      ...detailMessages.map((detailMessage) =>
        getSourceReferences(detailMessage)
      )
    );
  }, [message]);

  const usageLabel = getUsageLabel(message.usage || message.metadata?.llm?.usage);

  const metaItems = Array.isArray(message.meta)
    ? message.meta
    : Array.isArray(message.metadata?.meta)
    ? message.metadata.meta
    : message.metadata?.intent
    ? [message.metadata.intent]
    : [];

  const attachmentMeta = message.metadata?.attachments;
  const knowledgeMeta = message.metadata?.retrievedKnowledge;
  const toolsMeta = message.metadata?.tools;
  const llmStatus = message.metadata?.llm?.status;
  const pendingAction =
  message.metadata
    ?.pendingAction ||
  null;

const writeActionResult =
  message.metadata
    ?.writeAction
    ?.result ||
  null;

const clarificationInteraction =
message.metadata
?.clarificationInteraction ||
null;

const readPresentation =
message.metadata
?.readPresentation ||
null;

const readPresentations =
Array.isArray(
message.metadata
?.readPresentations
)
? message.metadata
.readPresentations
.filter(Boolean)
: readPresentation
? [
readPresentation,
]
: [];

const hasMultipleReadPresentations =
readPresentations.length >
1;
const hasReadPresentation =
readPresentations.length >
0;
const attachmentAnalysisRequested =
message.metadata
?.routing
?.attachmentAnalysisRequested ===
true;
const responseSynthesis =
message.metadata
?.responseSynthesis &&
typeof message.metadata
.responseSynthesis ===
"object" &&
!Array.isArray(
message.metadata
.responseSynthesis
)
? message.metadata
.responseSynthesis
: null;

const responseSummary =
normalizeCopilotDisplayText(
responseSynthesis
?.summary ||
""
);

const responseNextStep =
normalizeCopilotDisplayText(
responseSynthesis
?.nextStepQuestion ||
""
);

const structuredPresentation =
message.metadata
?.structuredAnswer
?.presentation ||
null;

  return (
    <div
      className={`flex w-full gap-2.5 ${styles.wrapper}`}
      data-message-id={getMessageId(message, "")}
    >
      {message.role !== "user" && (
        <div
          className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${styles.avatar}`}
        >
          <i className={`${styles.icon} text-[11px]`}></i>
        </div>
      )}

      <div
className={`min-w-0 overflow-hidden ${
isToolMessage
? "max-w-[88%] px-2.5 py-2"
: hasMultipleReadPresentations ||
[
"brand_kit",
"connected_accounts",
"published_posts",
"scheduled_posts",
"asset_count",
"asset_search",
"profile_summary",
"post_reports",
"apps_history",
"background_jobs",
"credit_balance",
].includes(
readPresentation?.type
)
? "w-full max-w-full p-0"
: "max-w-[82%] px-3.5 py-2.5"
} ${styles.bubble}`}
>
        {isToolMessage && (
          <button
            type="button"
            onClick={() => setToolDetailsOpen((prev) => !prev)}
            aria-expanded={toolDetailsOpen}
            className="flex w-full min-w-[190px] items-center gap-2 rounded-lg px-1.5 py-1 text-left text-xs font-semibold text-blue-100/70 transition hover:text-blue-50"
          >
            <i className="fa-solid fa-magnifying-glass text-[10px] text-blue-200/55"></i>

            <span className="flex-1">
              {toolActivityLabel}
              <span className="ml-1.5 text-blue-100/35">
                · {toolCount} tool{toolCount === 1 ? "" : "s"}
              </span>
            </span>

            <i
              className={`fa-solid fa-chevron-down text-[9px] text-blue-100/40 transition-transform duration-200 ${
                toolDetailsOpen ? "rotate-180" : ""
              }`}
            ></i>
          </button>
        )}

        {isToolMessage &&
        promptGoal && (
          <details className="group mt-2 overflow-hidden rounded-xl border border-violet-300/[0.1] bg-violet-400/[0.035]">
            <summary className="flex cursor-pointer list-none items-center gap-2.5 px-3 py-2.5">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-violet-400/10 text-violet-100/70">
                <i className="fa-solid fa-bullseye text-[9px]"></i>
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-[9px] font-medium uppercase tracking-[0.08em] text-violet-100/40">
                  Goal
                </span>

                <span className="block truncate text-xs font-medium text-white/70">
                  {promptGoal.objective ||
                    "Complete this request"}
                </span>
              </span>

              <i className="fa-solid fa-chevron-down text-[8px] text-white/30 transition-transform group-open:rotate-180"></i>
            </summary>

            <div className="border-t border-white/[0.06] p-2">
              <CopilotGoalCard
                goal={
                  promptGoal
                }
                loading={
                  goalLoading
                }
                onCancel={
                  onCancelGoal
                }
              />
            </div>
          </details>
        )}

        <div className={isToolMessage && !toolDetailsOpen ? "hidden" : ""}>
          {isToolMessage ? (
            <div className="mt-2 space-y-2 border-t border-white/[0.05] pt-3">
              {toolDetailMessages.map((toolMessage, index) => (
                <div
                  key={getMessageId(toolMessage, index)}
                  className="rounded-lg bg-black/[0.12] px-3 py-2.5"
                >
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-100/35">
                      Tool {index + 1}
                    </span>

                    {toolMessage.metadata?.toolName && (
                      <span className="max-w-[220px] truncate text-[10px] font-semibold text-blue-100/40">
                        {toolMessage.metadata.toolName}
                      </span>
                    )}
                  </div>

                  <p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-blue-50/80">
                    {toolMessage.content || "No tool output returned."}
                  </p>
                </div>
              ))}
            </div>
          ) : writeActionResult
    ?.operation ===
  "account_connect" ? (
  <AccountConnectResultCard
    result={
      writeActionResult
    }
  />
) : hasReadPresentation &&
!attachmentAnalysisRequested ? null : (
structuredPresentation ? (
<StructuredAssistantContent
presentation={
structuredPresentation
}
/>
) : (
<PlainAssistantContent
content={
message.content
}
/>
)
)}



{!isToolMessage &&
hasMultipleReadPresentations && (
<WorkspaceHealthReport
presentations={
readPresentations
}
message={
message
}
onReadResultAction={
onReadResultAction
}
onConnectAccount={
onConnectAccount
}
profileSelectionEnabled={
profileSelectionEnabled
}
actionDraftStatus={
actionDraftStatus
}
clarificationInteraction={
clarificationInteraction
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
/>
)}

        {!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"brand_kit" && (
<BrandKitReadCard
presentation={
readPresentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"connected_accounts" && (
<ConnectedAccountsReadCard
presentation={
readPresentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
onConnect={
onConnectAccount
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"published_posts" && (
<PublishedPostsReadCard
presentation={
readPresentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"apps_history" && (
<AppsHistoryReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"credit_balance" && (
<CreditBalanceReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"background_jobs" && (
<BackgroundJobsReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"post_reports" && (
<PostReportsReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"profile_summary" && (
<ProfileSummaryReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"asset_search" && (
<AssetSearchReadCard
presentation={
readPresentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"asset_count" && (
<AssetCountReadCard
presentation={
readPresentation
}
/>
)}

{!isToolMessage &&
!hasMultipleReadPresentations &&
readPresentation?.type ===
"scheduled_posts" && (
<ScheduledPostsReadCard
presentation={
readPresentation
}
message={
message
}
loading={
actionDraftStatus ===
"loading"
}
inlineInteraction={
clarificationInteraction
}
inlineEditingEnabled={
profileSelectionEnabled
}
loadRemoteOptions={
loadRemoteOptions
}
uploadActionDraftMedia={
uploadActionDraftMedia
}
onSubmitActionDraft={
onSubmitActionDraft
}
onCancelActionDraft={
onCancelActionDraft
}
onAction={
onReadResultAction
}
/>
)}



{!isToolMessage &&
hasReadPresentation &&
(
responseSummary ||
responseNextStep
) && (
<div className="border-t border-white/[0.07] px-4 py-3.5">
<div className="flex items-start gap-2.5">
<span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.055] text-white/60">
<i className="fa-solid fa-sparkles text-[10px]"></i>
</span>

<div className="min-w-0">
<span className="mb-1 block text-[9px] font-medium uppercase tracking-[0.08em] text-white/35">
Summary
</span>

<p className="mb-0 whitespace-pre-wrap text-sm leading-relaxed text-white/72">
{responseSummary}
{responseSummary &&
responseNextStep
? " "
: ""}
{responseNextStep}
</p>
</div>
</div>
</div>
)}

{!isToolMessage &&
!readPresentation &&
clarificationInteraction?.type ===
"action_form" && (
            <ActionDraftForm
              interaction={
                clarificationInteraction
              }
              enabled={
                profileSelectionEnabled
              }
              loading={
                actionDraftStatus ===
                "loading"
              }
              loadRemoteOptions={
                loadRemoteOptions
              }
              uploadMedia={
                uploadActionDraftMedia
              }
              onSubmit={
                onSubmitActionDraft
              }
              onCancel={
                onCancelActionDraft
              }
            />
          )}

          {!isToolMessage &&
clarificationInteraction?.type ===
  "account_connect" && (
  <AccountConnectCard
    interaction={
      clarificationInteraction
    }
    enabled={
      profileSelectionEnabled
    }
    onConnect={
      onConnectAccount
    }
  />
)}


        {!isToolMessage &&
          clarificationInteraction?.type ===
            "connected_profile_selection" && (
            <ConnectedProfileSelectionCard
              interaction={
                clarificationInteraction
              }
              message={message}
              enabled={
                profileSelectionEnabled
              }
              loading={
                chatStatus ===
                "loading"
              }
              onSubmit={
                onSubmitProfileSelection
              }
            />
          )}

        {!isToolMessage &&
          clarificationInteraction?.type ===
            "publishing_requirements" && (
            <PublishingRequirementsCard
              interaction={
                clarificationInteraction
              }
              message={message}
              enabled={
                profileSelectionEnabled
              }
              loading={
                chatStatus ===
                "loading"
              }
              loadPinterestBoards={
                loadPinterestBoards
              }
              uploadPublishingMedia={
                uploadPublishingMedia
              }
              onSubmit={
                onSubmitPublishingRequirements
              }
            />
          )}

        {!isToolMessage && pendingAction && (
          <PendingActionCard
pendingAction={pendingAction}
onConfirmAction={onConfirmAction}
onCancelAction={onCancelAction}
onEditAction={onEditAction}
activeActionId={activeActionId}
actionStatus={actionStatus}
/>
        )}

        {Array.isArray(message.attachments) && message.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.attachments.map((attachment, index) => (
              <span
                key={getAttachmentId(attachment, index)}
                className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${styles.attachment}`}
              >
                <i className="fa-solid fa-paperclip"></i>
                <span className="truncate">{attachment.name || "Attachment"}</span>
              </span>
            ))}
          </div>
        )}

        {sourceReferences.length > 0 && (
          <div className="mt-3">
            <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/25">
              <i className="fa-solid fa-layer-group text-[9px]"></i>
              <span>
                {sourceReferences.length} reference
                {sourceReferences.length === 1 ? "" : "s"} reviewed
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {sourceReferences.slice(0, 8).map((source, index) => (
                <SourceChip
                  key={normalizeReferenceKey(source, index)}
                  source={source}
                  className={styles.source}
                />
              ))}

              {sourceReferences.length > 8 && (
                <span className="flex h-8 items-center rounded-lg bg-white/[0.025] px-2.5 text-[10px] font-medium text-white/30">
                  +{sourceReferences.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}

        {(metaItems.length > 0 ||
          usageLabel ||
          attachmentMeta?.enabled ||
          knowledgeMeta?.enabled ||
          toolsMeta?.enabled ||
          llmStatus) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/[0.04] pt-2 text-[10px] font-medium text-white/25">
            {metaItems.map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <i className="fa-regular fa-folder-open text-[9px]"></i>
                {formatMetaLabel(item)}
              </span>
            ))}

            {attachmentMeta?.enabled && (
              <span className="inline-flex items-center gap-1.5">
                <i className="fa-solid fa-paperclip text-[9px]"></i>
                {attachmentMeta.count || 0} attachment
                {Number(attachmentMeta.count || 0) === 1 ? "" : "s"}
              </span>
            )}

            {knowledgeMeta?.enabled && (
              <span className="inline-flex items-center gap-1.5">
                <i className="fa-solid fa-database text-[9px]"></i>
                {knowledgeMeta.count || 0} source
                {Number(knowledgeMeta.count || 0) === 1 ? "" : "s"}
              </span>
            )}

            {!isToolMessage &&
              toolsMeta?.enabled &&
              Number(toolsMeta.count || 0) > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <i className="fa-solid fa-screwdriver-wrench text-[9px]"></i>
                  {toolsMeta.count || 0} tool
                  {Number(toolsMeta.count || 0) === 1 ? "" : "s"}
                </span>
              )}

            {llmStatus && (
              <span className="inline-flex items-center gap-1.5">
                <i
                  className={`fa-solid text-[9px] ${
                    String(llmStatus).toLowerCase() === "success"
                      ? "fa-circle-check text-emerald-300/45"
                      : "fa-circle-info"
                  }`}
                ></i>
                {formatMetaLabel(llmStatus)}
              </span>
            )}

            {usageLabel && (
              <span className="inline-flex items-center gap-1.5">
                <i className="fa-solid fa-wave-square text-[9px]"></i>
                {usageLabel}
              </span>
            )}
          </div>
        )}

          {timestamp && (
            <div className="mt-1 text-[10px] font-semibold opacity-35">
              {timestamp}
            </div>
          )}
        </div>
      </div>

      {message.role === "user" && (
        <div
          className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${styles.avatar}`}
        >
          <i className={`${styles.icon} text-[11px]`}></i>
        </div>
      )}
    </div>
  );
}